import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../store/AuthContext';
import { useLanguage } from '../store/LanguageContext';
import type { User } from '../store/AuthContext';
import {
  AdminProduct, AdminSupplier, AdminCustomer, AdminCustomerDetail,
  AdminQuote, AdminPricingRow, AdminStats,
  adminGetStats, adminGetProducts, adminUpdateProduct, adminCreateProduct,
  adminGetSuppliers, adminCreateSupplier, adminUpdateSupplier, adminDeleteSupplier,
  adminGetCustomers, adminGetCustomer,
  adminGetQuotes, adminGetQuoteDetail, adminUpdateQuoteStatus, adminDeleteQuote,
  adminGetPricing, adminCreatePricingRule, adminUpdatePricingRule,
  fetchProducts, ApiProduct, authUpdateProfile,
} from '../lib/api';
import { PRODUCTS } from '../store/productData';
import SEOHead from '../components/SEOHead';
import { ImageUpload } from '../components/ImageUpload';
import {
  Package, Users, Truck, FileText, BarChart2,
  RefreshCw, AlertCircle, CheckCircle, Clock, XCircle,
  Edit3, Save, X, Plus, ChevronRight, ArrowLeft, Euro, User as UserIcon,
  ChevronDown, ChevronUp, Star, Download, Info, ArrowUpDown, Trash2,
  Tag, Calendar, Flame, Loader2, History, Mail,
  TrendingUp,
} from 'lucide-react';

// ─── Shared sub-components ────────────────────────────────────────────────────

function StatusBadge({ status, onClick }: { status: string; onClick?: () => void }) {
  const { t } = useLanguage();
  const map: Record<string, { label: string; cls: string; Icon: React.ElementType }> = {
    draft:     { label: t('admin.status.draft'),    cls: 'bg-gray-100 text-gray-600',          Icon: Clock },
    pending:   { label: t('admin.status.pending'),  cls: 'bg-yellow-50 text-yellow-700',       Icon: Clock },
    sent:      { label: t('admin.status.sent'),     cls: 'bg-blue-50 text-blue-700',           Icon: CheckCircle },
    accepted:  { label: t('admin.status.accepted'), cls: 'bg-green-50 text-green-700',         Icon: CheckCircle },
    approved:  { label: t('admin.status.approved'), cls: 'bg-green-50 text-green-700',         Icon: CheckCircle },
    rejected:  { label: t('admin.status.rejected'), cls: 'bg-red-50 text-red-600',             Icon: XCircle },
    expired:   { label: t('admin.status.expired'),  cls: 'bg-gray-100 text-gray-500',          Icon: XCircle },
    new:       { label: t('admin.status.new'),      cls: 'bg-[#c47a3a]/10 text-[#c47a3a]',     Icon: AlertCircle },
  };
  const entry = map[status.toLowerCase()] || { label: status, cls: 'bg-gray-100 text-gray-600', Icon: AlertCircle };
  const { Icon } = entry;
  const base = `inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${entry.cls}`;
  // v3.2.117 — Klikbaar als onClick prop is geleverd. Filip's wens: 'als
  // je op concept drukt zou je de pdf moeten kunnen open doen in popup'.
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${base} hover:ring-2 hover:ring-[#c47a3a]/40 cursor-pointer transition-shadow`}
        title="Klik om PDF-voorbeeld te openen in een nieuw tabblad"
      >
        <Icon size={11} />
        {entry.label}
      </button>
    );
  }
  return (
    <span className={base}>
      <Icon size={11} />
      {entry.label}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, sub, onClick, isActive }: { icon: React.ElementType; label: string; value: string | number; sub?: string; onClick?: () => void; isActive?: boolean }) {
  return (
    <div
      onClick={onClick}
      className={`bg-white rounded-xl border shadow-sm p-4 sm:p-5 flex items-start gap-3 sm:gap-4 transition-all duration-200 ${
        onClick ? 'cursor-pointer hover:shadow-md hover:scale-[1.02]' : ''
      } ${isActive ? 'border-[#c47a3a] ring-1 ring-[#c47a3a]/30 border-b-[3px] border-b-[#c47a3a]' : 'border-gray-100'}`}
    >
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isActive ? 'bg-[#c47a3a] text-white' : 'bg-[#c47a3a]/10'}`}>
        <Icon size={20} className={isActive ? 'text-white' : 'text-[#c47a3a]'} />
      </div>
      <div className="min-w-0">
        <p className="text-xl sm:text-2xl font-bold text-[#1a1a1a] truncate">{value}</p>
        <p className="text-sm font-medium text-[#4a4a4a]">{label}</p>
        {sub && <p className="text-xs text-[#8a7e74] mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function formatDate(ts: number) {
  return new Date(ts * 1000).toLocaleDateString('nl-BE', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatEuro(val: number | null | undefined) {
  if (val == null) return '—';
  return `€${val.toFixed(2)}`;
}

// ─── Tab definitions ──────────────────────────────────────────────────────────

// Owner workflow order: suppliers → catalog → resellers → customers&quotes → pricing → audit.
// Customers and quotes are merged into one tab (KlantenOffertesTab already
// shows quotes per customer; the standalone Test Offertes tab is rendered
// inline below the customer list so everything quote-related lives together).
type TabId = 'leveranciers' | 'producten' | 'resellers' | 'klanten-offertes' | 'backlog';

// v5-verbouwing blok 2: 'prijzen' (Kortingen Resellers) is als aparte tab
// verdwenen — de kortingen leven nu ín het Resellers-vak (per reseller:
// type + kortingspercentage + prijsafspraken-paneel). Vier hoofdtabs.
const TABS: { id: TabId; label: string; icon: React.ElementType }[] = [
  { id: 'leveranciers',     label: 'admin.tab.leveranciers',    icon: Truck },
  { id: 'producten',        label: 'admin.tab.producten',       icon: Package },
  { id: 'resellers',        label: 'admin.tab.resellers',       icon: Users },
  { id: 'klanten-offertes', label: 'admin.tab.klantenOffertes', icon: FileText },
  { id: 'backlog',          label: 'admin.tab.backlog',         icon: BarChart2 },
];

// Owner accounts
const OWNERS = [
  { email: 'filip@gevelsteenbedrijf.be', name: 'Filip' },
  { email: 'bicek@gevelsteenbedrijf.be', name: 'Bicek' },
];

// ═══════════════════════════════════════════════════════════════════════════════
// MIJN PROFIEL SECTION
// ═══════════════════════════════════════════════════════════════════════════════

function MijnProfielSection({ user, onUpdate }: { user: User; onUpdate: (u: User) => void }) {
  const { t } = useLanguage();
  // Collapsed by default — owner profile takes a lot of space, and once
  // it's filled in correctly you rarely need to look at it.
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [form, setForm] = useState({
    name: user.name || '',
    phone: user.phone || '',
    company_name: user.company_name || '',
    btw_number: user.btw_number || '',
    address: user.address || '',
    city: user.city || '',
    postal_code: user.postal_code || '',
  });

  const startEdit = () => {
    setForm({
      name: user.name || '',
      phone: user.phone || '',
      company_name: user.company_name || '',
      btw_number: user.btw_number || '',
      address: user.address || '',
      city: user.city || '',
      postal_code: user.postal_code || '',
    });
    setSaveError(null);
    setSaveSuccess(false);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setSaveError(null);
  };

  const saveProfile = async () => {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const payload: Record<string, string | undefined> = {};
      for (const [k, v] of Object.entries(form)) {
        payload[k] = v || undefined;
      }
      const updated = await authUpdateProfile(payload);
      onUpdate(updated as unknown as User);
      setEditing(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (e) {
      setSaveError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const ProfileField = ({ label, field, type = 'text' }: { label: string; field: keyof typeof form; type?: string }) => (
    <div>
      <label className="block text-xs font-semibold text-[#8a7e74] uppercase tracking-wide mb-1">{label}</label>
      <input
        type={type}
        className="w-full px-3 py-3 border border-gray-200 rounded-lg text-sm sm:text-base focus:border-[#c47a3a] focus:outline-none min-h-[44px]"
        value={form[field]}
        onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
      />
    </div>
  );

  const InfoRow = ({ label, value }: { label: string; value: string | null | undefined }) => (
    <div className="flex flex-col sm:flex-row sm:items-center py-2 border-b border-gray-50 last:border-0">
      <span className="text-xs font-semibold text-[#8a7e74] uppercase tracking-wide sm:w-32 flex-shrink-0 mb-0.5 sm:mb-0">{label}</span>
      <span className="text-sm text-[#1a1a1a]">{value || '—'}</span>
    </div>
  );

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(e => !e)}
        className="w-full px-4 sm:px-6 py-4 border-b border-gray-100 flex items-center justify-between gap-3 hover:bg-[#faf7f3] transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          <CheckCircle size={20} className="text-green-600 flex-shrink-0" />
          <h2 className="text-lg font-bold text-[#1a1a1a] flex items-center gap-2">
            <UserIcon size={18} className="text-[#c47a3a]" /> {t('admin.profile')}
            <span className="ml-2 text-xs font-normal text-[#8a7e74]">— {user.name}</span>
          </h2>
        </div>
        <div className="flex items-center gap-2">
          {expanded && !editing && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); startEdit(); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); startEdit(); } }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#c47a3a] hover:bg-[#b06930] text-white rounded-lg text-xs font-medium transition-colors cursor-pointer"
            >
              <Edit3 size={12} />
              {t('admin.profile.edit')}
            </span>
          )}
          <ChevronDown
            size={18}
            className={`text-[#8a7e74] transition-transform ${expanded ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {expanded && (
      <div className="px-4 sm:px-6 pb-5 pt-3">
        {saveError && (
          <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 rounded-lg text-red-600 text-sm">
            <AlertCircle size={14} /> {saveError}
          </div>
        )}
        {saveSuccess && (
          <div className="flex items-center gap-2 p-3 mb-4 bg-green-50 rounded-lg text-green-700 text-sm">
            <CheckCircle size={14} /> {t('admin.profile.success')}
          </div>
        )}

        {editing ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <ProfileField label={t('admin.profile.name')} field="name" />
              <div>
                <label className="block text-xs font-semibold text-[#8a7e74] uppercase tracking-wide mb-1">E-mail</label>
                <input
                  type="email"
                  className="w-full px-3 py-3 border border-gray-200 rounded-lg text-sm sm:text-base bg-gray-50 text-[#8a7e74] min-h-[44px] cursor-not-allowed"
                  value={user.email}
                  disabled
                />
              </div>
              <ProfileField label={t('admin.profile.phone')} field="phone" type="tel" />
              <ProfileField label={t('admin.profile.company')} field="company_name" />
              <ProfileField label={t('admin.profile.btw')} field="btw_number" />
              <ProfileField label={t('admin.profile.address')} field="address" />
              <ProfileField label={t('admin.profile.city')} field="city" />
              <ProfileField label={t('admin.profile.postalCode')} field="postal_code" />
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-6">
              <button
                onClick={saveProfile}
                disabled={saving}
                className="flex items-center justify-center gap-1.5 px-5 py-3 bg-[#c47a3a] hover:bg-[#b06930] text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 min-h-[44px]"
              >
                <Save size={14} />
                {saving ? t('admin.profile.saving') : t('admin.profile.save')}
              </button>
              <button
                onClick={cancelEdit}
                className="px-5 py-3 bg-gray-100 hover:bg-gray-200 text-[#4a4a4a] rounded-lg text-sm font-medium transition-colors min-h-[44px]"
              >
                {t('admin.profile.cancel')}
              </button>
            </div>
          </>
        ) : (
          <div>
            <InfoRow label={t('admin.profile.name')} value={user.name} />
            <InfoRow label={t('admin.profile.email')} value={user.email} />
            <InfoRow label={t('admin.profile.phone')} value={user.phone} />
            <InfoRow label={t('admin.profile.companyShort')} value={user.company_name} />
            <InfoRow label={t('admin.profile.btwShort')} value={user.btw_number} />
            <InfoRow label={t('admin.profile.address')} value={user.address} />
            <InfoRow label={t('admin.profile.city')} value={user.city} />
            <InfoRow label={t('admin.profile.postalCode')} value={user.postal_code} />
          </div>
        )}
      </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

export default function AdminDashboard() {
  const [searchParams] = useSearchParams();
  const isDemoMode = searchParams.get('demo') === 'true';
  const { user, setUser, authLoading } = useAuth();
  const { t } = useLanguage();

  const isOwner = user?.role === 'owner' || user?.role === 'superadmin';
  const showFullDashboard = isOwner;

  // v3.2.127 — Bij mount altijd naar boven scrollen, niet de browser-restored
  // positie van de vorige sessie. Filip kwam steeds in het midden van de
  // Leveranciers-tabel uit ipv bovenaan bij Mijn Profiel + stat-cards.
  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Active tab
  // v5.27: deep-link naar een tab via ?tab=... (bv. /admin?tab=producten) —
  // gebruikt door de reviewlijst zodat Filip direct op de juiste plek landt.
  const [activeTab, setActiveTab] = useState<TabId>(() => {
    const t = new URLSearchParams(window.location.search).get('tab');
    return (['leveranciers', 'producten', 'resellers', 'klanten-offertes', 'backlog'] as TabId[])
      .includes(t as TabId) ? (t as TabId) : 'leveranciers';
  });

  // Stats
  const [stats, setStats] = useState<AdminStats | null>(null);

  // Data
  const [products, setProducts] = useState<AdminProduct[]>([]);
  const [demoProducts, setDemoProducts] = useState<ApiProduct[]>([]);
  const [suppliers, setSuppliers] = useState<AdminSupplier[]>([]);
  const [customers, setCustomers] = useState<AdminCustomer[]>([]);
  const [quotes, setQuotes] = useState<AdminQuote[]>([]);
  const [pricing, setPricing] = useState<AdminPricingRow[]>([]);
  const [resellersCount, setResellersCount] = useState<number>(0);
  // v3.2.72 — bewaar de hele resellers-lijst zodat ResellersTab niet
  // opnieuw hoeft te fetchen. Snellere tab-switch.
  const [resellersData, setResellersData] = useState<import('../lib/api').AdminReseller[]>([]);

  // Loading / Error
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter out superadmin users from customers
  const filteredCustomers = customers.filter(c => (c as AdminCustomer & { role?: string }).role !== 'superadmin');
  const filteredCustomerCount = stats ? filteredCustomers.length || stats.customers : 0;

  // ── Data fetching ──────────────────────────────────────────────────────────

  const loadDemoData = useCallback(async () => {
    setLoading(true);
    try {
      const prods = await fetchProducts();
      setDemoProducts(prods);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadFullData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Lazy-load resellers list to avoid hard dependency on the API module
      // for owners who never used the resellers tab before.
      const { adminGetResellers } = await import('../lib/api');

      const [statsRes, prodsRes, suppsRes, custsRes, quotesRes, pricingRes, resellersRes] = await Promise.allSettled([
        adminGetStats(),
        adminGetProducts(),
        adminGetSuppliers(),
        adminGetCustomers(),
        adminGetQuotes(),
        adminGetPricing(),
        adminGetResellers(),
      ]);

      if (statsRes.status === 'fulfilled') setStats(statsRes.value);
      if (prodsRes.status === 'fulfilled') setProducts(prodsRes.value);
      if (suppsRes.status === 'fulfilled') setSuppliers(suppsRes.value);
      if (custsRes.status === 'fulfilled') setCustomers(custsRes.value);
      if (quotesRes.status === 'fulfilled') setQuotes(quotesRes.value);
      if (pricingRes.status === 'fulfilled') setPricing(pricingRes.value);
      if (resellersRes.status === 'fulfilled') {
        // Count only active resellers (Filip cares about who's actively selling).
        setResellersCount(resellersRes.value.filter(r => r.is_active === 1).length);
        // v3.2.72 — bewaar de volledige lijst zodat de Resellers-tab geen
        // tweede fetch hoeft te doen. Filip's klacht: 'dat duurt altijd om
        // dit te laden ... zo zwaar is die info toch ook niet?'.
        setResellersData(resellersRes.value);
      }

      const errors = [statsRes, prodsRes, suppsRes, custsRes, quotesRes, pricingRes]
        .filter(r => r.status === 'rejected')
        .map(r => (r as PromiseRejectedResult).reason?.message);
      if (errors.length) setError(errors.join(' | '));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (showFullDashboard) {
      loadFullData();
    } else if (isDemoMode) {
      loadDemoData();
    }
  }, [showFullDashboard, isDemoMode, loadFullData, loadDemoData]);

  // ── Demo mode (not logged in, ?demo=true) ─────────────────────────────────

  if (!showFullDashboard) {
    if (!isDemoMode) {
      // Sessie wordt nog hersteld (bv. net teruggeschakeld uit test-modus):
      // even niets tonen i.p.v. naar login te sturen.
      if (authLoading) return null;
      // Niet ingelogd → rechtstreeks naar login en daarna terug naar deze
      // admin-pagina (incl. ?tab=…), zodat links uit de doorloop-lijst werken.
      if (!user) {
        window.location.replace(`/login?redirect=${encodeURIComponent('/admin' + window.location.search)}`);
        return null;
      }
      // Ingelogd maar in test-modus (klant/reseller): wijs op de terugweg.
      return (
        <div className="pt-16 lg:pt-[72px] min-h-screen bg-[#faf7f3] flex items-center justify-center">
          <div className="text-center max-w-md mx-auto px-6">
            <div className="w-16 h-16 rounded-2xl bg-[#c47a3a]/10 flex items-center justify-center mx-auto mb-4">
              <AlertCircle size={28} className="text-[#c47a3a]" />
            </div>
            <h1 className="text-2xl font-bold text-[#1a1a1a] mb-2">{t('admin.accessDenied')}</h1>
            <p className="text-[#8a7e74]">
              Je bekijkt de site nu als test-account. Klik bovenaan op "Terug naar admin" (of log in als eigenaar) om het dashboard te openen.
            </p>
          </div>
        </div>
      );
    }

    // Demo: only product cards
    return (
      <div className="pt-16 lg:pt-[72px] min-h-screen bg-[#faf7f3]">
        <SEOHead page="dashboard" />
        <div className="bg-[#1a1a1a] text-white py-6 sm:py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-xl sm:text-2xl font-bold">{t('admin.dashboard')}</h1>
              <span className="px-2 py-0.5 bg-[#c47a3a] text-white text-xs font-bold rounded-full tracking-wide uppercase">Demo</span>
            </div>
            <p className="text-gray-400 text-sm">{t('admin.demoMode')}</p>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-bold text-[#1a1a1a] flex items-center gap-2">
                <Package size={18} className="text-[#c47a3a]" /> {t('admin.products.title')}
                <span className="px-2 py-0.5 bg-[#c47a3a]/10 text-[#c47a3a] text-xs font-semibold rounded-full">{demoProducts.length}</span>
              </h2>
            </div>
            <div className="px-4 sm:px-6 pb-6 pt-2">
              {loading ? (
                <p className="text-sm text-[#8a7e74] py-4">{t('admin.loading')}</p>
              ) : (
                <>
                  {/* Mobile cards */}
                  <div className="sm:hidden space-y-3">
                    {demoProducts.map(p => (
                      <div key={p.id} className="border border-gray-100 rounded-lg p-4">
                        <p className="font-medium text-[#1a1a1a] text-base mb-2">{p.name}</p>
                        <div className="grid grid-cols-2 gap-y-2 text-sm">
                          <span className="text-[#8a7e74]">{t('admin.products.category')}</span>
                          <span className="px-2 py-0.5 bg-[#c47a3a]/10 text-[#c47a3a] rounded text-xs font-medium capitalize w-fit">{p.category}</span>
                          <span className="text-[#8a7e74]">{t('admin.products.color')}</span>
                          <div className="flex items-center gap-1.5">
                            {p.color_hex && <span className="w-3.5 h-3.5 rounded-full border border-gray-200 inline-block" style={{ backgroundColor: p.color_hex }} />}
                            <span className="text-[#4a4a4a]">{p.color}</span>
                          </div>
                          <span className="text-[#8a7e74]">{t('admin.products.texture')}</span>
                          <span className="text-[#4a4a4a]">{p.texture}</span>
                          <span className="text-[#8a7e74]">{t('admin.products.dimensions')}</span>
                          <span className="text-[#4a4a4a]">{p.dimensions}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Desktop table — kleine product-thumbnail in eerste kolom
                       zodat je in één oogopslag de steen herkent. */}
                  <div className="hidden sm:block overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-100">
                          <th className="text-left py-2 pr-2 text-xs font-semibold text-[#8a7e74] uppercase tracking-wide w-14"></th>
                          {[t('admin.products.name'), t('admin.products.category'), t('admin.products.color'), t('admin.products.texture'), t('admin.products.dimensions'), t('admin.products.weight')].map(h => (
                            <th key={h} className="text-left py-2 pr-4 text-xs font-semibold text-[#8a7e74] uppercase tracking-wide whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {demoProducts.map(p => (
                          <tr key={p.id} className="border-b border-gray-50 hover:bg-[#faf7f3] transition-colors">
                            <td className="py-2 pr-2">
                              {p.primary_image ? (
                                <img
                                  src={p.primary_image}
                                  alt={p.name}
                                  className="w-12 h-12 rounded-lg object-cover border border-gray-200"
                                  loading="lazy"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                              ) : (
                                <div
                                  className="w-12 h-12 rounded-lg border border-gray-200"
                                  style={{ backgroundColor: p.color_hex }}
                                />
                              )}
                            </td>
                            <td className="py-2.5 pr-4 font-medium text-[#1a1a1a] whitespace-nowrap">{p.name}</td>
                            <td className="py-2.5 pr-4">
                              <span className="px-2 py-0.5 bg-[#c47a3a]/10 text-[#c47a3a] rounded text-xs font-medium capitalize">{p.category}</span>
                            </td>
                            <td className="py-2.5 pr-4">
                              <div className="flex items-center gap-1.5">
                                {p.color_hex && <span className="w-3.5 h-3.5 rounded-full border border-gray-200 inline-block" style={{ backgroundColor: p.color_hex }} />}
                                <span className="text-[#4a4a4a]">{p.color}</span>
                              </div>
                            </td>
                            <td className="py-2.5 pr-4 text-[#4a4a4a]">{p.texture}</td>
                            <td className="py-2.5 pr-4 text-[#4a4a4a]">{p.dimensions}</td>
                            <td className="py-2.5 text-[#4a4a4a]">{p.weight}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Full owner dashboard ──────────────────────────────────────────────────

  return (
    <div className="pt-16 lg:pt-[72px] min-h-screen bg-[#faf7f3]">
      <SEOHead page="dashboard" />

      {/* Header — v3.2.75: padding op de INNER max-w-7xl (zelfde als
           navbar) ipv op de outer full-width-zwarte div. Vroeger:
           outer.px-X + inner.max-w-7xl mx-auto → content begon (viewport
           − 7xl)/2 PIXELS van de rand. Navbar daarentegen zette padding
           op de inner → content begon (viewport − 7xl)/2 + X. Daardoor
           stond 'Filip' systematisch X pixels te ver links t.o.v. het
           logo. Nu hetzelfde patroon op beide → écht aligned. */}
      {/* Gsm-compact (Franky 19/8): kop op één rij, kleine verversen-knop */}
      <div className="bg-[#1a1a1a] text-white py-3 sm:py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-row items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-lg sm:text-2xl font-bold sm:mb-1 truncate">
              {(user?.name?.split(' ')[0]) || t('admin.ownerDashboard')}
            </h1>
            <p className="hidden sm:block text-gray-400 text-sm">{t('admin.loggedInAs')} {user?.name}</p>
          </div>
          <button
            onClick={loadFullData}
            className="flex items-center justify-center gap-1.5 px-3 sm:px-4 py-2 sm:py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-xs sm:text-sm font-medium transition-colors flex-shrink-0"
          >
            <RefreshCw size={14} />
            <span className="hidden sm:inline">{t('admin.refresh')}</span>
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-5 sm:space-y-6">

        {/* Mijn Profiel */}
        {user && (
          <MijnProfielSection user={user} onUpdate={(u) => setUser(u)} />
        )}

        {/* Stats cards — order requested by Filip:
              1. Leveranciers   (linksboven)
              2. Producten
              3. Resellers
              4. Klanten & Offertes  (samen in één vak)
              5. Prijzen
            All clickable to switch the corresponding tab below. */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
            <StatCard icon={Truck}    label={t('admin.stat.leveranciers')}     value={stats.suppliers}                                                                              onClick={() => setActiveTab('leveranciers')}     isActive={activeTab === 'leveranciers'} />
            <StatCard icon={Package}  label={t('admin.stat.producten')}        value={stats.products}                                                                               onClick={() => setActiveTab('producten')}        isActive={activeTab === 'producten'} />
            <StatCard icon={Users}    label={t('admin.stat.resellers')}        value={resellersCount}                          sub={t('admin.stat.resellersSub')}                  onClick={() => setActiveTab('resellers')}        isActive={activeTab === 'resellers'} />
            <StatCard icon={FileText} label={t('admin.tab.klantenOffertes')}   value={`${filteredCustomerCount} ${t('admin.stat.klanten')}`}  sub={`${stats.quotes.total} ${t('admin.stat.offertes')}`}  onClick={() => setActiveTab('klanten-offertes')} isActive={activeTab === 'klanten-offertes'} />
            {/* V6.17 — het vijfde vak ontbrak: de backlog (berichtenlogboek,
                gewiste offertes, exports, back-ups) was enkel via een directe
                link ?tab=backlog te bereiken. */}
            <StatCard icon={BarChart2} label={t('admin.tab.backlog')}          value={t('admin.backlog.what')}                 sub={t('admin.backlog.activityTitle')}              onClick={() => setActiveTab('backlog')}          isActive={activeTab === 'backlog'} />
          </div>
        )}

        {/* Mede-eigenaar section volledig verwijderd op verzoek van Filip
             (v3.2.31). Filip + Bicek zijn samen eigenaar — ze weten dat van
             elkaar, geen apart blok nodig dat plaats invult zonder waarde. */}

        {/* Error banner */}
        {error && (
          <div className="flex items-center gap-2 p-4 bg-red-50 rounded-xl text-red-600 text-sm">
            <AlertCircle size={15} /> {error}
          </div>
        )}

        {/* Tab content */}
        {loading ? (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-12 text-center">
            <RefreshCw size={24} className="text-[#c47a3a] animate-spin mx-auto mb-3" />
            <p className="text-sm text-[#8a7e74]">{t('admin.loading')}</p>
          </div>
        ) : (
          <>
            {activeTab === 'leveranciers' && <LeveranciersTab suppliers={suppliers} products={products} onRefresh={loadFullData} />}
            {activeTab === 'producten' && <ProductenTab products={products} resellers={resellersData} onRefresh={loadFullData} />}
            {activeTab === 'resellers' && <ResellersTab initialItems={resellersData} pricing={pricing} onRefresh={loadFullData} />}
            {activeTab === 'klanten-offertes' && (
              <KlantenOffertesTab customers={filteredCustomers} quotes={quotes} onRefresh={loadFullData} />
            )}
            {activeTab === 'backlog' && <BacklogTab />}
          </>
        )}

        {/* Footer */}
        <div className="flex items-center gap-2 p-4 bg-white rounded-xl border border-gray-100 shadow-sm">
          <BarChart2 size={15} className="text-[#c47a3a] flex-shrink-0" />
          <p className="text-xs text-[#8a7e74]">
            API: <span className="font-mono text-[#4a4a4a] break-all">gevelsteenbedrijf-api-v5.franky-f29.workers.dev</span>
            {' '}— {t('admin.apiFooter')}
          </p>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PRODUCTEN TAB — mobile: card layout + full-width edit form
// ═══════════════════════════════════════════════════════════════════════════════

function ProductenTab({ products, resellers, onRefresh }: { products: AdminProduct[]; resellers: import('../lib/api').AdminReseller[]; onRefresh: () => void }) {
  const { t } = useLanguage();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editData, setEditData] = useState<Record<string, string | number | null>>({});
  const [saving, setSaving] = useState(false);
  // ─── Discount manager modal ─────────────────────────────────────────────
  // Opened from the Tag/Korting icon button per product row.
  const [discountForId, setDiscountForId] = useState<string | null>(null);
  const productForDiscount = products.find(p => p.id === discountForId) || null;
  const [historyForId, setHistoryForId] = useState<string | null>(null);
  const productForHistory = products.find(p => p.id === historyForId) || null;
  const [saveError, setSaveError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // Collapsed by default — 27 products take a lot of vertical space.
  // Producten is de hoofd-tab — moet meteen open zijn als de gebruiker
  // erop klikt, anders ziet hij/zij alleen een lege koptekst en denkt dat
  // er niets is. Inklappen kan altijd via de chevron rechts.
  const [tabExpanded, setTabExpanded] = useState(true);
  // Defensieve fix: forceer open bij elke mount, ongeacht eerdere state.
  useEffect(() => { setTabExpanded(true); }, []);

  // ─── Nieuw product modal ──────────────────────────────────────────────
  const [creatingProduct, setCreatingProduct] = useState(false);

  // Supplier filter
  const [supplierFilter, setSupplierFilter] = useState<string | null>(null);

  // Price sorting
  type SortCol = 'supplier_cost' | 'locked_price' | 'verkoop_resell' | 'incl21' | 'incl6' | 'supplier' | 'category' | 'name' | 'margin';
  // v3.2.93 — Standaard reseller-korting is nu instelbaar bovenaan de tab.
  // Persisted in localStorage zodat de keuze tussen sessies behouden blijft.
  // Default = 20%; Filip kan dit aanpassen naar 35/40/45% om scenario's
  // door te rekenen voor hij definitief beslist hoe hij de reseller-deal
  // standaardiseert.
  // v3.2.98 — Slider is MARGE TOTAAL target (niet reseller-korting zoals
  // verkeerd geïnterpreteerd in v3.2.93). Filip's wens: 'standaard marge
  // aanpassing mogelijk bovenaan'. De marge totaal-kolom toont overal deze
  // waarde, EXCL.BTW (=catalog) wordt herrekend zodat: catalog = inkoop /
  // (1 − marge/100). Reseller's share blijft hardcoded 20% binnen die marge.
  // v5 — de marge staat in de settings-tabel (database), niet meer in
  // localStorage: dezelfde bron die calculator en quotes gebruiken, dus wat
  // hier getoond wordt is per definitie wat de site rekent.
  const [margeTarget, setMargeTarget] = useState<number>(20);
  // v5 blok 6: minimummarge + kortingsplafond, zelfde settings-bron.
  const [minMargin, setMinMargin] = useState<number>(0);
  const [maxDiscount, setMaxDiscount] = useState<number>(50);
  // V5.34: snijverlies — vast % voor alle klanten/resellers, alleen hier instelbaar.
  const [cuttingLossPct, setCuttingLossPct] = useState<number>(10);
  const margeLoadedRef = useRef(false);
  useEffect(() => {
    import('../lib/api').then(({ adminGetSettings }) => adminGetSettings())
      .then(s => {
        setMargeTarget(s.default_margin_pct);
        setMinMargin(s.min_margin_pct);
        setMaxDiscount(s.max_discount_pct);
        if (Number.isFinite(s.cutting_loss_pct)) setCuttingLossPct(s.cutting_loss_pct as number);
      })
      .catch(() => { /* API down → defaults blijven staan */ })
      .finally(() => { margeLoadedRef.current = true; });
  }, []);
  const [discountSaved, setDiscountSaved] = useState(false);
  useEffect(() => {
    if (!margeLoadedRef.current) return; // initiële load niet terugschrijven
    const save = setTimeout(() => {
      import('../lib/api').then(({ adminUpdateSettings }) =>
        adminUpdateSettings({
          default_margin_pct: margeTarget,
          min_margin_pct: minMargin,
          max_discount_pct: maxDiscount,
          cutting_loss_pct: cuttingLossPct,
        })
      ).catch(() => { /* volgende wijziging probeert opnieuw */ });
    }, 600);
    setDiscountSaved(true);
    const t = setTimeout(() => setDiscountSaved(false), 1500);
    return () => { clearTimeout(save); clearTimeout(t); };
  }, [margeTarget, minMargin, maxDiscount, cuttingLossPct]);
  // Bereken catalog (= EXCL.BTW) op basis van target marge totaal:
  //   catalog × (1 − marge/100) = inkoop  →  catalog = inkoop / (1 − marge/100)
  const targetCatalog = (cost: number | null | undefined): number | null => {
    if (cost == null || margeTarget >= 100) return null;
    return Math.round((cost / (1 - margeTarget / 100)) * 100) / 100;
  };
  // v3.2.105 — Per-product override-aware. Wanneer Filip in edit-mode een
  // specifieke verkoopprijs typt en opslaat (p.locked_price ≠ null), wint
  // die override over de globale slider voor dat ene product. Anders volgt
  // de row de slider via targetCatalog(supplier_cost).
  const RESELLER_SHARE_PCT = 20;
  const displayCatalog = (p: AdminProduct): number | null => {
    return p.locked_price ?? targetCatalog(p.supplier_cost);
  };
  const displayMargin = (p: AdminProduct): number | null => {
    if (p.locked_price != null && p.supplier_cost != null && p.locked_price > 0) {
      return Math.round(((p.locked_price - p.supplier_cost) / p.locked_price) * 1000) / 10;
    }
    return p.supplier_cost != null ? margeTarget : null;
  };
  // v5 — échte resellerkortingen uit de database i.p.v. hardcoded 20%.
  // Elke actieve reseller heeft zijn eigen default_discount_pct; de kolom
  // toont het bereik (laagste–hoogste resellerprijs) over alle resellers.
  const resellerDiscounts = resellers
    .filter(r => r.is_active !== 0)
    .map(r => r.default_discount_pct)
    .filter((n): n is number => typeof n === 'number');
  const resellerPriceRange = (p: AdminProduct): { min: number; max: number } | null => {
    const cat = displayCatalog(p);
    if (cat == null) return null;
    const discounts = resellerDiscounts.length > 0 ? resellerDiscounts : [RESELLER_SHARE_PCT];
    const prices = discounts.map(d => Math.round(cat * (1 - d / 100) * 100) / 100);
    return { min: Math.min(...prices), max: Math.max(...prices) };
  };
  const [sortCol, setSortCol] = useState<SortCol | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Bestseller filter
  const [showBestsellers, setShowBestsellers] = useState(false);

  // Local bestseller state (frontend toggle, persisted via API)
  const [bestsellers, setBestsellers] = useState<Set<string>>(() => {
    const set = new Set<string>();
    products.forEach(p => { if ((p as AdminProduct & { is_bestseller?: number }).is_bestseller) set.add(p.id); });
    return set;
  });

  const toggleBestseller = async (productId: string) => {
    const isCurrently = bestsellers.has(productId);
    const next = new Set(bestsellers);
    if (isCurrently) next.delete(productId); else next.add(productId);
    setBestsellers(next);
    try {
      await adminUpdateProduct(productId, { is_bestseller: isCurrently ? 0 : 1 });
    } catch {
      // revert on failure
      setBestsellers(bestsellers);
    }
  };

  const toggleSort = (col: SortCol) => {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  // Returnt number voor numerieke kolommen, string voor tekst-kolommen.
  // Sortering hieronder dispatcht op type.
  const getSortValue = (p: AdminProduct, col: SortCol): number | string => {
    switch (col) {
      case 'supplier_cost':   return p.supplier_cost ?? -Infinity;
      // v3.2.108 — Sortering volgt uniform targetCatalog (slider-based).
      // Override-logica is verwijderd samen met de edit-flow.
      case 'locked_price':    return targetCatalog(p.supplier_cost) ?? -Infinity;
      case 'verkoop_resell':  return resellerPriceRange(p)?.min ?? -Infinity;
      case 'incl21':          { const c = targetCatalog(p.supplier_cost); return c != null ? c * 1.21 : -Infinity; }
      case 'incl6':           { const c = targetCatalog(p.supplier_cost); return c != null ? c * 1.06 : -Infinity; }
      case 'margin':          return p.supplier_cost != null ? margeTarget : -Infinity;
      // String kolommen — leeg laten naar het einde sorteren
      case 'supplier':     return (p.supplier_name ?? '￿').toLowerCase();
      case 'category':     return (p.category ?? '￿').toLowerCase();
      case 'name':         return (p.name ?? '￿').toLowerCase();
    }
  };

  // Filter and sort products
  let displayProducts = [...products];
  if (supplierFilter) {
    displayProducts = displayProducts.filter(p => p.supplier_name === supplierFilter);
  }
  if (showBestsellers) {
    displayProducts = displayProducts.filter(p => bestsellers.has(p.id));
  }
  if (sortCol) {
    displayProducts.sort((a, b) => {
      const va = getSortValue(a, sortCol);
      const vb = getSortValue(b, sortCol);
      // Type-veilige compare: strings → localeCompare, numbers → diff
      if (typeof va === 'string' && typeof vb === 'string') {
        return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      const na = typeof va === 'number' ? va : 0;
      const nb = typeof vb === 'number' ? vb : 0;
      return sortDir === 'asc' ? na - nb : nb - na;
    });
  }

  // Unique supplier names for filter
  const supplierNames = [...new Set(products.map(p => p.supplier_name).filter(Boolean))] as string[];

  const SortHeader = ({ label, col, title }: { label: string; col: SortCol; title?: string }) => (
    <th
      className="text-left py-2 pr-4 text-xs font-semibold text-[#8a7e74] uppercase tracking-wide whitespace-nowrap cursor-pointer hover:text-[#c47a3a] select-none"
      onClick={() => toggleSort(col)}
      title={title}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortCol === col ? (
          <span className="text-[#c47a3a]">{sortDir === 'asc' ? '▲' : '▼'}</span>
        ) : (
          <ArrowUpDown size={10} className="opacity-40" />
        )}
      </span>
    </th>
  );

  const toggleExpand = (id: string) => {
    setExpandedId(prev => prev === id ? null : id);
  };

  const startEdit = (p: AdminProduct) => {
    setEditingId(p.id);
    setSaveError(null);
    // v3.2.54: name + category zijn read-only in Producten-tab geworden
    // (wijzigen gebeurt enkel in Leveranciers waar het product aangemaakt
    // is). We sturen ze niet meer mee, dus geen risico op accidentele
    // overwrite. Marge is ook read-only — wordt afgeleid uit netto + sell.
    // v3.2.86: supplier_* (AKP Lev / korting / netto inkoop) is GEEN edit
    // meer in Producten-tab — die hoort in de Leveranciers-tab thuis.
    // Filip bepaalt hier enkel de verkoopprijs eindklant (locked_price);
    // marge volgt automatisch.
    setEditData({
      locked_price: p.locked_price,
      target_margin_pct: p.target_margin_pct ?? null,
      allow_below_cost: (p as AdminProduct & { allow_below_cost?: number }).allow_below_cost ?? 0,
      serial_number: p.serial_number ?? '',
      ean_code: p.ean_code ?? '',
      tech_specs: p.tech_specs ?? '',
      dimensions: p.dimensions,
      delivery_weeks: (p as AdminProduct & { delivery_weeks?: number | null }).delivery_weeks ?? null,
      fiche_url: (p as AdminProduct & { fiche_url?: string | null }).fiche_url ?? '',
      product_type: (p as AdminProduct & { product_type?: string }).product_type ?? 'gevelsteen',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditData({});
    setSaveError(null);
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setSaving(true);
    setSaveError(null);
    try {
      const payload: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(editData)) {
        if (v === '') payload[k] = null;
        else payload[k] = v;
      }
      await adminUpdateProduct(editingId, payload);
      setEditingId(null);
      setEditData({});
      onRefresh();
    } catch (e) {
      setSaveError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const EditField = ({ label, field, type = 'text', placeholder }: { label: string; field: string; type?: string; placeholder?: string }) => (
    <div>
      <label className="block text-xs font-semibold text-[#8a7e74] uppercase tracking-wide mb-1">{label}</label>
      {field === 'category' ? (
        <select
          className="w-full px-3 py-3 border border-gray-200 rounded-lg text-sm sm:text-base focus:border-[#c47a3a] focus:outline-none min-h-[44px]"
          value={editData.category as string}
          onChange={e => setEditData(d => ({ ...d, category: e.target.value }))}
        >
          {['modern', 'landelijk', 'industrieel', 'klassiek'].map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      ) : (
        <input
          type={type}
          step={type === 'number' ? '0.01' : undefined}
          className="w-full px-3 py-3 border border-gray-200 rounded-lg text-sm sm:text-base focus:border-[#c47a3a] focus:outline-none min-h-[44px]"
          value={(field === 'locked_price' || field === 'delivery_weeks') ? ((editData[field] as number | null | undefined) ?? '') : (editData[field] as string)}
          onChange={e => {
            if (field === 'locked_price' || field === 'delivery_weeks') {
              // Numerieke velden → number of null, nooit een lege string naar de API.
              setEditData(d => ({ ...d, [field]: e.target.value ? parseFloat(e.target.value) : null }));
            } else {
              setEditData(d => ({ ...d, [field]: e.target.value }));
            }
          }}
          placeholder={placeholder}
        />
      )}
    </div>
  );

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="px-4 sm:px-6 py-4 border-b border-gray-100">
        <button
          type="button"
          onClick={() => setTabExpanded(e => !e)}
          className="w-full flex items-center justify-between mb-3 hover:bg-[#faf7f3] -mx-4 sm:-mx-6 px-4 sm:px-6 py-1 rounded transition-colors text-left"
        >
          <div className="flex items-center gap-3">
            <CheckCircle size={20} className="text-green-600 flex-shrink-0" />
            <h2 className="text-lg font-bold text-[#1a1a1a] flex items-center gap-2">
              <Package size={18} className="text-[#c47a3a]" /> {t('admin.products.title')}
              <span className="px-2 py-0.5 bg-[#c47a3a]/10 text-[#c47a3a] text-xs font-semibold rounded-full">{displayProducts.length}{supplierFilter || showBestsellers ? `/${products.length}` : ''}</span>
              <span className="px-2 py-0.5 bg-blue-50 text-blue-800 border border-blue-200 text-[10px] font-bold rounded uppercase tracking-wide" title={t('admin.products.priceM2Title')}>
                {t('admin.products.priceM2Badge')}
              </span>
            </h2>
          </div>
          <ChevronDown size={18} className={`text-[#8a7e74] transition-transform ${tabExpanded ? 'rotate-180' : ''}`} />
        </button>
        {/* Filter bar — only when expanded */}
        {tabExpanded && (
        <div className="flex flex-wrap items-center gap-2">
          {/* + Nieuw product knop — opent ProductCreateModal.
               Hoofd-CTA dus eerste knop in de filter-rij. */}
          {/* v3.2.56: '+ Nieuw product' verhuisd naar Leveranciers-tab.
               Producten worden nu aangemaakt vanuit de leverancier-rij zodat
               supplier_id automatisch correct gekoppeld is. Hier blijft een
               navigatie-hint achter die naar Leveranciers verwijst. */}
          <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-800 border border-blue-200 rounded-full text-[11px] italic">
            <Info size={12} /> Nieuwe producten toevoegen via tab <strong>Leveranciers</strong>
          </span>
          {supplierFilter && (
            <button
              onClick={() => setSupplierFilter(null)}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#c47a3a] text-white rounded-full text-xs font-medium hover:bg-[#b06930] transition-colors"
            >
              <X size={12} /> {t('admin.products.showAll')}
            </button>
          )}
          <button
            onClick={() => setShowBestsellers(!showBestsellers)}
            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              showBestsellers ? 'bg-amber-500 text-white' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
            }`}
            title={showBestsellers ? t('admin.products.bestsellersOn') : t('admin.products.bestsellersOff')}
          >
            <Star size={12} fill={showBestsellers ? 'white' : 'none'} /> {t('admin.products.bestsellers')}
            {showBestsellers && <X size={12} className="ml-0.5" />}
          </button>
          {/* v3.2.93 — Standaard reseller-korting instelbaar bovenaan.
               Beïnvloedt enkel de VERKOOP RESELL kolom (display-only); past
               geen DB-data aan. Filip kan hier scenario's doorrekenen voor
               hij definitief beslist wat de standaardkorting wordt. */}
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 border border-purple-200 rounded-full text-xs">
            <span className="text-[10px] font-bold text-purple-800 uppercase tracking-wide">Marge totaal</span>
            <div className="relative">
              <input
                type="number" min="0" max="99" step="1"
                value={margeTarget}
                onChange={e => {
                  const n = parseFloat(e.target.value);
                  setMargeTarget(isFinite(n) ? Math.min(99, Math.max(0, n)) : 0);
                }}
                className="w-14 px-2 py-1 border border-purple-300 rounded text-xs font-bold text-purple-900 bg-white focus:outline-none focus:border-purple-500 pr-5"
                title="Marge totaal in de catalogprijs — past de Marge-kolom, EXCL.BTW, BTW-varianten en Verkoop resell direct aan"
              />
              <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-purple-700 font-semibold pointer-events-none">%</span>
            </div>
            {/* v5 blok 6: minimummarge + kortingsplafond — server-side
                 afgedwongen in de prijsvloer en de kortingschecks. */}
            <div className="flex items-center gap-1.5 ml-3 pl-3 border-l border-purple-200">
              <span className="text-[10px] font-bold text-purple-900 uppercase tracking-wide" title="Verkoop- en kortingsprijzen kunnen nooit onder AKP + deze minimummarge zakken (behalve bij producten met 'Dumping toestaan')">Min. marge</span>
              <div className="relative">
                <input
                  type="number" min="0" max="99" step="1"
                  value={minMargin}
                  onChange={e => { const n = parseFloat(e.target.value); setMinMargin(isFinite(n) ? Math.min(99, Math.max(0, n)) : 0); }}
                  className="w-14 px-2 py-1 border border-purple-300 rounded text-xs font-bold text-purple-900 bg-white focus:outline-none focus:border-purple-500 pr-5"
                />
                <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-purple-700 font-semibold pointer-events-none">%</span>
              </div>
              <span className="text-[10px] font-bold text-purple-900 uppercase tracking-wide ml-2" title="Maximale resellerkorting (basis + extra samen). Hogere kortingen worden geweigerd.">Kortingsplafond</span>
              <div className="relative">
                <input
                  type="number" min="0" max="100" step="1"
                  value={maxDiscount}
                  onChange={e => { const n = parseFloat(e.target.value); setMaxDiscount(isFinite(n) ? Math.min(100, Math.max(0, n)) : 0); }}
                  className="w-14 px-2 py-1 border border-purple-300 rounded text-xs font-bold text-purple-900 bg-white focus:outline-none focus:border-purple-500 pr-5"
                />
                <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-purple-700 font-semibold pointer-events-none">%</span>
              </div>
              <span className="text-[10px] font-bold text-purple-900 uppercase tracking-wide ml-2" title="Vast snijverlies-percentage in elke berekening. Klanten en resellers zien dit maar kunnen het niet wijzigen.">Snijverlies</span>
              <div className="relative">
                <input
                  type="number" min="0" max="30" step="1"
                  value={cuttingLossPct}
                  onChange={e => { const n = parseFloat(e.target.value); setCuttingLossPct(isFinite(n) ? Math.min(30, Math.max(0, n)) : 0); }}
                  className="w-14 px-2 py-1 border border-purple-300 rounded text-xs font-bold text-purple-900 bg-white focus:outline-none focus:border-purple-500 pr-5"
                />
                <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-purple-700 font-semibold pointer-events-none">%</span>
              </div>
            </div>
            <span
              className={`inline-flex items-center gap-1 text-[10px] font-bold transition-opacity duration-300 ${discountSaved ? 'opacity-100 text-emerald-700' : 'opacity-0'}`}
              aria-live="polite"
            >
              <CheckCircle size={11} /> opgeslagen
            </span>
          </div>
          {/* Universele "Toon alle" reset — verschijnt zodra er filters actief
               zijn die producten verbergen. Voorkomt de "Help, ik zie geen
               producten meer" situatie. */}
          {(supplierFilter || showBestsellers) && displayProducts.length === 0 && (
            <button
              onClick={() => { setSupplierFilter(null); setShowBestsellers(false); }}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-100 text-red-700 hover:bg-red-200 rounded-full text-xs font-bold transition-colors animate-pulse"
              title={t('admin.products.resetFiltersTitle')}
            >
              <X size={12} /> {t('admin.products.resetFilters')}
            </button>
          )}
        </div>
        )}
      </div>
      {tabExpanded && (
      <div className="px-4 sm:px-6 pb-6 pt-2">
        {saveError && (
          <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 rounded-lg text-red-600 text-sm">
            <AlertCircle size={14} /> {saveError}
          </div>
        )}

        {/* Empty state — verschijnt wanneer een filter alle producten heeft
             verborgen. Toont een grote reset-knop zodat Filip altijd terug
             naar de volledige lijst kan, zelfs na 'Bestsellers' aanklikken
             zonder bestsellers te hebben gemarkeerd. */}
        {displayProducts.length === 0 && products.length > 0 && (
          <div className="my-4 p-6 bg-amber-50 border-2 border-amber-200 rounded-xl text-center">
            <Package size={32} className="mx-auto mb-2 text-amber-500" />
            <p className="text-sm font-bold text-amber-900 mb-1">
              {t('admin.products.emptyFilterTitle')}
            </p>
            <p className="text-xs text-amber-800 mb-3">
              {showBestsellers && supplierFilter && t('admin.products.emptyBoth').replace('{supplier}', supplierFilter).replace('{count}', String(products.length))}
              {showBestsellers && !supplierFilter && t('admin.products.emptyBestsellers')}
              {!showBestsellers && supplierFilter && t('admin.products.emptySupplier').replace('{supplier}', supplierFilter)}
            </p>
            <button
              onClick={() => { setSupplierFilter(null); setShowBestsellers(false); }}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#c47a3a] hover:bg-[#b06930] text-white rounded-lg text-sm font-bold shadow-sm"
            >
              <X size={14} /> {t('admin.products.resetFiltersFull').replace('{count}', String(products.length))}
            </button>
          </div>
        )}

        {/* Mobile: accordion layout */}
        <div className="sm:hidden space-y-2">
          {displayProducts.map(p => {
            const isEditing = editingId === p.id;
            const isExpanded = expandedId === p.id;

            if (isEditing) {
              return (
                <div key={p.id} className="border-2 border-[#c47a3a] rounded-xl p-4 bg-[#faf7f3]">
                  <h3 className="font-bold text-[#1a1a1a] mb-3">{t('admin.products.editProduct')}</h3>
                  <div className="space-y-3">
                    <EditField label={t('admin.products.name')} field="name" />
                    <EditField label={t('admin.products.category')} field="category" />
                    <EditField label={t('admin.products.dimensions')} field="dimensions" />
                    <EditField label={t('admin.products.serialNumber')} field="serial_number" placeholder={t('admin.products.optional')} />
                    <EditField label={t('admin.products.ean')} field="ean_code" placeholder={t('admin.products.optional')} />
                    <EditField label={t('admin.products.fixedPrice')} field="locked_price" type="number" placeholder="—" />
                    {/* v5 blok 4: levertermijn per product, zichtbaar op de offerte */}
                    <EditField label="Levertermijn (weken)" field="delivery_weeks" type="number" placeholder="bv. 8" />
                    {/* v5 blok 9: technische fiche (PDF) + producttype */}
                    <EditField label="Technische fiche (PDF-URL)" field="fiche_url" placeholder="/api/uploads/r2/products/....pdf" />
                    <div>
                      <label className="block text-xs font-semibold text-[#8a7e74] uppercase tracking-wide mb-1">Producttype</label>
                      <select
                        className="w-full px-3 py-3 border border-gray-200 rounded-lg text-sm focus:border-[#c47a3a] focus:outline-none min-h-[44px]"
                        value={(editData.product_type as string) ?? 'gevelsteen'}
                        onChange={e => setEditData(d => ({ ...d, product_type: e.target.value }))}
                      >
                        <option value="gevelsteen">Gevelsteen</option>
                        <option value="toebehoren">Toebehoren (lijm/voegsel/profielen)</option>
                      </select>
                    </div>
                    <EditField label={t('admin.products.techSpecs')} field="tech_specs" placeholder={t('admin.products.optional')} />
                  </div>
                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={saveEdit}
                      disabled={saving}
                      className="flex-1 flex items-center justify-center gap-1.5 px-4 py-3 bg-[#c47a3a] hover:bg-[#b06930] text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 min-h-[44px]"
                    >
                      <Save size={16} />
                      {saving ? t('admin.products.saving') : t('admin.products.save')}
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="flex-1 flex items-center justify-center gap-1.5 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-[#4a4a4a] rounded-lg text-sm font-medium transition-colors min-h-[44px]"
                    >
                      <X size={16} />
                      {t('admin.products.cancel')}
                    </button>
                  </div>
                </div>
              );
            }

            return (
              <div key={p.id} className="border border-gray-100 rounded-lg overflow-hidden">
                {/* Collapsed header — always visible */}
                <div className="flex items-center">
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleBestseller(p.id); }}
                    className="pl-3 pr-1 py-4 flex-shrink-0"
                    title="Bestseller"
                  >
                    <Star size={18} fill={bestsellers.has(p.id) ? '#f59e0b' : 'none'} className={bestsellers.has(p.id) ? 'text-amber-500' : 'text-gray-300'} />
                  </button>
                  <button
                    onClick={() => toggleExpand(p.id)}
                    className="flex-1 flex items-center justify-between gap-3 p-4 pl-2 text-left active:bg-[#faf7f3] transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-[#1a1a1a] text-base truncate">{p.name}</p>
                        {p.is_demo === 1 && (
                          <span
                            className="px-1.5 py-0.5 bg-purple-100 text-purple-800 border border-purple-300 rounded text-[9px] font-bold uppercase tracking-wide flex-shrink-0"
                            title="Demo voorbeeld-product — kan niet verwijderd worden"
                          >
                            DEMO
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        {p.supplier_name && (
                          <span
                            onClick={(e) => { e.stopPropagation(); setSupplierFilter(p.supplier_name); }}
                            className={`px-2 py-0.5 rounded text-xs font-medium cursor-pointer hover:opacity-80 ${
                              p.supplier_country === 'IT' ? 'bg-orange-100 text-orange-700' :
                              p.supplier_country === 'PL' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            {p.supplier_name}
                          </span>
                        )}
                        <span className="text-sm font-semibold text-[#1a1a1a]">
                          {p.locked_price != null ? `€${p.locked_price.toFixed(2)}/m²` : '—'}
                        </span>
                      </div>
                    </div>
                    {isExpanded ? (
                      <ChevronUp size={20} className="text-[#c47a3a] flex-shrink-0" />
                    ) : (
                      <ChevronDown size={20} className="text-[#8a7e74] flex-shrink-0" />
                    )}
                  </button>
                </div>

                {/* Expanded details */}
                <div
                  className="overflow-hidden transition-all duration-300 ease-in-out"
                  style={{ maxHeight: isExpanded ? '500px' : '0px', opacity: isExpanded ? 1 : 0 }}
                >
                  <div className="px-4 pb-4 pt-1 border-t border-gray-100">
                    <div className="grid grid-cols-2 gap-y-2 text-sm">
                      <span className="text-[#8a7e74]">{t('admin.products.dimensions')}</span>
                      <span className="text-[#4a4a4a]">{p.dimensions}</span>
                      <span className="text-[#8a7e74]">{t('admin.products.color')}</span>
                      <div className="flex items-center gap-1.5">
                        {p.color_hex && <span className="w-3 h-3 rounded-full border border-gray-200 inline-block" style={{ backgroundColor: p.color_hex }} />}
                        <span className="text-[#4a4a4a]">{p.color}</span>
                      </div>
                      <span className="text-[#8a7e74]">{t('admin.products.texture')}</span>
                      <span className="text-[#4a4a4a]">{p.texture}</span>
                      <span className="text-[#8a7e74]">{t('admin.products.serialNumber')}</span>
                      <span className="text-[#8a7e74] font-mono text-xs">{p.serial_number || '—'}</span>
                      <span className="text-[#8a7e74]">EAN</span>
                      <span className="text-[#8a7e74] font-mono text-xs">{p.ean_code || '—'}</span>
                      {p.tech_specs && (
                        <>
                          <span className="text-[#8a7e74]">{t('admin.products.specs')}</span>
                          <span className="text-[#8a7e74] text-xs">{p.tech_specs}</span>
                        </>
                      )}
                    </div>
                    <div className="mt-3 grid grid-cols-1 gap-2">
                      {/* v3.2.108 — Mobile Edit-knop verwijderd (zie desktop). */}
                      <button
                        onClick={(e) => { e.stopPropagation(); setDiscountForId(p.id); }}
                        className={`flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors min-h-[44px] ${
                          p.show_discount_badge || p.liquidation_active
                            ? 'bg-rose-100 hover:bg-rose-200 text-rose-700'
                            : 'bg-gray-50 hover:bg-gray-100 text-[#4a4a4a]'
                        }`}
                      >
                        <Tag size={16} />
                        {t('admin.products.discountManage')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Desktop: table */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-2 pr-2 text-xs font-semibold text-[#8a7e74] uppercase tracking-wide w-8"></th>
                <SortHeader label={t('admin.products.name')}     col="name" />
                <SortHeader label={t('admin.products.supplier')} col="supplier" />
                {/* v3.2.96 — Categorie kolom weg op Filip's verzoek; categorie
                    blijft wel als filter beschikbaar via de leveranciers. */}
                <SortHeader label={t('admin.products.purchase')} col="supplier_cost" title={t('admin.products.purchaseTooltip')} />
                <SortHeader label="Marge totaal"                  col="margin"        title="Totale marge in de catalogprijs — past zich aan de slider bovenaan aan" />
                <SortHeader label="Verkoop resell"                col="verkoop_resell" title={`Wat resellers aan Filip betalen — bereik over de echte kortingen per reseller (${resellerDiscounts.length > 0 ? resellerDiscounts.map(d => d + '%').join(', ') : 'geen resellers; fallback ' + RESELLER_SHARE_PCT + '%'}); per-product override in Kortingen-tab`} />
                <SortHeader label={t('admin.products.exclVat')}  col="locked_price"  title={t('admin.products.exclVatTooltip')} />
                <SortHeader label={t('admin.products.incl6')}    col="incl6" />
                <SortHeader label={t('admin.products.incl21')}   col="incl21" />
                <th className="text-left py-2 pr-4 text-xs font-semibold text-[#8a7e74] uppercase tracking-wide whitespace-nowrap">{t('admin.products.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {displayProducts.map(p => {
                const isEditing = editingId === p.id;
                const supplierBadgeCls = p.supplier_country === 'IT'
                  ? 'bg-orange-100 text-orange-700'
                  : p.supplier_country === 'PL'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-100 text-gray-600';
                // v3.2.108 — Rij-tint voor prijs-override is weg samen met de
                // edit-flow. Alle rijen volgen nu uniform de slider-marge.
                return (
                  <tr key={p.id} className="border-b border-gray-50 hover:bg-[#faf7f3] transition-colors">
                    <td className="py-2.5 pr-2">
                      <button onClick={() => toggleBestseller(p.id)} className="p-1 hover:scale-110 transition-transform" title="Bestseller">
                        <Star size={16} fill={bestsellers.has(p.id) ? '#f59e0b' : 'none'} className={bestsellers.has(p.id) ? 'text-amber-500' : 'text-gray-300 hover:text-amber-300'} />
                      </button>
                    </td>
                    {isEditing ? (
                      <>
                        {/* Naam: read-only in Producten — wijzigen gebeurt enkel
                             in de Leveranciers-tab waar het product is aangemaakt. */}
                        <td className="py-2 pr-3 font-medium text-[#1a1a1a] whitespace-nowrap">
                          {p.name}
                        </td>
                        <td className="py-2 pr-3 text-[#8a7e74] text-xs">{p.supplier_name || '—'}</td>
                        {/* v3.2.86 — INKOOP cel is READ-ONLY in Producten-tab.
                             Filip beslist hier alleen de VERKOOPprijs (excl. BTW
                             in de cel hierna). De inkoopkant (AKP Lev. + korting +
                             netto inkoop) wijzig je in de Leveranciers-tab waar
                             het product onder zijn leverancier hangt. Reden:
                             Filip's business is doorverkoop — hij verdient aan
                             marge & inkoopkorting, niet aan kortingen aan
                             eindklant op productniveau (die zitten in de
                             Kortingen-modal of via de Resellers-tab). */}
                        <td className="py-2 pr-3 text-xs">
                          <div className="text-[#4a4a4a] font-medium" title={t('admin.products.netAkpTooltip')}>
                            {p.supplier_cost != null ? `${formatEuro(p.supplier_cost)}/m²` : '—'}
                          </div>
                          {p.supplier_list_price != null && p.supplier_discount_pct != null && (
                            <div className="text-[10px] text-[#8a7e74] mt-0.5">
                              {t('admin.products.purchaseSubtitle').replace('{akp}', formatEuro(p.supplier_list_price)).replace('{disc}', String(p.supplier_discount_pct))}
                            </div>
                          )}
                          <div className="text-[9px] text-blue-700 italic mt-1">
                            ✎ wijzig via Leveranciers
                          </div>
                        </td>
                        {/* Marge — read-only, afgeleid uit p.supplier_cost (vast)
                             en editData.locked_price (live). */}
                        <td className="py-2 pr-3">
                          {(() => {
                            const cost = p.supplier_cost;
                            const sell = editData.locked_price as number | null | undefined;
                            const margin = (cost != null && sell != null && sell > 0)
                              ? Math.round(((sell - cost) / sell) * 1000) / 10
                              : null;
                            return margin != null ? (
                              <span className={`font-semibold text-xs ${margin > 0 ? 'text-green-700' : 'text-red-600'}`}>{margin.toFixed(1)}%</span>
                            ) : <span className="text-[#8a7e74] text-xs">—</span>;
                          })()}
                        </td>
                        {/* Verkoop Resell — live-preview in edit-mode op basis
                             van de échte resellerkortingen (bereik), zelfde
                             berekening als de gewone kolom. */}
                        <td className="py-2 pr-3 text-[#8a7e74] text-xs">
                          {(() => {
                            const sell = editData.locked_price as number | null | undefined;
                            if (sell == null) return '—';
                            const discounts = resellerDiscounts.length > 0 ? resellerDiscounts : [RESELLER_SHARE_PCT];
                            const prices = discounts.map(d => Math.round(sell * (1 - d / 100) * 100) / 100);
                            const min = Math.min(...prices), max = Math.max(...prices);
                            return min === max ? `${formatEuro(min)}/m²` : `${formatEuro(min)}–${formatEuro(max)}/m²`;
                          })()}
                        </td>
                        {/* Excl BTW — HET enige editable veld in deze rij.
                             Verkoopprijs eindklant ex. BTW = locked_price. */}
                        <td className="py-2 pr-3">
                          <div className="min-w-[120px] space-y-1">
                            <div className="relative">
                              <input
                                type="number" step="1" min="0"
                                className="w-full px-2 py-1.5 border-2 border-[#c47a3a] bg-amber-50/30 rounded text-xs font-bold text-[#1a1a1a] focus:border-[#a8643a] focus:outline-none pr-8"
                                value={(editData.locked_price as number | null | undefined) ?? ''}
                                onChange={e => setEditData(d => ({ ...d, locked_price: e.target.value ? parseFloat(e.target.value) : null }))}
                                placeholder="—"
                                title="Verkoopprijs eindklant ex. BTW"
                              />
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-[#8a7e74] font-semibold pointer-events-none">/m²</span>
                            </div>
                            {/* Prijsvloer-override: alleen met dit vinkje aan
                                 accepteert de API een prijs onder inkoop. */}
                            <label className="flex items-center gap-1.5 pt-1 cursor-pointer select-none" title="Sta bewust een verkoopprijs onder de inkoopprijs toe (dumping / restpartij wegwerken). Zonder vinkje weigert het systeem elke prijs of korting onder inkoop.">
                              <input
                                type="checkbox"
                                checked={(editData.allow_below_cost as number | undefined) === 1}
                                onChange={e => setEditData(d => ({ ...d, allow_below_cost: e.target.checked ? 1 : 0 }))}
                                className="accent-red-600"
                              />
                              <span className={`text-[10px] font-bold ${(editData.allow_below_cost as number | undefined) === 1 ? 'text-red-600' : 'text-[#8a7e74]'}`}>Dumping toestaan</span>
                            </label>
                            {/* Inline Save/Cancel direct onder de input zodat
                                 Filip niet hoeft te scrollen naar de ACTIES kolom. */}
                            <div className="flex items-center gap-1.5 pt-1">
                              <button
                                onClick={saveEdit}
                                disabled={saving}
                                className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded text-[11px] font-bold shadow-sm"
                                title={t('admin.products.save')}
                              >
                                <Save size={12} /> {saving ? t('admin.products.saving') : t('admin.products.save')}
                              </button>
                              <button
                                onClick={cancelEdit}
                                className="inline-flex items-center justify-center gap-1 px-2 py-1.5 bg-white border border-gray-300 hover:bg-gray-50 text-[#4a4a4a] rounded text-[11px] font-medium"
                                title={t('admin.products.cancel')}
                              >
                                <X size={12} />
                              </button>
                            </div>
                          </div>
                        </td>
                        <td className="py-2 pr-3 text-[#8a7e74] text-xs">
                          {editData.locked_price != null ? `${formatEuro((editData.locked_price as number) * 1.06)}/m²` : '—'}
                        </td>
                        <td className="py-2 pr-3 text-[#8a7e74] text-xs">
                          {editData.locked_price != null ? `${formatEuro((editData.locked_price as number) * 1.21)}/m²` : '—'}
                        </td>
                        <td className="py-2">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={saveEdit}
                              disabled={saving}
                              className="p-2.5 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                              title={t('admin.products.save')}
                            >
                              <Save size={16} />
                            </button>
                            <button
                              onClick={cancelEdit}
                              className="p-2.5 bg-gray-50 hover:bg-gray-100 text-gray-600 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                              title={t('admin.products.cancel')}
                            >
                              <X size={16} />
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="py-2.5 pr-4 font-medium text-[#1a1a1a] whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            {p.name}
                            {p.is_demo === 1 && (
                              <span
                                className="px-1.5 py-0.5 bg-purple-100 text-purple-800 border border-purple-300 rounded text-[9px] font-bold uppercase tracking-wide"
                                title="Demo voorbeeld-product — kan niet verwijderd worden"
                              >
                                DEMO
                              </span>
                            )}
                            {(p as AdminProduct & { allow_below_cost?: number }).allow_below_cost === 1 && (
                              <span
                                className="px-1.5 py-0.5 bg-red-100 text-red-700 border border-red-300 rounded text-[9px] font-bold uppercase tracking-wide"
                                title="Dumping toegestaan — prijzen en kortingen onder inkoopprijs worden voor dit product NIET geblokkeerd"
                              >
                                DUMPING
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-2.5 pr-4">
                          {p.supplier_name ? (
                            <button
                              onClick={() => setSupplierFilter(p.supplier_name)}
                              title={t('admin.products.filterSupplier')}
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium cursor-pointer hover:scale-105 hover:shadow-md transition-all ${supplierBadgeCls}`}
                            >
                              {p.supplier_name}
                            </button>
                          ) : '—'}
                        </td>
                        {/* v3.2.96 — Categorie cel verwijderd op Filip's verzoek. */}
                        {/* Inkoop = Netto AKP. Wanneer Filip ook AKP Lev. + Korting
                             heeft ingegeven, tonen we die als subtitel zodat in
                             één oogopslag zichtbaar is welke leveranciersdeal
                             eronder zit. */}
                        <td className="py-2.5 pr-4 text-xs">
                          <div className="text-[#4a4a4a] font-medium" title={t('admin.products.netAkpTooltip')}>
                            {p.supplier_cost != null ? `${formatEuro(p.supplier_cost)}/m²` : '—'}
                          </div>
                          {p.supplier_list_price != null && p.supplier_discount_pct != null && (
                            <div className="text-[10px] text-[#8a7e74] mt-0.5" title={`${t('admin.products.akpLevTooltip')} (€${p.supplier_list_price.toFixed(2)}/m² · −${p.supplier_discount_pct}%)`}>
                              {t('admin.products.purchaseSubtitle').replace('{akp}', formatEuro(p.supplier_list_price)).replace('{disc}', String(p.supplier_discount_pct))}
                            </div>
                          )}
                        </td>
                        {/* Marge % — toont actuele marge + doel-afspraak (als gezet).
                             Groen = klopt met afspraak. Oranje ⚠ = wijkt af. */}
                        <td className="py-2.5 pr-4 text-xs">
                          {/* v3.2.108 — Alle rijen volgen nu uniform de
                              MARGE TOTAAL slider. Override-detectie (rode
                              pill) is weg — Filip wou per-product editen
                              uit Producten halen, dus is er ook niets meer
                              te overrulen. Prijzen worden centraal beslist. */}
                          {p.supplier_cost == null ? (
                            <span className="text-[#8a7e74]">—</span>
                          ) : (
                            <span className="font-semibold text-green-700">{margeTarget.toFixed(1)}%</span>
                          )}
                        </td>
                        {/* v3.2.108 — Alle prijskolommen volgen uniform de
                            MARGE TOTAAL slider via targetCatalog(supplier_cost).
                            Geen per-product override meer. */}
                        <td className="py-2.5 pr-4 text-[#8a7e74]">{(() => { const r = resellerPriceRange(p); if (!r) return '—'; return r.min === r.max ? `${formatEuro(r.min)}/m²` : `${formatEuro(r.min)}–${formatEuro(r.max)}/m²`; })()}</td>
                        <td className="py-2.5 pr-4 text-[#4a4a4a] font-medium">{(() => { const c = targetCatalog(p.supplier_cost); return c != null ? `${formatEuro(c)}/m²` : '—'; })()}</td>
                        <td className="py-2.5 pr-4 text-[#4a4a4a]">{(() => { const c = targetCatalog(p.supplier_cost); return c != null ? `${formatEuro(c * 1.06)}/m²` : '—'; })()}</td>
                        <td className="py-2.5 pr-4 text-[#8a7e74]">{(() => { const c = targetCatalog(p.supplier_cost); return c != null ? `${formatEuro(c * 1.21)}/m²` : '—'; })()}</td>
                        <td className="py-2.5">
                          <div className="flex items-center gap-1">
                            {/* v3.2.108 — Edit-knop voor per-product prijs is
                                verwijderd op Filip's verzoek. Prijzen worden
                                centraal beslist via de MARGE TOTAAL-slider
                                bovenaan; inkoop edit je via de Leveranciers-tab. */}
                            <button
                              onClick={() => setDiscountForId(p.id)}
                              className={`p-2.5 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center relative ${
                                p.show_discount_badge || p.liquidation_active
                                  ? 'bg-rose-100 hover:bg-rose-200 text-rose-700'
                                  : 'bg-gray-50 hover:bg-gray-100 text-gray-500'
                              }`}
                              title={t('admin.products.discountManage')}
                            >
                              <Tag size={16} />
                              {(p.show_discount_badge || p.liquidation_active) && (
                                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-rose-500 rounded-full ring-2 ring-white" />
                              )}
                            </button>
                            {/* Prijshistoriek-knop (v3.2.56) — opent een modal met
                                 de tijdlijn van AKP/korting/netto-wijzigingen. */}
                            <button
                              onClick={() => setHistoryForId(p.id)}
                              className="p-2.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                              title="Prijshistoriek bekijken"
                            >
                              <History size={16} />
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* ─── Discount manager modal ────────────────────────────────────── */}
      {productForDiscount && (
        <ProductDiscountModal
          product={productForDiscount}
          onClose={() => setDiscountForId(null)}
          onSaved={() => { setDiscountForId(null); onRefresh(); }}
        />
      )}

      {/* ─── Prijshistoriek modal ───────────────────────────────────────── */}
      {productForHistory && (
        <PriceHistoryModal
          productId={productForHistory.id}
          productName={productForHistory.name}
          onClose={() => setHistoryForId(null)}
        />
      )}

      {/* ─── Nieuw product modal ────────────────────────────────────────── */}
      {creatingProduct && (
        <ProductCreateModal
          onClose={() => setCreatingProduct(false)}
          onSaved={() => { setCreatingProduct(false); onRefresh(); }}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PRODUCT DISCOUNT MODAL — Filip beheert hier de korting-badge per product:
//   • show_discount_badge   toon korting op productkaart (front-end)
//   • discount_pct          standaard korting % (max 50)
//   • discount_label        bv. "Vroege boeking", "Promo"
//   • liquidation_active    overschrijft discount_pct in periode
//   • liquidation_pct       liquidatie % (max 50)
//   • liquidation_label     bv. "Liquidatie", "Einde seizoen"
//   • liquidation_start/end periode (datepicker → unix ts)
// ═══════════════════════════════════════════════════════════════════════════════

function ProductDiscountModal({
  product,
  onClose,
  onSaved,
}: {
  product: AdminProduct;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useLanguage();
  const tsToInputDate = (ts: number | null | undefined): string =>
    ts ? new Date(ts * 1000).toISOString().slice(0, 10) : '';
  const inputDateToTs = (s: string): number | null =>
    s ? Math.floor(new Date(s + 'T00:00:00').getTime() / 1000) : null;

  const [showDiscount, setShowDiscount] = useState<boolean>(!!product.show_discount_badge);
  const [discountPct, setDiscountPct] = useState<number>(product.discount_pct ?? 0);
  const [discountLabel, setDiscountLabel] = useState<string>(product.discount_label ?? '');
  const [liquidActive, setLiquidActive] = useState<boolean>(!!product.liquidation_active);
  const [liquidPct, setLiquidPct] = useState<number>(product.liquidation_pct ?? 0);
  const [liquidLabel, setLiquidLabel] = useState<string>(product.liquidation_label ?? '');
  const [liquidStart, setLiquidStart] = useState<string>(tsToInputDate(product.liquidation_start));
  const [liquidEnd, setLiquidEnd] = useState<string>(tsToInputDate(product.liquidation_end));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Compute live preview price
  const catalogPrice = product.locked_price;
  const now = Math.floor(Date.now() / 1000);
  const liquidWindowOk =
    liquidActive &&
    (!liquidStart || (inputDateToTs(liquidStart) ?? 0) <= now) &&
    (!liquidEnd || (inputDateToTs(liquidEnd) ?? Infinity) >= now);
  const effectivePct = liquidWindowOk ? liquidPct : (showDiscount ? discountPct : 0);
  const effectivePrice = catalogPrice != null ? catalogPrice * (1 - effectivePct / 100) : null;

  const save = async () => {
    if (discountPct > 50 || liquidPct > 50) {
      setError(t('admin.products.discountMax50'));
      return;
    }
    if (liquidActive && liquidStart && liquidEnd) {
      const s = inputDateToTs(liquidStart);
      const e = inputDateToTs(liquidEnd);
      if (s != null && e != null && e < s) {
        setError(t('admin.products.discountDateInvalid'));
        return;
      }
    }
    setSaving(true);
    setError(null);
    try {
      await adminUpdateProduct(product.id, {
        show_discount_badge: showDiscount ? 1 : 0,
        discount_pct: discountPct,
        discount_label: discountLabel || null,
        liquidation_active: liquidActive ? 1 : 0,
        liquidation_pct: liquidPct,
        liquidation_label: liquidLabel || null,
        liquidation_start: inputDateToTs(liquidStart),
        liquidation_end: inputDateToTs(liquidEnd),
      });
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  // v5.22: actie in één klik volledig verwijderen — beide schakelaars uit,
  // percentages, labels en periode gewist. De verkoopprijs zelf blijft
  // ongemoeid; alleen de actie/badge verdwijnt van de voorkant.
  const removeAction = async () => {
    if (!confirm(`Kortingsactie volledig verwijderen voor "${product.name}"? De verkoopprijs blijft staan; alleen de actie en badge verdwijnen.`)) return;
    setSaving(true);
    setError(null);
    try {
      await adminUpdateProduct(product.id, {
        show_discount_badge: 0,
        discount_pct: 0,
        discount_label: null,
        liquidation_active: 0,
        liquidation_pct: 0,
        liquidation_label: null,
        liquidation_start: null,
        liquidation_end: null,
      });
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-rose-50 to-amber-50">
          <div>
            <h3 className="text-lg font-bold text-[#1a1a1a] flex items-center gap-2">
              <Tag size={18} className="text-rose-700" />
              {t('admin.products.discountFor')} {product.name}
            </h3>
            <p className="text-xs text-[#8a7e74] mt-0.5">{t('admin.products.discountHelp')}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/60 rounded-lg" title={t('admin.products.cancel')}>
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
              <AlertCircle size={14} /> {error}
            </div>
          )}

          {/* ─── Standaard korting ─────────────────────────────────── */}
          <div className="border border-rose-200 rounded-xl bg-rose-50/40">
            <label className="flex items-center gap-3 px-4 py-3 cursor-pointer">
              <input
                type="checkbox"
                checked={showDiscount}
                onChange={e => setShowDiscount(e.target.checked)}
                className="w-5 h-5 accent-rose-600"
              />
              <div className="flex-1">
                <div className="font-semibold text-[#1a1a1a] flex items-center gap-2">
                  <Tag size={14} className="text-rose-600" />
                  {t('admin.products.discountShow')}
                </div>
                <div className="text-xs text-[#8a7e74] mt-0.5">{t('admin.products.discountShowHelp')}</div>
              </div>
            </label>
            {showDiscount && (
              <div className="px-4 pb-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-[#8a7e74] uppercase tracking-wide mb-1">
                    {t('admin.products.discountPct')} <span className="font-normal lowercase">({t('admin.products.discountMax')})</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number" min="0" max="50" step="1"
                      value={discountPct}
                      onChange={e => setDiscountPct(Math.min(50, Math.max(0, Number(e.target.value) || 0)))}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-rose-500 pr-7"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#8a7e74]">%</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-[#8a7e74] uppercase tracking-wide mb-1">
                    {t('admin.products.discountLabel')}
                  </label>
                  <input
                    type="text" maxLength={60}
                    value={discountLabel}
                    onChange={e => setDiscountLabel(e.target.value)}
                    placeholder={t('admin.products.discountLabelPh')}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-rose-500"
                  />
                </div>
              </div>
            )}
          </div>

          {/* ─── Liquidatie / einde seizoen ────────────────────────── */}
          <div className="border border-amber-300 rounded-xl bg-amber-50/40">
            <label className="flex items-center gap-3 px-4 py-3 cursor-pointer">
              <input
                type="checkbox"
                checked={liquidActive}
                onChange={e => setLiquidActive(e.target.checked)}
                className="w-5 h-5 accent-amber-600"
              />
              <div className="flex-1">
                <div className="font-semibold text-[#1a1a1a] flex items-center gap-2">
                  <Flame size={14} className="text-amber-600" />
                  {t('admin.products.liquidationToggle')}
                </div>
                <div className="text-xs text-[#8a7e74] mt-0.5">{t('admin.products.liquidationHelp')}</div>
              </div>
            </label>
            {liquidActive && (
              <div className="px-4 pb-4 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-[#8a7e74] uppercase tracking-wide mb-1">
                      {t('admin.products.liquidationPct')} <span className="font-normal lowercase">({t('admin.products.discountMax')})</span>
                    </label>
                    <div className="relative">
                      <input
                        type="number" min="0" max="50" step="1"
                        value={liquidPct}
                        onChange={e => setLiquidPct(Math.min(50, Math.max(0, Number(e.target.value) || 0)))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-amber-500 pr-7"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#8a7e74]">%</span>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-[#8a7e74] uppercase tracking-wide mb-1">
                      {t('admin.products.liquidationLabel')}
                    </label>
                    <input
                      type="text" maxLength={60}
                      value={liquidLabel}
                      onChange={e => setLiquidLabel(e.target.value)}
                      placeholder={t('admin.products.liquidationLabelPh')}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-amber-500"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-[#8a7e74] uppercase tracking-wide mb-1 flex items-center gap-1">
                      <Calendar size={12} /> {t('admin.products.liquidationStart')}
                    </label>
                    <input
                      type="date"
                      value={liquidStart}
                      onChange={e => setLiquidStart(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-amber-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-[#8a7e74] uppercase tracking-wide mb-1 flex items-center gap-1">
                      <Calendar size={12} /> {t('admin.products.liquidationEnd')}
                    </label>
                    <input
                      type="date"
                      value={liquidEnd}
                      onChange={e => setLiquidEnd(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-amber-500"
                    />
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const today = new Date();
                      const inOneMonth = new Date(today);
                      inOneMonth.setMonth(today.getMonth() + 1);
                      setLiquidStart(today.toISOString().slice(0, 10));
                      setLiquidEnd(inOneMonth.toISOString().slice(0, 10));
                    }}
                    className="text-xs px-3 py-1.5 rounded-full bg-white border border-amber-200 hover:bg-amber-100 text-amber-700 font-medium"
                  >
                    {t('admin.products.preset1month')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const today = new Date();
                      const inOneWeek = new Date(today);
                      inOneWeek.setDate(today.getDate() + 7);
                      setLiquidStart(today.toISOString().slice(0, 10));
                      setLiquidEnd(inOneWeek.toISOString().slice(0, 10));
                    }}
                    className="text-xs px-3 py-1.5 rounded-full bg-white border border-amber-200 hover:bg-amber-100 text-amber-700 font-medium"
                  >
                    {t('admin.products.preset1week')}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* ─── Live preview ──────────────────────────────────────── */}
          {catalogPrice != null && (
            <div className="border border-gray-200 rounded-xl bg-gray-50 p-4">
              <h4 className="text-xs font-bold text-[#8a7e74] uppercase tracking-wide mb-2">{t('admin.products.preview')}</h4>
              <div className="flex items-end gap-3 flex-wrap">
                <div>
                  <div className="text-xs text-[#8a7e74]">{t('admin.products.catalogPrice')}</div>
                  <div className={`text-base ${effectivePct > 0 ? 'line-through text-[#8a7e74]' : 'font-bold text-[#1a1a1a]'}`}>
                    {formatEuro(catalogPrice)}/m²
                  </div>
                </div>
                {effectivePct > 0 && effectivePrice != null && (
                  <>
                    <div>
                      <div className="text-xs text-rose-700">{t('admin.products.afterDiscount')}</div>
                      <div className="text-2xl font-extrabold text-rose-700">{formatEuro(effectivePrice)}/m²</div>
                    </div>
                    <div className="ml-auto">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold text-white ${liquidWindowOk ? 'bg-amber-600' : 'bg-rose-600'}`}>
                        −{effectivePct}% {liquidWindowOk && liquidLabel ? `· ${liquidLabel}` : (showDiscount && discountLabel ? `· ${discountLabel}` : '')}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 flex flex-col sm:flex-row gap-2 sm:justify-end bg-gray-50 rounded-b-2xl">
          {/* v5.22: actie in één klik weg — alleen tonen als er iets actief is */}
          {(product.show_discount_badge || product.liquidation_active) ? (
            <button
              onClick={() => void removeAction()}
              disabled={saving}
              className="px-5 py-2.5 bg-white border border-red-300 hover:bg-red-50 text-red-700 rounded-lg text-sm font-bold min-h-[44px] sm:mr-auto"
              title="Verwijdert de kortingsactie én de tijdsgebonden liquidatie volledig; de verkoopprijs blijft ongewijzigd"
            >
              Actie verwijderen
            </button>
          ) : null}
          <button
            onClick={onClose}
            className="px-5 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-[#4a4a4a] rounded-lg text-sm font-medium min-h-[44px]"
          >
            {t('admin.products.cancel')}
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="flex items-center justify-center gap-1.5 px-5 py-2.5 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium min-h-[44px]"
          >
            <Save size={14} />
            {saving ? t('admin.profile.saving') : t('admin.products.discountSave')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PRICE HISTORY MODAL — tijdlijn van AKP Lev / korting / netto wijzigingen
// per product. Wordt gevoed door de supplier_price_history tabel (zie
// migratie 0025) — elke wijziging vanuit PUT /admin/products schrijft een
// snapshot weg, dus deze modal toont nooit "current" als entry maar wel
// alle voorgaande versies + de huidige actieve prijs als marker bovenaan.
// ═══════════════════════════════════════════════════════════════════════════════

function PriceHistoryModal({
  productId, productName, onClose,
}: {
  productId: string;
  productName: string;
  onClose: () => void;
}) {
  const [data, setData] = useState<import('../lib/api').ProductPriceHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const { adminGetProductHistory } = await import('../lib/api');
        setData(await adminGetProductHistory(productId));
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, [productId]);

  const formatDate = (ts: number) =>
    new Date(ts * 1000).toLocaleString('nl-BE', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl my-8" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-blue-50 to-blue-100/50">
          <div>
            <h3 className="text-lg font-bold text-[#1a1a1a] flex items-center gap-2">
              <History size={18} className="text-blue-700" /> Prijshistoriek
            </h3>
            <p className="text-xs text-[#8a7e74] mt-0.5">
              <span className="font-semibold text-blue-900">{productName}</span> — alle prijswijzigingen sinds aanmaak
            </p>
          </div>
          <button onClick={onClose} className="p-1 text-[#8a7e74] hover:text-[#1a1a1a]"><X size={18} /></button>
        </div>

        <div className="px-6 py-5 max-h-[70vh] overflow-y-auto">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-[#8a7e74] py-6">
              <Loader2 className="animate-spin" size={14} /> Geschiedenis ophalen…
            </div>
          )}
          {error && <div className="p-3 bg-red-50 text-red-700 text-sm rounded">{error}</div>}
          {data && (
            <>
              {/* Huidige actieve prijs als markeerblok bovenaan */}
              {data.current && (data.current.price_eur != null || data.current.list_price_eur != null) && (
                <div className="mb-4 p-3 bg-emerald-50 border-2 border-emerald-200 rounded-lg">
                  <p className="text-[10px] font-bold text-emerald-900 uppercase tracking-wide mb-1">Huidige actieve prijs</p>
                  <div className="flex items-center gap-4 text-sm flex-wrap">
                    <span><span className="text-[#8a7e74]">AKP Lev: </span><strong>{data.current.list_price_eur != null ? `€${data.current.list_price_eur.toFixed(2)}/m²` : '—'}</strong></span>
                    <span><span className="text-[#8a7e74]">Korting: </span><strong>{data.current.supplier_discount_pct != null ? `${data.current.supplier_discount_pct}%` : '—'}</strong></span>
                    <span><span className="text-[#8a7e74]">Netto: </span><strong>{data.current.price_eur != null ? `€${data.current.price_eur.toFixed(2)}/m²` : '—'}</strong></span>
                  </div>
                </div>
              )}

              {/* Tijdlijn — nieuwste bovenaan */}
              {data.history.length === 0 ? (
                <div className="text-center py-8">
                  <History size={32} className="mx-auto mb-2 text-gray-300" />
                  <p className="text-sm text-[#8a7e74]">Nog geen geschiedenis-snapshots.</p>
                  <p className="text-xs text-[#8a7e74] mt-1">Bij elke prijswijziging wordt automatisch een snapshot bewaard.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-[11px] font-bold text-[#8a7e74] uppercase tracking-wide mb-2">
                    Geschiedenis ({data.history.length} {data.history.length === 1 ? 'wijziging' : 'wijzigingen'}):
                  </p>
                  {data.history.map((h, idx) => (
                    <div key={h.id} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2">
                          <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[9px] font-bold">v{data.history.length - idx}</span>
                          <span className="text-xs text-[#8a7e74]">{formatDate(h.changed_at)}</span>
                        </div>
                        {h.changed_by_email && (
                          <span className="text-[10px] text-[#8a7e74] font-mono">{h.changed_by_email}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-sm flex-wrap">
                        <span><span className="text-[10px] text-[#8a7e74] uppercase mr-1">AKP</span><strong className="text-[#1a1a1a]">{h.list_price_eur != null ? `€${h.list_price_eur.toFixed(2)}` : '—'}</strong></span>
                        <span><span className="text-[10px] text-[#8a7e74] uppercase mr-1">Kort.</span><strong className="text-[#1a1a1a]">{h.supplier_discount_pct != null ? `${h.supplier_discount_pct}%` : '—'}</strong></span>
                        <span><span className="text-[10px] text-[#8a7e74] uppercase mr-1">Netto</span><strong className="text-[#1a1a1a]">{h.price_eur != null ? `€${h.price_eur.toFixed(2)}` : '—'}</strong></span>
                      </div>
                      {h.reason && (
                        <p className="text-[10px] text-[#8a7e74] italic mt-1">— {h.reason}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-6 py-3 border-t border-gray-100 bg-gray-50 rounded-b-xl text-right">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-[#4a4a4a] bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Sluiten
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PRODUCT CREATE MODAL — Filip voegt een nieuw product toe (alle velden vereist
// behalve de optionele zoals serial_number, ean_code, tech_specs, locked_price)
// ═══════════════════════════════════════════════════════════════════════════════

function ProductCreateModal({ onClose, onSaved, presetSupplierId, presetSupplierName }: { onClose: () => void; onSaved: () => void; presetSupplierId?: string; presetSupplierName?: string }) {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    name: '',
    slug: '',
    // Kleur/Kleur-hex velden weggelaten in v3.2.58 — foto bepaalt nu de
    // visuele identiteit op de productkaart. Backend krijgt placeholders
    // ('Foto' / '#888888') zodat de bestaande NOT NULL-constraint niet breekt
    // en oude UI die kleurchips verwacht een neutrale fallback heeft.
    texture: 'Ruw',
    category: 'modern' as 'modern' | 'landelijk' | 'industrieel' | 'klassiek',
    dimensions: '240 x 71 x 15 mm',
    weight: '4.0 kg/m²',
    description_nl: '',
    supplier_code: '',
    // v3.2.56: bij open vanuit Leveranciers-tab pre-fillen we supplier_id.
    supplier_id: presetSupplierId ?? '',
    locked_price: '',
    serial_number: '',
    tech_specs: '',
    ean_code: '',
    // Inkoop-blok — bidirectioneel zoals in de Producten-tabel.
    supplier_list_price: '',
    supplier_discount_pct: '',
    supplier_cost: '',
    // v3.2.58 — afgesproken doel-marge voor prognose (% winst).
    // Geen versie-historiek hier nog — komt later via AKP-blok subtitel.
    target_margin_pct: '',
    // R2-upload URL — wordt na save opgeslagen als primary product_image.
    image_url: null as string | null,
  });
  const [suppliers, setSuppliers] = useState<AdminSupplier[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-genereer slug uit naam (kebab-case, lowercase)
  const slugify = (s: string) =>
    s.toLowerCase()
      .normalize('NFD').replace(/[̀-ͯ]/g, '')  // strip accents
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  // Laad leveranciers eenmalig zodat Filip kan koppelen
  useEffect(() => {
    void (async () => {
      try {
        const list = await adminGetSuppliers();
        setSuppliers(list.filter(s => s.is_active === 1));
      } catch { /* niet kritiek voor maken */ }
    })();
  }, []);

  const update = <K extends keyof typeof form>(key: K, value: typeof form[K]) => {
    setForm(f => {
      const next = { ...f, [key]: value };
      // Slug auto-fillen vanuit naam wanneer slug nog leeg is
      if (key === 'name' && (f.slug === '' || f.slug === slugify(f.name))) {
        next.slug = slugify(value as string);
      }
      return next;
    });
  };

  const validate = (): string | null => {
    if (form.name.trim().length < 2) return 'Naam minstens 2 tekens';
    if (!form.slug.match(/^[a-z0-9-]+$/)) return 'Slug mag alleen kleine letters, cijfers en streepjes';
    if (!form.texture.trim()) return 'Textuur is verplicht';
    if (!form.dimensions.trim()) return 'Afmetingen verplicht';
    if (!form.weight.trim()) return 'Gewicht verplicht';
    if (!form.description_nl.trim()) return 'Beschrijving verplicht';
    if (!form.supplier_code.trim()) return 'Leverancier-artikelnummer verplicht';
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setSaving(true);
    setError(null);
    try {
      await adminCreateProduct({
        name: form.name.trim(),
        slug: form.slug.trim(),
        // Kleur-velden vervallen UI-zijde maar zijn DB-zijde nog NOT NULL.
        // We sturen placeholders zodat oude code (productkaart-swatch) geen
        // crash geeft; de echte visuele identiteit is voortaan de foto.
        color: 'Foto',
        color_hex: '#888888',
        texture: form.texture.trim(),
        category: form.category,
        dimensions: form.dimensions.trim(),
        weight: form.weight.trim(),
        description_nl: form.description_nl.trim(),
        supplier_code: form.supplier_code.trim(),
        supplier_id: form.supplier_id || null,
        locked_price: form.locked_price ? parseFloat(form.locked_price) : null,
        serial_number: form.serial_number.trim() || null,
        tech_specs: form.tech_specs.trim() || null,
        ean_code: form.ean_code.trim() || null,
        supplier_list_price:    form.supplier_list_price    ? parseFloat(form.supplier_list_price)    : null,
        supplier_discount_pct:  form.supplier_discount_pct  ? parseFloat(form.supplier_discount_pct)  : null,
        supplier_cost:          form.supplier_cost          ? parseFloat(form.supplier_cost)          : null,
        image_url:              form.image_url,
      });
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const Field = ({ label, value, onChange, placeholder, required, type = 'text', hint }: {
    label: string; value: string; onChange: (v: string) => void; placeholder?: string;
    required?: boolean; type?: string; hint?: string;
  }) => (
    <div>
      <label className="block text-xs font-bold text-[#8a7e74] uppercase tracking-wide mb-1">
        {label}{required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a]"
      />
      {hint && <p className="text-[10px] text-[#8a7e74] mt-0.5">{hint}</p>}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl my-8" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-[#c47a3a]/10 to-amber-50">
          <div>
            <h3 className="text-lg font-bold text-[#1a1a1a] flex items-center gap-2">
              <Plus size={18} className="text-[#c47a3a]" />
              Nieuw product toevoegen
            </h3>
            <p className="text-xs text-[#8a7e74] mt-0.5">
              Vul de basis gegevens in. Korting + bestseller kun je later toevoegen vanuit de tabel.
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/60 rounded-lg" title="Sluiten">
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto">
          <Field label="Naam" value={form.name} onChange={v => update('name', v)} placeholder="Linea Brons" required />
          <Field label="Slug (URL-vriendelijk)" value={form.slug} onChange={v => update('slug', slugify(v))} placeholder="linea-brons" required hint="lowercase, dashes" />
          {/* v3.2.58 — Foto van het product. Vervangt de oude Kleur + Kleur-hex
               velden. Foto bepaalt visueel hoe de steen eruitziet op de
               productkaart en in de offerte-PDF. Optioneel — kan later via
               de productpagina alsnog toegevoegd worden. */}
          <div className="sm:col-span-2 bg-blue-50/40 border border-blue-200 rounded-lg p-3">
            <label className="block text-xs font-bold text-blue-900 uppercase tracking-wide mb-2 flex items-center gap-1.5">
              📸 Productfoto <span className="text-[10px] text-blue-700 font-normal normal-case">(vervangt kleur — foto bepaalt de visuele identiteit)</span>
            </label>
            <ImageUpload
              value={form.image_url}
              onChange={url => update('image_url', url)}
              folder="products"
              placeholder="Sleep een productfoto of klik om te kiezen"
              previewClass="h-32 w-48 object-cover rounded-lg border border-blue-200"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-[#8a7e74] uppercase tracking-wide mb-1">
              Textuur<span className="text-red-500 ml-1">*</span>
            </label>
            <select
              value={form.texture}
              onChange={e => update('texture', e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a]"
            >
              {['Ruw', 'Glad', 'Gesinterd', 'Gewassen'].map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-[#8a7e74] uppercase tracking-wide mb-1">
              Categorie<span className="text-red-500 ml-1">*</span>
            </label>
            <select
              value={form.category}
              onChange={e => update('category', e.target.value as typeof form.category)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a]"
            >
              {(['modern', 'landelijk', 'industrieel', 'klassiek'] as const).map(c => (
                <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
              ))}
            </select>
          </div>
          <Field label="Afmetingen" value={form.dimensions} onChange={v => update('dimensions', v)} placeholder="240 x 71 x 15 mm" required />
          <Field label="Gewicht" value={form.weight} onChange={v => update('weight', v)} placeholder="4.0 kg/m²" required />
          <Field label="Leverancier-artikelnummer" value={form.supplier_code} onChange={v => update('supplier_code', v)} placeholder="LIN-BRO-001" required />
          <div>
            <label className="block text-xs font-bold text-[#8a7e74] uppercase tracking-wide mb-1">Leverancier</label>
            <select
              value={form.supplier_id}
              onChange={e => update('supplier_id', e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a]"
            >
              <option value="">— Geen / nader te bepalen —</option>
              {suppliers.map(s => <option key={s.id} value={s.id}>{s.name} ({s.country})</option>)}
            </select>
          </div>
          {/* v3.2.89 — Aankoopprijs LINKS (start van prijsketen = wat Filip
               aan leverancier betaalt), Verkoopprijs RECHTS (resultaat =
               wat de eindklant betaalt). Volgt de natuurlijke flow van
               links naar rechts: inkoop → marge → verkoop. */}
          <Field label="Aankoopprijs/m² (netto)" value={form.supplier_cost} onChange={v => update('supplier_cost', v)} placeholder="39.00" type="number" hint="Wat Filip aan de leverancier betaalt (na korting)" />
          <Field label="Verkoopprijs/m² (catalogus)" value={form.locked_price} onChange={v => update('locked_price', v)} placeholder="48.75" type="number" hint="Optioneel — kan later via Prijzen tab" />
          <Field label="Serienummer" value={form.serial_number} onChange={v => update('serial_number', v)} placeholder="optioneel" />
          <Field label="EAN-code" value={form.ean_code} onChange={v => update('ean_code', v)} placeholder="optioneel" />

          {/* ─── Inkoop-blok (AKP Lev. + Korting → Netto AKP) ────────────────
               Optioneel: Filip kan de leveranciersdeal hier al vastleggen,
               of pas later via de pencil-edit in de Producten-tabel.
               Bidirectioneel zoals in de tabel: AKP × (1 − %) = Netto. */}
          <div className="sm:col-span-2 border border-blue-200 bg-blue-50/30 rounded-lg p-3">
            <p className="text-[11px] font-bold text-blue-900 uppercase tracking-wide mb-2 flex items-center gap-1.5">
              <span>{t('admin.products.akpBlockTitle')}</span>
              <span className="text-[9px] text-blue-700 font-normal normal-case">{t('admin.products.akpBlockHint')}</span>
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div>
                <label className="block text-[10px] font-bold text-[#8a7e74] uppercase tracking-wide mb-1" title={t('admin.products.akpLevTooltip')}>{t('admin.products.akpLev')}</label>
                <input
                  type="number" step="0.01"
                  value={form.supplier_list_price}
                  placeholder="55.71"
                  onChange={e => {
                    const newList = e.target.value;
                    setForm(f => {
                      const disc = f.supplier_discount_pct ? parseFloat(f.supplier_discount_pct) : null;
                      const listNum = newList ? parseFloat(newList) : null;
                      const newCost = (listNum != null && disc != null)
                        ? (Math.round(listNum * (1 - disc / 100) * 100) / 100).toString()
                        : f.supplier_cost;
                      return { ...f, supplier_list_price: newList, supplier_cost: newCost };
                    });
                  }}
                  className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs focus:outline-none focus:border-[#c47a3a]"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-[#8a7e74] uppercase tracking-wide mb-1">{t('admin.products.akpDiscount')} %</label>
                <div className="relative">
                  <input
                    type="number" step="0.5" min="0" max="100"
                    value={form.supplier_discount_pct}
                    placeholder="30"
                    onChange={e => {
                      const newDisc = e.target.value;
                      setForm(f => {
                        const list = f.supplier_list_price ? parseFloat(f.supplier_list_price) : null;
                        const discNum = newDisc ? parseFloat(newDisc) : null;
                        const newCost = (list != null && discNum != null)
                          ? (Math.round(list * (1 - discNum / 100) * 100) / 100).toString()
                          : f.supplier_cost;
                        return { ...f, supplier_discount_pct: newDisc, supplier_cost: newCost };
                      });
                    }}
                    className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs focus:outline-none focus:border-[#c47a3a] pr-5"
                  />
                  <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-[#8a7e74] font-semibold">%</span>
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-extrabold text-[#1a1a1a] uppercase tracking-wide mb-1" title={t('admin.products.netAkpTooltip')}>{t('admin.products.netAkp')}</label>
                <input
                  type="number" step="0.01"
                  value={form.supplier_cost}
                  placeholder="39.00"
                  onChange={e => {
                    const newCost = e.target.value;
                    setForm(f => {
                      const list = f.supplier_list_price ? parseFloat(f.supplier_list_price) : null;
                      const costNum = newCost ? parseFloat(newCost) : null;
                      const newDisc = (list != null && list > 0 && costNum != null)
                        ? (Math.round((1 - costNum / list) * 1000) / 10).toString()
                        : f.supplier_discount_pct;
                      return { ...f, supplier_cost: newCost, supplier_discount_pct: newDisc };
                    });
                  }}
                  className="w-full px-2 py-1.5 border-2 border-blue-200 bg-white rounded text-xs font-semibold focus:outline-none focus:border-[#c47a3a]"
                />
              </div>
            </div>
            {/* Bevestigingspil — toont live de toegepaste formule. */}
            {(() => {
              const list = form.supplier_list_price ? parseFloat(form.supplier_list_price) : null;
              const disc = form.supplier_discount_pct ? parseFloat(form.supplier_discount_pct) : null;
              const net  = form.supplier_cost ? parseFloat(form.supplier_cost) : null;
              if (list == null || disc == null) return null;
              const expected = Math.round(list * (1 - disc / 100) * 100) / 100;
              const matches = net != null && Math.abs(net - expected) < 0.015;
              return (
                <div className={`flex items-center gap-1 text-[10px] font-semibold rounded px-1.5 py-1 mt-2 ${
                  matches
                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                    : 'bg-amber-50 text-amber-700 border border-amber-200'
                }`}>
                  <span>{matches ? '✓' : '⚠'}</span>
                  <span className="font-mono">€{list.toFixed(2)} × (1 − {disc}%) = €{expected.toFixed(2)}</span>
                </div>
              );
            })()}
          </div>

          {/* ─── Doel-marge indicator (v3.2.58) ──────────────────────────────
               Filip wil bij het invoeren al een prognose: 'als ik X% marge wil
               aanhouden bij deze inkoopprijs, dan moet mijn verkoopprijs Y zijn'.
               Werkt zonder verkoopprijs te overschrijven — gewoon ter info.  */}
          <div className="sm:col-span-2 bg-purple-50/40 border border-purple-200 rounded-lg p-3">
            <label className="block text-xs font-bold text-purple-900 uppercase tracking-wide mb-2 flex items-center gap-1.5">
              🎯 Doel-marge prognose <span className="text-[10px] text-purple-700 font-normal normal-case">(optioneel — helpt bij prijszetting)</span>
            </label>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs text-[#8a7e74] w-32">Gewenste marge:</span>
              <div className="relative">
                <input
                  type="number" step="0.5" min="0" max="80"
                  value={form.target_margin_pct}
                  placeholder="20"
                  onChange={e => update('target_margin_pct', e.target.value)}
                  className="w-24 px-2 py-1.5 border border-purple-300 rounded text-sm font-semibold focus:outline-none focus:border-purple-500 pr-6"
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-[#8a7e74] font-semibold">%</span>
              </div>
            </div>
            {(() => {
              const cost = form.supplier_cost ? parseFloat(form.supplier_cost) : null;
              const margin = form.target_margin_pct ? parseFloat(form.target_margin_pct) : null;
              if (cost == null || margin == null || margin >= 100) return (
                <p className="text-[10px] text-purple-700 italic">
                  Vul Netto AKP + gewenste marge in om de aanbevolen verkoopprijs te zien.
                </p>
              );
              // sell = cost / (1 - margin/100)
              const recommended = Math.round((cost / (1 - margin / 100)) * 100) / 100;
              const currentSell = form.locked_price ? parseFloat(form.locked_price) : null;
              const inclVat21 = Math.round(recommended * 1.21 * 100) / 100;
              return (
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-[#8a7e74] w-32">Aanbevolen verkoop:</span>
                    <strong className="font-mono text-purple-900">€{recommended.toFixed(2)}/m²</strong>
                    <span className="text-[10px] text-[#8a7e74]">excl. BTW</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-[#8a7e74] w-32">Incl. 21% BTW:</span>
                    <span className="font-mono text-purple-900">€{inclVat21.toFixed(2)}/m²</span>
                  </div>
                  {currentSell != null && (
                    <div className="text-[10px] mt-1.5 pt-1.5 border-t border-purple-200">
                      {Math.abs(currentSell - recommended) < 0.05 ? (
                        <span className="text-emerald-700 font-semibold">✓ Verkoopprijs (€{currentSell.toFixed(2)}) klopt met deze marge</span>
                      ) : currentSell < recommended ? (
                        <span className="text-amber-700">⚠ Huidige verkoopprijs (€{currentSell.toFixed(2)}) ligt €{(recommended - currentSell).toFixed(2)} lager — werkelijke marge wordt {((1 - cost / currentSell) * 100).toFixed(1)}%</span>
                      ) : (
                        <span className="text-blue-700">↑ Huidige verkoopprijs (€{currentSell.toFixed(2)}) ligt €{(currentSell - recommended).toFixed(2)} hoger — werkelijke marge wordt {((1 - cost / currentSell) * 100).toFixed(1)}%</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>

          <div className="sm:col-span-2">
            <label className="block text-xs font-bold text-[#8a7e74] uppercase tracking-wide mb-1">
              Beschrijving<span className="text-red-500 ml-1">*</span>
            </label>
            <textarea
              value={form.description_nl}
              onChange={e => update('description_nl', e.target.value)}
              rows={3}
              placeholder="Korte productomschrijving voor de catalogus en offerte-PDF…"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a]"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-bold text-[#8a7e74] uppercase tracking-wide mb-1">Technische specs</label>
            <textarea
              value={form.tech_specs}
              onChange={e => update('tech_specs', e.target.value)}
              rows={2}
              placeholder="optioneel — vorstbestendigheid, drukweerstand, etc."
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a]"
            />
          </div>
        </div>

        {error && (
          <div className="px-5 py-2 bg-red-50 border-t border-red-100 text-red-700 text-sm flex items-center gap-2">
            <AlertCircle size={14} /> {error}
          </div>
        )}

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 flex flex-col sm:flex-row gap-2 sm:justify-end bg-gray-50 rounded-b-2xl">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-5 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-[#4a4a4a] rounded-lg text-sm font-medium min-h-[44px]"
          >
            Annuleren
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="flex items-center justify-center gap-1.5 px-5 py-2.5 bg-[#c47a3a] hover:bg-[#b06930] disabled:opacity-50 text-white rounded-lg text-sm font-semibold min-h-[44px]"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {saving ? 'Bezig met opslaan…' : 'Product aanmaken'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// LEVERANCIERS TAB — mobile: card layout
// ═══════════════════════════════════════════════════════════════════════════════

const EMPTY_SUPPLIER = {
  name: '', country: 'BE', contact_email: '', btw_number: '', company_name: '',
  address: '', city: '', postal_code: '', contact_person: '', contact_phone: '', website: '',
  // v3.2.58 — kleur per leverancier voor visuele identificatie in tabellen/badges.
  color_hex: '#888888',
};

function LeveranciersTab({ suppliers, products, onRefresh }: { suppliers: AdminSupplier[]; products: AdminProduct[]; onRefresh: () => void }) {
  const { t } = useLanguage();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...EMPTY_SUPPLIER });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  // v3.2.56 — productcreatie verhuisde van Producten naar Leveranciers.
  // Wanneer Filip op '+ Product' bij een leverancier klikt, openen we de
  // bestaande ProductCreateModal met die leverancier al gekozen.
  const [creatingProductForSupplier, setCreatingProductForSupplier] = useState<AdminSupplier | null>(null);
  // v3.2.58 — bestelbon naar leverancier (Filip → leverancier).
  const [orderingFromSupplier, setOrderingFromSupplier] = useState<AdminSupplier | null>(null);
  // v3.2.87 — uitklappen per leverancier om producten met hun inkoopprijs
  // (AKP Lev / Korting Lev / Netto Inkoop) inline te kunnen bewerken. Hoort
  // hier thuis: inkoop is per-leveranciersdeal, niet per-product op
  // catalogniveau (zie commit v3.2.86 + Filip's feedback).
  const [expandedSupplierId, setExpandedSupplierId] = useState<string | null>(null);
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const [productEditData, setProductEditData] = useState<{
    supplier_list_price: number | null;
    supplier_discount_pct: number | null;
    supplier_cost: number | null;
  }>({ supplier_list_price: null, supplier_discount_pct: null, supplier_cost: null });
  const [savingProduct, setSavingProduct] = useState(false);
  const [productError, setProductError] = useState<string | null>(null);

  const toggleSupplier = (id: string) => {
    setExpandedSupplierId(prev => (prev === id ? null : id));
    // Annuleer eventueel openstaande product-edit als we een andere supplier
    // expanden — anders zou de Save in de verkeerde context kunnen werken.
    setEditingProductId(null);
  };

  const startEditProduct = (p: AdminProduct) => {
    setEditingProductId(p.id);
    setProductError(null);
    setProductEditData({
      supplier_list_price: p.supplier_list_price ?? null,
      supplier_discount_pct: p.supplier_discount_pct ?? null,
      supplier_cost: p.supplier_cost,
    });
  };

  const cancelEditProduct = () => {
    setEditingProductId(null);
    setProductError(null);
  };

  const saveProductEdit = async () => {
    if (!editingProductId) return;
    setSavingProduct(true);
    setProductError(null);
    try {
      const { adminUpdateProduct } = await import('../lib/api');
      const { priceRecalculated } = await adminUpdateProduct(editingProductId, productEditData);
      setEditingProductId(null);
      // Melding aan Filip: inkoop gewijzigd → verkoopprijs automatisch mee.
      if (priceRecalculated) {
        window.alert(
          `Verkoopprijs automatisch herberekend: ` +
          `${priceRecalculated.old != null ? '€' + priceRecalculated.old : 'geen'} → €${priceRecalculated.new}/m² ` +
          `(marge ${priceRecalculated.marginPct}%). ` +
          `Andere prijs nodig? Pas de verkoopprijs aan in de Producten-tab.`
        );
      }
      onRefresh();
    } catch (e) {
      setProductError((e as Error).message);
    } finally {
      setSavingProduct(false);
    }
  };

  const openAdd = () => {
    setEditingId(null);
    setForm({ ...EMPTY_SUPPLIER });
    setFormError(null);
    setShowForm(true);
  };

  const openEdit = (s: AdminSupplier) => {
    setEditingId(s.id);
    setForm({
      name: s.name ?? '',
      country: s.country ?? 'BE',
      contact_email: s.contact_email ?? '',
      btw_number: s.btw_number ?? '',
      company_name: s.company_name ?? '',
      address: s.address ?? '',
      city: s.city ?? '',
      postal_code: s.postal_code ?? '',
      contact_person: s.contact_person ?? '',
      contact_phone: s.contact_phone ?? '',
      website: s.website ?? '',
      color_hex: s.color_hex ?? '#888888',
    });
    setFormError(null);
    setShowForm(true);
  };

  const saveForm = async () => {
    if (!form.name.trim()) { setFormError(t('admin.suppliers.nameRequired')); return; }
    setSaving(true);
    setFormError(null);
    try {
      const payload: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(form)) {
        payload[k] = v === '' ? null : v;
      }
      if (editingId) {
        await adminUpdateSupplier(editingId, payload);
      } else {
        await adminCreateSupplier(payload);
      }
      setShowForm(false);
      setEditingId(null);
      onRefresh();
    } catch (e) {
      setFormError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t('admin.suppliers.confirmDelete'))) return;
    try {
      await adminDeleteSupplier(id);
      onRefresh();
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const Field = ({ label, field, type = 'text' }: { label: string; field: keyof typeof form; type?: string }) => (
    <div>
      <label className="block text-xs font-semibold text-[#8a7e74] uppercase tracking-wide mb-1">{label}</label>
      <input
        type={type}
        className="w-full px-3 py-3 border border-gray-200 rounded-lg text-sm sm:text-base focus:border-[#c47a3a] focus:outline-none min-h-[44px]"
        value={form[field]}
        onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
      />
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 sm:p-6">
          <h3 className="text-lg font-bold text-[#1a1a1a] mb-4">{editingId ? t('admin.suppliers.editSupplier') : t('admin.suppliers.newSupplier')}</h3>
          {formError && (
            <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 rounded-lg text-red-600 text-sm">
              <AlertCircle size={14} /> {formError}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Field label={t('admin.suppliers.company')} field="company_name" />
            <Field label={t('admin.suppliers.nameShort')} field="name" />
            {/* Land — dropdown met ISO 2-letter codes. De API verwacht dit
                 strikt (z.string().length(2)) — ooit was "Italie" / "Polen"
                 ingegeven, wat sinds v3.2.34 gefixed is in DB en hier. */}
            <div>
              <label className="block text-xs font-semibold text-[#8a7e74] uppercase tracking-wide mb-1">Land</label>
              <select
                className="w-full px-3 py-3 border border-gray-200 rounded-lg text-sm sm:text-base focus:border-[#c47a3a] focus:outline-none min-h-[44px] bg-white"
                value={form.country}
                onChange={e => setForm(f => ({ ...f, country: e.target.value }))}
              >
                <option value="BE">🇧🇪 België (BE)</option>
                <option value="NL">🇳🇱 Nederland (NL)</option>
                <option value="IT">🇮🇹 Italië (IT)</option>
                <option value="PL">🇵🇱 Polen (PL)</option>
                <option value="ES">🇪🇸 Spanje (ES)</option>
                <option value="DE">🇩🇪 Duitsland (DE)</option>
                <option value="FR">🇫🇷 Frankrijk (FR)</option>
                <option value="PT">🇵🇹 Portugal (PT)</option>
                <option value="UK">🇬🇧 Verenigd Koninkrijk (UK)</option>
              </select>
            </div>
            <Field label={t('admin.suppliers.btw')} field="btw_number" />
            <Field label={t('admin.suppliers.address')} field="address" />
            <Field label={t('admin.suppliers.city')} field="city" />
            <Field label={t('admin.suppliers.postalCode')} field="postal_code" />
            <Field label={t('admin.suppliers.contactPerson')} field="contact_person" />
            <Field label={t('admin.suppliers.email')} field="contact_email" type="email" />
            <Field label={t('admin.suppliers.phone')} field="contact_phone" type="tel" />
            <Field label={t('admin.suppliers.website')} field="website" type="url" />
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-6">
            <button
              onClick={saveForm}
              disabled={saving}
              className="flex items-center justify-center gap-1.5 px-5 py-3 bg-[#c47a3a] hover:bg-[#b06930] text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 min-h-[44px]"
            >
              <Save size={14} />
              {saving ? t('admin.suppliers.saving') : t('admin.suppliers.save')}
            </button>
            <button
              onClick={() => setShowForm(false)}
              className="px-5 py-3 bg-gray-100 hover:bg-gray-200 text-[#4a4a4a] rounded-lg text-sm font-medium transition-colors min-h-[44px]"
            >
              {t('admin.suppliers.cancel')}
            </button>
          </div>
        </div>
      )}

      {/* List */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-100 flex items-center justify-between gap-3">
          <h2 className="text-lg font-bold text-[#1a1a1a] flex items-center gap-2">
            <Truck size={18} className="text-[#c47a3a]" /> {t('admin.suppliers.title')}
            <span className="px-2 py-0.5 bg-[#c47a3a]/10 text-[#c47a3a] text-xs font-semibold rounded-full">{suppliers.length}</span>
          </h2>
          <button
            onClick={openAdd}
            className="flex items-center gap-1.5 px-4 py-2.5 bg-[#c47a3a] hover:bg-[#b06930] text-white rounded-lg text-sm font-medium transition-colors min-h-[44px]"
          >
            <Plus size={14} />
            <span className="hidden sm:inline">{t('admin.suppliers.add')}</span>
            <span className="sm:hidden">{t('admin.suppliers.new')}</span>
          </button>
        </div>
        <div className="px-4 sm:px-6 pb-6 pt-2">
          {/* Hint-banner: producten worden hier aangemaakt vanaf v3.2.56 — Filip
               zag in eerste instantie de groene Plus-knop niet. Maakt duidelijk
               wat de '+ Product'-knop doet en dat de productinfo (naam, AKP,
               korting, …) tijdens dat aanmaken wordt ingegeven. */}
          {suppliers.length > 0 && (
            <div className="mb-4 p-3 bg-emerald-50/60 border border-emerald-200 rounded-lg flex items-start gap-2">
              <Info size={14} className="text-emerald-700 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-emerald-900 leading-relaxed">
                <strong>Producten worden hier aangemaakt</strong> — klik op de groene <strong>+ Product</strong> knop bij een leverancier om een nieuw product met AKP, korting en netto inkoop in te voeren. De leverancier wordt automatisch ingevuld.
              </p>
            </div>
          )}
          {suppliers.length === 0 ? (
            <p className="text-sm text-[#8a7e74] py-4">{t('admin.suppliers.noSuppliers')}</p>
          ) : (
            <>
              {/* Mobile cards */}
              <div className="sm:hidden space-y-3">
                {suppliers.map(s => (
                  <div key={s.id} className="border border-gray-100 rounded-lg p-4">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div>
                        <p className="font-medium text-[#1a1a1a] text-base">{s.company_name || s.name}</p>
                        {s.company_name && <p className="text-sm text-[#8a7e74]">{s.name}</p>}
                      </div>
                      <span className="px-2 py-0.5 bg-[#c47a3a]/10 text-[#c47a3a] rounded text-xs font-bold flex-shrink-0">{s.product_count} {t('admin.suppliers.prod')}</span>
                    </div>
                    <div className="space-y-1 text-sm mb-3">
                      {s.contact_person && <p className="text-[#4a4a4a]">{s.contact_person}</p>}
                      {s.contact_email && <p className="text-[#8a7e74]">{s.contact_email}</p>}
                      {s.contact_phone && <p className="text-[#8a7e74]">{s.contact_phone}</p>}
                      {s.city && <p className="text-[#4a4a4a]">{s.city}</p>}
                      {s.btw_number && <p className="text-[#8a7e74] font-mono text-xs">{s.btw_number}</p>}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setCreatingProductForSupplier(s)}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg text-sm font-medium transition-colors min-h-[44px]"
                      >
                        <Plus size={16} /> Product
                      </button>
                      <button
                        onClick={() => openEdit(s)}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 bg-[#c47a3a]/10 hover:bg-[#c47a3a]/20 text-[#c47a3a] rounded-lg text-sm font-medium transition-colors min-h-[44px]"
                      >
                        <Edit3 size={16} /> {t('admin.suppliers.edit')}
                      </button>
                      <button
                        onClick={() => handleDelete(s.id)}
                        className="flex items-center justify-center px-3 py-2.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-colors min-h-[44px] min-w-[44px]"
                        title={t('admin.suppliers.delete')}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      {[t('admin.suppliers.company'), t('admin.suppliers.name'), t('admin.suppliers.contactPerson'), t('admin.suppliers.email'), t('admin.suppliers.phone'), t('admin.suppliers.city'), t('admin.profile.btwShort'), t('admin.suppliers.products'), t('admin.suppliers.actions')].map(h => (
                        <th key={h} className="text-left py-2 pr-4 text-xs font-semibold text-[#8a7e74] uppercase tracking-wide whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {suppliers.map(s => {
                      const isExpanded = expandedSupplierId === s.id;
                      const supplierProducts = products.filter(p => p.supplier_id === s.id);
                      return (
                      <React.Fragment key={s.id}>
                      <tr className={`border-b border-gray-50 transition-colors ${isExpanded ? 'bg-[#faf7f3]' : 'hover:bg-[#faf7f3]'}`}>
                        <td className="py-2.5 pr-4 font-medium text-[#1a1a1a]">{s.company_name || '—'}</td>
                        <td className="py-2.5 pr-4 text-[#4a4a4a]">{s.name}</td>
                        <td className="py-2.5 pr-4 text-[#4a4a4a]">{s.contact_person || '—'}</td>
                        <td className="py-2.5 pr-4 text-[#8a7e74]">{s.contact_email || '—'}</td>
                        <td className="py-2.5 pr-4 text-[#8a7e74]">{s.contact_phone || '—'}</td>
                        <td className="py-2.5 pr-4 text-[#4a4a4a]">{s.city || '—'}</td>
                        <td className="py-2.5 pr-4 text-[#8a7e74] font-mono text-xs">{s.btw_number || '—'}</td>
                        <td className="py-2.5 pr-4">
                          {/* Klikbare product-teller — opent uitklap-paneel met
                              alle producten van deze leverancier + inline AKP/
                              Korting/Netto-inkoop editor (v3.2.87). */}
                          <button
                            onClick={() => toggleSupplier(s.id)}
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-bold transition-colors ${isExpanded ? 'bg-[#c47a3a] text-white' : 'bg-[#c47a3a]/10 text-[#c47a3a] hover:bg-[#c47a3a]/20'}`}
                            title={isExpanded ? 'Klap producten in' : `Bekijk en bewerk inkoopprijzen van ${s.product_count} producten`}
                          >
                            {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                            {s.product_count}
                          </button>
                        </td>
                        <td className="py-2.5">
                          <div className="flex items-center gap-1.5">
                            {/* '+ Product' — prominent groene knop met tekst zodat
                                 in één oogopslag duidelijk is dat hier producten
                                 voor deze leverancier worden aangemaakt. */}
                            <button
                              onClick={() => setCreatingProductForSupplier(s)}
                              className="inline-flex items-center gap-1 px-2.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition-colors min-h-[44px] shadow-sm whitespace-nowrap"
                              title={`Nieuw product toevoegen voor ${s.name}`}
                            >
                              <Plus size={14} /> Product
                            </button>
                            {/* Bestelbon naar deze leverancier — opent een snel-form
                                 modal met Filip's gegevens als koper. (v3.2.58) */}
                            <button
                              onClick={() => setOrderingFromSupplier(s)}
                              className="inline-flex items-center gap-1 px-2.5 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded-lg text-xs font-medium transition-colors min-h-[44px] whitespace-nowrap"
                              title={`Bestelbon / factuur opmaken voor ${s.name}`}
                            >
                              <FileText size={14} /> Bestelbon
                            </button>
                            <button
                              onClick={() => openEdit(s)}
                              className="p-2.5 bg-[#c47a3a]/10 hover:bg-[#c47a3a]/20 text-[#c47a3a] rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                              title={t('admin.suppliers.edit')}
                            >
                              <Edit3 size={16} />
                            </button>
                            <button
                              onClick={() => handleDelete(s.id)}
                              className="p-2.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                              title={t('admin.suppliers.delete')}
                            >
                              <X size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {/* v3.2.87 — Uitklap-paneel met producten + inline inkoop-editor */}
                      {isExpanded && (
                        <tr className="bg-blue-50/30 border-b border-blue-100">
                          <td colSpan={9} className="p-4">
                            <div className="bg-white rounded-lg border border-blue-200 overflow-hidden">
                              <div className="px-4 py-2 bg-blue-50 border-b border-blue-100 flex items-center justify-between gap-3">
                                <h4 className="text-xs font-bold text-blue-900 uppercase tracking-wide flex items-center gap-2">
                                  <Truck size={14} /> Inkoopprijzen — {s.company_name || s.name}
                                </h4>
                                <span className="text-[10px] text-blue-700">
                                  AKP Lev × (1 − Korting %) = Netto Inkoop
                                </span>
                              </div>
                              {supplierProducts.length === 0 ? (
                                <p className="px-4 py-6 text-sm text-[#8a7e74] text-center">
                                  Nog geen producten — klik op <strong className="text-emerald-700">+ Product</strong> hierboven om toe te voegen.
                                </p>
                              ) : (
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="border-b border-gray-100 bg-gray-50">
                                      <th className="text-left py-2 px-3 text-[10px] font-semibold text-[#8a7e74] uppercase tracking-wide">Product</th>
                                      <th className="text-left py-2 px-3 text-[10px] font-semibold text-[#8a7e74] uppercase tracking-wide">AKP Lev. (bruto)</th>
                                      <th className="text-left py-2 px-3 text-[10px] font-semibold text-[#8a7e74] uppercase tracking-wide">Korting Lev. %</th>
                                      <th className="text-left py-2 px-3 text-[10px] font-semibold text-[#1a1a1a] uppercase tracking-wide">Netto Inkoop</th>
                                      <th className="text-left py-2 px-3 text-[10px] font-semibold text-[#8a7e74] uppercase tracking-wide">Acties</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {supplierProducts.map(p => {
                                      const isEditingP = editingProductId === p.id;
                                      const listVal = isEditingP ? productEditData.supplier_list_price : (p.supplier_list_price ?? null);
                                      const discVal = isEditingP ? productEditData.supplier_discount_pct : (p.supplier_discount_pct ?? null);
                                      const costVal = isEditingP ? productEditData.supplier_cost : p.supplier_cost;
                                      return (
                                        <tr key={p.id} className={`border-b border-gray-50 ${isEditingP ? 'bg-amber-50/30' : 'hover:bg-[#faf7f3]'}`}>
                                          <td className="py-2 px-3 font-medium text-[#1a1a1a] text-xs">{p.name}</td>
                                          {/* AKP Lev. (bruto leveranciersprijs) */}
                                          <td className="py-2 px-3">
                                            {isEditingP ? (
                                              <input
                                                type="number" step="1" min="0"
                                                className="w-24 px-2 py-1.5 border border-gray-200 rounded text-xs focus:border-[#c47a3a] focus:outline-none"
                                                value={listVal ?? ''}
                                                placeholder="—"
                                                onChange={e => {
                                                  const newList = e.target.value ? parseFloat(e.target.value) : null;
                                                  setProductEditData(d => {
                                                    const newCost = (newList != null && d.supplier_discount_pct != null)
                                                      ? Math.round(newList * (1 - d.supplier_discount_pct / 100) * 100) / 100
                                                      : d.supplier_cost;
                                                    return { ...d, supplier_list_price: newList, supplier_cost: newCost };
                                                  });
                                                }}
                                              />
                                            ) : (
                                              <span className="text-xs text-[#4a4a4a]">{listVal != null ? `${formatEuro(listVal)}/m²` : '—'}</span>
                                            )}
                                          </td>
                                          {/* Korting Lev. % */}
                                          <td className="py-2 px-3">
                                            {isEditingP ? (
                                              <div className="relative w-20">
                                                <input
                                                  type="number" step="0.5" min="0" max="100"
                                                  className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs focus:border-[#c47a3a] focus:outline-none pr-5"
                                                  value={discVal ?? ''}
                                                  placeholder="—"
                                                  onChange={e => {
                                                    const newDisc = e.target.value ? parseFloat(e.target.value) : null;
                                                    setProductEditData(d => {
                                                      const newCost = (d.supplier_list_price != null && newDisc != null)
                                                        ? Math.round(d.supplier_list_price * (1 - newDisc / 100) * 100) / 100
                                                        : d.supplier_cost;
                                                      return { ...d, supplier_discount_pct: newDisc, supplier_cost: newCost };
                                                    });
                                                  }}
                                                />
                                                <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-[#8a7e74] font-semibold">%</span>
                                              </div>
                                            ) : (
                                              <span className="text-xs text-[#4a4a4a]">{discVal != null ? `${discVal}%` : '—'}</span>
                                            )}
                                          </td>
                                          {/* Netto Inkoop — what Filip really pays */}
                                          <td className="py-2 px-3">
                                            {isEditingP ? (
                                              <input
                                                type="number" step="0.01" min="0"
                                                className="w-28 px-2 py-1.5 border-2 border-blue-200 bg-blue-50/30 rounded text-xs font-bold text-[#1a1a1a] focus:border-[#c47a3a] focus:outline-none"
                                                value={costVal ?? ''}
                                                placeholder="—"
                                                onChange={e => {
                                                  const newCost = e.target.value ? parseFloat(e.target.value) : null;
                                                  setProductEditData(d => {
                                                    // Back-calculate korting als list bekend is, zodat de
                                                    // 3 velden intern consistent blijven.
                                                    const newDisc = (d.supplier_list_price != null && d.supplier_list_price > 0 && newCost != null)
                                                      ? Math.round((1 - newCost / d.supplier_list_price) * 1000) / 10
                                                      : d.supplier_discount_pct;
                                                    return { ...d, supplier_cost: newCost, supplier_discount_pct: newDisc };
                                                  });
                                                }}
                                              />
                                            ) : (
                                              <span className="text-xs font-bold text-[#1a1a1a]">{costVal != null ? `${formatEuro(costVal)}/m²` : '—'}</span>
                                            )}
                                          </td>
                                          {/* Acties: Save/Cancel of Edit */}
                                          <td className="py-2 px-3">
                                            {isEditingP ? (
                                              <div className="flex items-center gap-1">
                                                <button
                                                  onClick={saveProductEdit}
                                                  disabled={savingProduct}
                                                  className="inline-flex items-center justify-center gap-1 px-2 py-1.5 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded text-[11px] font-bold shadow-sm"
                                                  title="Opslaan"
                                                >
                                                  <Save size={12} /> {savingProduct ? '...' : 'Opslaan'}
                                                </button>
                                                <button
                                                  onClick={cancelEditProduct}
                                                  className="inline-flex items-center justify-center px-2 py-1.5 bg-white border border-gray-300 hover:bg-gray-50 text-[#4a4a4a] rounded text-[11px] font-medium"
                                                  title="Annuleer"
                                                >
                                                  <X size={12} />
                                                </button>
                                              </div>
                                            ) : (
                                              <button
                                                onClick={() => startEditProduct(p)}
                                                disabled={editingProductId !== null}
                                                className="inline-flex items-center gap-1 px-2 py-1 bg-[#c47a3a]/10 hover:bg-[#c47a3a]/20 disabled:opacity-40 text-[#c47a3a] rounded text-[11px] font-medium"
                                                title="Inkoopprijs bewerken"
                                              >
                                                <Edit3 size={12} /> Bewerk inkoop
                                              </button>
                                            )}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              )}
                              {productError && (
                                <div className="px-4 py-2 bg-red-50 border-t border-red-200 text-red-700 text-xs flex items-center gap-2">
                                  <AlertCircle size={12} /> {productError}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ─── Nieuw product modal vanuit Leveranciers (v3.2.56) ───────────
           Filip's vision: producten worden aangemaakt vanuit de
           leverancier-rij. Supplier_id wordt automatisch ingevuld zodat
           je niet opnieuw hoeft te kiezen. */}
      {creatingProductForSupplier && (
        <ProductCreateModal
          presetSupplierId={creatingProductForSupplier.id}
          presetSupplierName={creatingProductForSupplier.name}
          onClose={() => setCreatingProductForSupplier(null)}
          onSaved={() => { setCreatingProductForSupplier(null); onRefresh(); }}
        />
      )}
      {orderingFromSupplier && (
        <SupplierOrderModal
          supplier={orderingFromSupplier}
          onClose={() => setOrderingFromSupplier(null)}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SUPPLIER ORDER MODAL (v3.2.58) — Filip vraagt een bestelbon aan een leverancier
// ═══════════════════════════════════════════════════════════════════════════════

function SupplierOrderModal({ supplier, onClose }: {
  supplier: AdminSupplier;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const [lines, setLines] = useState<{ description: string; qty_m2: string; unit_price: string }[]>([
    { description: '', qty_m2: '', unit_price: '' },
  ]);
  const [notes, setNotes] = useState('');
  const orderRef = `BB-${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;

  const addLine = () => setLines(l => [...l, { description: '', qty_m2: '', unit_price: '' }]);
  const removeLine = (idx: number) => setLines(l => l.filter((_, i) => i !== idx));
  const updateLine = (idx: number, key: 'description' | 'qty_m2' | 'unit_price', v: string) =>
    setLines(l => l.map((x, i) => i === idx ? { ...x, [key]: v } : x));

  const generate = async () => {
    const validLines = lines
      .filter(l => l.description.trim() && parseFloat(l.qty_m2) > 0 && parseFloat(l.unit_price) > 0)
      .map(l => ({
        description: l.description.trim(),
        qty_m2: parseFloat(l.qty_m2),
        unit_price: parseFloat(l.unit_price),
      }));
    if (validLines.length === 0) {
      alert('Voeg minstens één bestellijn toe met omschrijving, hoeveelheid en prijs.');
      return;
    }
    const { printSupplierOrderPDF } = await import('../lib/supplierOrderPdf');
    printSupplierOrderPDF({
      buyer: {
        name: user?.company_name || 'Het Gevelsteenbedrijf',
        address: user?.address || '',
        postal_code: user?.postal_code || '',
        city: user?.city || '',
        country: 'België',
        btw: user?.btw_number || '',
        email: user?.email || '',
        phone: user?.phone || '',
      },
      seller: {
        name: supplier.company_name || supplier.name,
        address: supplier.address || '',
        postal_code: supplier.postal_code || '',
        city: supplier.city || '',
        country: supplier.country || '',
        btw: supplier.btw_number || '',
        email: supplier.contact_email || '',
        phone: supplier.contact_phone || '',
      },
      order_ref: orderRef,
      notes: notes.trim() || undefined,
      lines: validLines,
    });
  };

  const total = lines.reduce((s, l) => {
    const q = parseFloat(l.qty_m2) || 0;
    const p = parseFloat(l.unit_price) || 0;
    return s + q * p;
  }, 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl my-8" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-blue-50 to-blue-100/50">
          <div>
            <h3 className="text-lg font-bold text-[#1a1a1a] flex items-center gap-2">
              <FileText size={18} className="text-blue-700" /> Bestelbon naar leverancier
            </h3>
            <p className="text-xs text-[#8a7e74] mt-0.5">
              Voor <span className="font-semibold text-blue-900">{supplier.company_name || supplier.name}</span> · ref <span className="font-mono">{orderRef}</span>
            </p>
          </div>
          <button onClick={onClose} className="p-1 text-[#8a7e74] hover:text-[#1a1a1a]"><X size={18} /></button>
        </div>

        <div className="px-6 py-5 max-h-[70vh] overflow-y-auto">
          <p className="text-xs text-[#8a7e74] mb-3">Vul de bestellijnen in. Filip's bedrijfsgegevens (uit Mijn Profiel) worden automatisch ingevuld als koper.</p>

          <div className="space-y-2">
            {lines.map((l, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-start">
                <input
                  type="text"
                  placeholder="Omschrijving (bv. Linea Brons 240×71×15)"
                  value={l.description}
                  onChange={e => updateLine(idx, 'description', e.target.value)}
                  className="col-span-6 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a]"
                />
                <input
                  type="number" step="0.5" min="0"
                  placeholder="m²"
                  value={l.qty_m2}
                  onChange={e => updateLine(idx, 'qty_m2', e.target.value)}
                  className="col-span-2 px-3 py-2 border border-gray-200 rounded-lg text-sm text-right focus:outline-none focus:border-[#c47a3a]"
                />
                <input
                  type="number" step="0.01" min="0"
                  placeholder="€/m²"
                  value={l.unit_price}
                  onChange={e => updateLine(idx, 'unit_price', e.target.value)}
                  className="col-span-3 px-3 py-2 border border-gray-200 rounded-lg text-sm text-right focus:outline-none focus:border-[#c47a3a]"
                />
                {lines.length > 1 && (
                  <button onClick={() => removeLine(idx)} className="col-span-1 p-2 text-red-600 hover:bg-red-50 rounded">
                    <X size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>

          <button
            onClick={addLine}
            className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 text-xs text-[#c47a3a] hover:bg-[#c47a3a]/10 rounded transition-colors"
          >
            <Plus size={12} /> Bestellijn toevoegen
          </button>

          <div className="mt-4">
            <label className="block text-xs font-bold text-[#8a7e74] uppercase tracking-wide mb-1">Notities (optioneel)</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              placeholder="Levertermijn, leveradres, betalingsvoorwaarden, …"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a]"
            />
          </div>

          {total > 0 && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
              <span className="text-xs font-semibold text-blue-900 uppercase tracking-wide">Totaal excl. BTW (preview):</span>
              <strong className="text-lg text-blue-900">€{total.toFixed(2)}</strong>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2 bg-gray-50 rounded-b-xl">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-[#4a4a4a] bg-white border border-gray-200 rounded-lg hover:bg-gray-50">
            Annuleren
          </button>
          <button
            onClick={generate}
            className="px-4 py-2 text-sm font-bold bg-[#c47a3a] hover:bg-[#a8643a] text-white rounded-lg flex items-center gap-1.5"
          >
            <FileText size={14} /> Genereer PDF
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// KLANTEN & OFFERTES TAB — merged view with inline quotes per customer
// ═══════════════════════════════════════════════════════════════════════════════

function KlantenOffertesTab({ customers, quotes, onRefresh }: { customers: AdminCustomer[]; quotes: AdminQuote[]; onRefresh: () => void }) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [expandedCustomerId, setExpandedCustomerId] = useState<string | null>(null);
  const [customerDetails, setCustomerDetails] = useState<Record<string, AdminCustomerDetail>>({});
  // v5.17: volledig klantdossier in een rechterpaneel (offertes + bestellingen
  // + facturen + acties) — Filip ziet in één oogopslag wat een klant heeft
  // gedaan of nog moet doen, en kan offertes openen en aanpassen.
  const [dossierCustomer, setDossierCustomer] = useState<AdminCustomer | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [updatingQuoteId, setUpdatingQuoteId] = useState<string | null>(null);
  // v3.2.118 — visuele busy-state op de PDF-knop zelf zodat Filip ZIET
  // dat z'n klik aankomt. Sleutel = quote.id + mode (download/preview).
  const [pdfBusy, setPdfBusy] = useState<string | null>(null);

  // Build PDF for any quote in this tab — uses the shared printQuotePDF util.
  // For reseller-routed quotes, switches branding to the reseller and adds the
  // "Powered by Het Gevelsteenbedrijf" header. Filip-direct quotes show the
  // owner brand only.
  const downloadPdf = async (q: AdminQuote, mode: 'download' | 'preview' = 'download') => {
    // v3.2.116/118 — Bulletproof error handling + visuele busy-state.
    setPdfBusy(q.id + ':' + mode);
    console.log('[PDF] downloadPdf clicked for quote', q.id, 'status', q.status, 'mode', mode);
    // v3.2.124 — Open print-venster SYNCHROON in de click-handler context.
    // Async imports hieronder zouden de pop-up blocker triggeren als we
    // window.open() later doen. Daarom: eerst venster claimen, daarna data.
    const targetWindow = window.open('', '_blank');
    if (!targetWindow) {
      alert('Pop-up blocker actief. Sta pop-ups toe voor deze site (klik op het slot-icoontje links van de URL → Pop-ups → Toestaan) en probeer opnieuw.');
      setPdfBusy(null);
      return;
    }
    // Toon meteen een "Bezig..." melding zodat de gebruiker ziet dat er iets gebeurt.
    targetWindow.document.write('<html><body style="font-family:system-ui;padding:40px;color:#8a4423">Offerte aan het laden…</body></html>');
    try {
      console.log('[PDF] importing quotePdf module + fresh quote fetch...');
      // v3.2.125 — Fresh fetch direct uit API. Bypass elk frontend-state-issue
      // (cached list, stale `q`, Test-als-context filter). Garanteert dat we
      // werken met de DB-data zoals die werkelijk is opgeslagen.
      const [mod, fresh] = await Promise.all([
        import('../lib/quotePdf'),
        adminGetQuoteDetail(q.id).catch(() => null),
      ]);
      const qFresh = fresh ?? q;
      console.log('[PDF] fresh quote loaded:', { id: qFresh.id, customer_name: qFresh.customer_name, is_b2b: qFresh.is_b2b, btw: qFresh.customer_btw_number, notes_len: qFresh.notes_nl?.length });
      const { printQuotePDF, OWNER_COMPANY } = mod;
      if (!printQuotePDF) throw new Error('printQuotePDF not exported from quotePdf module');

      const ownerCompany = {
        ...OWNER_COMPANY,
        name:       (user?.company_name || OWNER_COMPANY.name).trim(),
        address:    (user?.address || OWNER_COMPANY.address).trim(),
        postalCode: (user?.postal_code || OWNER_COMPANY.postalCode).trim(),
        city:       (user?.city || OWNER_COMPANY.city).trim(),
        btw:        (user?.btw_number || OWNER_COMPANY.btw).trim(),
        email:      (user?.email || OWNER_COMPANY.email).trim(),
        phone:      (user?.phone || OWNER_COMPANY.phone).trim(),
      };
      const company = q.reseller_id && q.reseller_company_name
        ? {
            name:       q.reseller_company_name.trim(),
            address:    (q.reseller_address || '').trim(),
            postalCode: (q.reseller_postal_code || '').trim(),
            city:       (q.reseller_city || '').trim(),
            country:    'België',
            btw:        (q.reseller_btw_number || '').trim(),
            email:      (q.reseller_contact_email || '').trim(),
            phone:      (q.reseller_contact_phone || '').trim(),
            logo_url:   (q.reseller_logo_url || '').trim(),
          }
        : ownerCompany;
      console.log('[PDF] calling printQuotePDF with company:', company.name, 'fresh quote customer_name:', qFresh.customer_name);
      await printQuotePDF({
        quote: {
          id: qFresh.id,
          customer_name: qFresh.customer_name,
          customer_email: qFresh.customer_email,
          customer_phone: qFresh.customer_phone,
          customer_btw_number: qFresh.customer_btw_number,
          status: qFresh.status,
          total_excl_vat: qFresh.total_excl_vat,
          total_incl_vat: qFresh.total_incl_vat,
          is_b2b: qFresh.is_b2b,
          notes_nl: qFresh.notes_nl,
          created_at: qFresh.created_at,
          expires_at: qFresh.expires_at,
          customer_address:         qFresh.address ?? null,
          installation_address:     qFresh.installation_address ?? null,
          installation_postal_code: qFresh.installation_postal_code ?? null,
          installation_city:        qFresh.installation_city ?? null,
          wants_installer:          (qFresh as unknown as { wants_installer?: number | null }).wants_installer ?? null,
          installer_choice:         (qFresh as unknown as { installer_choice?: string | null }).installer_choice ?? null,
          customer_company_name:    (qFresh as unknown as { customer_company_name?: string | null }).customer_company_name ?? null,
        },
        company,
        isResellerRouted: !!q.reseller_id,
        t,
        mode,
        targetWindow,
      });
      console.log('[PDF] printQuotePDF returned successfully (mode:', mode, ')');
    } catch (e) {
      // Sluit het lege venster als er iets misging — anders blijft er een
      // leeg "Offerte aan het laden…" tabblad open.
      try { targetWindow.close(); } catch { /* ignore */ }
      const msg = (e as Error).message || String(e);
      const stack = (e as Error).stack || '(no stack)';
      console.error('[PDF] FAILED:', msg, '\n', stack);
      alert('PDF maken mislukte:\n\n' + msg + '\n\nKopieer deze tekst naar Franky. Open ook DevTools (Cmd+Option+I) en kopieer de rode error in de Console.');
    } finally {
      setPdfBusy(null);
    }
  };

  const toggleCustomer = async (id: string) => {
    if (expandedCustomerId === id) {
      setExpandedCustomerId(null);
      return;
    }
    setExpandedCustomerId(id);
    if (!customerDetails[id]) {
      setLoadingId(id);
      try {
        const data = await adminGetCustomer(id);
        setCustomerDetails(prev => ({ ...prev, [id]: data }));
      } catch {
        // silently fail
      } finally {
        setLoadingId(null);
      }
    }
  };

  const updateQuoteStatus = async (quoteId: string, status: string) => {
    setUpdatingQuoteId(quoteId);
    try {
      await adminUpdateQuoteStatus(quoteId, status);
      onRefresh();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setUpdatingQuoteId(null);
    }
  };

  const statuses = ['draft', 'sent', 'accepted', 'rejected', 'expired'];

  return (
    <div className="space-y-4">
      {/* v3.2.96 — 2-koloms layout op desktop: links Klanten & Offertes,
           rechts Berichten (contact-form). Op tablet/mobile gestapeld
           omdat de tabellen anders te smal worden. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-[#1a1a1a] flex items-center gap-2">
            <Users size={18} className="text-[#c47a3a]" /> {t('admin.customers.title')}
            <span className="px-2 py-0.5 bg-[#c47a3a]/10 text-[#c47a3a] text-xs font-semibold rounded-full">{customers.length} {t('admin.customers.clients')}</span>
            <span className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-semibold rounded-full">{quotes.length} {t('admin.customers.quotes')}</span>
          </h2>
        </div>
        <div className="px-4 sm:px-6 pb-6 pt-2">
          {customers.length === 0 ? (
            <p className="text-sm text-[#8a7e74] py-4">{t('admin.customers.noCustomers')}</p>
          ) : (
            <div className="space-y-3">
              {customers.map(c => {
                const isExpanded = expandedCustomerId === c.id;
                const detail = customerDetails[c.id];
                const isLoading = loadingId === c.id;
                const isB2B = !!(c.btw_number || c.company_name);

                const isOpen = dossierCustomer && (dossierCustomer as AdminCustomer).id === c.id;
                return (
                  <div key={c.id} className={`border rounded-xl overflow-hidden transition-colors ${isOpen ? 'border-[#c47a3a] ring-2 ring-[#c47a3a]/30 bg-[#c47a3a]/5' : 'border-gray-100'}`}>
                    {/* Customer header — V5.34: de geopende klant licht op */}
                    <button
                      onClick={() => setDossierCustomer(c)}
                      className={`w-full flex items-center justify-between gap-3 p-4 text-left transition-colors ${isOpen ? 'bg-[#c47a3a]/10' : 'hover:bg-[#faf7f3]'}`}
                      title="Open het volledige klantdossier: gegevens, offertes maken/bewerken, facturen en creditnota's"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-[#1a1a1a] text-base">{c.name}</p>
                          {/* v3.2.119 — B2B/B2C badge verwijderd op Filip's verzoek. */}
                          {c.company_name && <span className="text-xs text-[#8a7e74]">{c.company_name}</span>}
                        </div>
                        <p className="text-sm text-[#8a7e74] mt-0.5">{c.email}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-[#8a7e74]">
                          <span>{c.quotes_count} {t('admin.customers.quotes')}</span>
                          <span>{formatEuro(c.total_spent)} {t('admin.customers.total')}</span>
                          <span>{t('admin.customers.since')} {formatDate(c.created_at)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {/* v5.17: volledig klantdossier in rechterpaneel */}
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={e => { e.stopPropagation(); setDossierCustomer(c); }}
                          onKeyDown={e => { if (e.key === 'Enter') { e.stopPropagation(); setDossierCustomer(c); } }}
                          className="px-2.5 py-1 bg-[#1a1a1a] hover:bg-[#333] text-white rounded-lg text-xs font-bold cursor-pointer"
                          title="Open het volledige dossier: offertes, bestellingen, facturen en acties"
                        >
                          Dossier
                        </span>
                        <span className="px-2 py-0.5 bg-[#c47a3a]/10 text-[#c47a3a] rounded text-xs font-bold">{c.quotes_count} {t('admin.customers.off')}</span>
                        {isExpanded ? <ChevronUp size={20} className="text-[#c47a3a]" /> : <ChevronDown size={20} className="text-[#8a7e74]" />}
                      </div>
                    </button>

                    {/* Expanded: quotes inline */}
                    {isExpanded && (
                      <div className="border-t border-gray-100 bg-[#faf7f3] px-4 sm:px-6 py-4">
                        {isLoading ? (
                          <div className="flex items-center gap-2 text-sm text-[#8a7e74]">
                            <RefreshCw size={14} className="animate-spin" /> {t('admin.customers.loadingQuotes')}
                          </div>
                        ) : detail && detail.quotes && detail.quotes.length > 0 ? (
                          <div className="space-y-2">
                            <h4 className="text-xs font-bold text-[#4a4a4a] uppercase tracking-wide mb-2">{t('admin.customers.quotesTitle')} ({detail.quotes.length})</h4>
                            {detail.quotes.map(q => (
                              <div key={q.id} className="bg-white rounded-lg border border-gray-100 p-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                  {/* v3.2.117 — Klik op Concept-badge → opent
                                       PDF-preview in nieuw tabblad zodat Filip
                                       eerst kan lezen wat erin staat. */}
                                  <StatusBadge status={q.status} onClick={() => downloadPdf(q, 'preview')} />
                                  <span className="text-sm text-[#4a4a4a]">{formatDate(q.created_at)}</span>
                                  <span className="text-sm font-medium text-[#1a1a1a]">{formatEuro(q.total_incl_vat)}</span>
                                  <span className="text-xs text-[#8a7e74]">({formatEuro(q.total_excl_vat)} {t('admin.customers.excl')})</span>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <select
                                    className="px-2 py-1.5 border border-gray-200 rounded-lg text-xs focus:border-[#c47a3a] focus:outline-none disabled:opacity-50"
                                    value={q.status}
                                    disabled={updatingQuoteId === q.id}
                                    onChange={e => updateQuoteStatus(q.id, e.target.value)}
                                  >
                                    {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                                  </select>
                                  <button
                                    onClick={() => downloadPdf(q, 'download')}
                                    disabled={pdfBusy === q.id + ':download'}
                                    className="flex items-center gap-1 px-2.5 py-1.5 bg-[#c47a3a] hover:bg-[#b06930] disabled:opacity-60 disabled:cursor-wait text-white rounded-lg text-xs font-semibold transition-colors"
                                    title={t('admin.customers.downloadPdf') || 'Download PDF'}
                                  >
                                    {pdfBusy === q.id + ':download' ? (
                                      <><Loader2 size={14} className="animate-spin" /> Bezig...</>
                                    ) : (
                                      <><Download size={14} /> PDF</>
                                    )}
                                  </button>
                                  {/* v5 blok 8: offerte → bestelling */}
                                  <button
                                    onClick={async () => {
                                      if (!confirm(`Offerte omzetten naar bestelling?\n\n${c.name} — ${formatEuro(q.total_incl_vat)}\n\nParticulier betaalt vóór levering; handelaar krijgt betalingstermijn.`)) return;
                                      try {
                                        const { adminConvertQuoteToOrder } = await import('../lib/api');
                                        const order = await adminConvertQuoteToOrder(q.id);
                                        alert(`Bestelling #${order.order_number} aangemaakt (${order.payment_terms === 'vooraf' ? 'betaling vóór levering' : 'betaling ' + order.payment_terms.replace('_', ' ')}). Zie het blok "Bestellingen & Facturen" onderaan deze tab.`);
                                        onRefresh();
                                      } catch (e) {
                                        alert((e as Error).message);
                                      }
                                    }}
                                    className="flex items-center gap-1 px-2.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-semibold transition-colors"
                                    title="Zet deze offerte om naar een bestelling (betalingsvoorwaarden + leveradres)"
                                  >
                                    → Bestelling
                                  </button>
                                  {/* v3.2.81 — Delete-knop per offerte. Alleen
                                       drafts kunnen weg (verstuurde offertes
                                       blijven gearchiveerd voor audit).
                                       v3.2.85 — Bug fixed: detail.quotes is een
                                       lokale cache (customerDetails[c.id]) die
                                       door onRefresh() niet wordt vernieuwd.
                                       We patchen hier optimistisch de cache én
                                       de tellers van het outer customer-blok
                                       zodat de UI direct klopt. */}
                                  {q.status === 'draft' && (
                                    <button
                                      onClick={async () => {
                                        if (!confirm(`Verwijder deze concept-offerte?\n\n${c.name} — ${formatEuro(q.total_incl_vat)}\n\nDit kan niet ongedaan gemaakt worden.`)) return;
                                        try {
                                          await adminDeleteQuote(q.id);
                                          setCustomerDetails(prev => {
                                            const cur = prev[c.id];
                                            if (!cur) return prev;
                                            return {
                                              ...prev,
                                              [c.id]: {
                                                ...cur,
                                                quotes: cur.quotes.filter(qq => qq.id !== q.id),
                                                quotes_count: Math.max(0, cur.quotes_count - 1),
                                                total_spent: Math.max(0, cur.total_spent - q.total_incl_vat),
                                              },
                                            };
                                          });
                                          onRefresh();
                                        } catch (e) {
                                          alert((e as Error).message);
                                        }
                                      }}
                                      className="flex items-center gap-1 px-2 py-1.5 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded-lg text-xs font-medium transition-colors"
                                      title="Concept-offerte verwijderen"
                                    >
                                      <Trash2 size={12} />
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-[#8a7e74]">{t('admin.customers.noQuotesForCustomer')}</p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {/* v5.23 (wens Franky): rechts het klantdossier zodra een klant is
           aangeklikt; anders Bestellingen & Facturen. Contact-berichten
           verhuisden naar onderaan — overzichtelijker. */}
      {dossierCustomer ? (
        <CustomerDossierDrawer
          key={dossierCustomer.id}
          customer={dossierCustomer}
          inline
          onClose={() => setDossierCustomer(null)}
          onChanged={onRefresh}
        />
      ) : (
        <OrdersInvoicesPanel />
      )}
      </div>

      {/* V6 punten 18-20: opvolging & forecast */}
      <div className="mt-4">
        <OpvolgingPanel />
      </div>

      {/* Contact-berichten — volle breedte onderaan */}
      <div className="mt-4">
        <BerichtenTab />
      </div>

      {/* V5.32: restpartij-berichten (interesse + voorstellen) — onwisbaar */}
      <div className="mt-4">
        <RestpartijBerichtenPanel />
      </div>

      {/* v3.2.111 — Audit-log info-bar verwijderd op Filip's verzoek.
           De technische waarheid (concept-offertes ZIJN verwijderbaar via
           de prullenbak-knop, alleen verzonden offertes worden gearchiveerd)
           stond ook niet correct in de tekst. Beter weg dan misleidend. */}

    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// v5.17 — KLANTDOSSIER (rechterpaneel): alles van één klant op één plek.
// Offertes (openen, status, BEWERKEN, PDF, → bestelling), bestellingen,
// facturen (PDF) en een reageer-knop. Gevoed door dezelfde admin-API's.
// ═══════════════════════════════════════════════════════════════════════════════

function CustomerDossierDrawer({ customer, onClose, onChanged, inline = false }: {
  customer: AdminCustomer;
  onClose: () => void;
  onChanged: () => void;
  /** v5.23: true = als kolom naast de klantenlijst i.p.v. overlay rechts. */
  inline?: boolean;
}) {
  const [detail, setDetail] = useState<AdminCustomerDetail | null>(null);
  const [orders, setOrders] = useState<import('../lib/api').AdminOrder[]>([]);
  const [invoices, setInvoices] = useState<import('../lib/api').AdminInvoice[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [editingQuote, setEditingQuote] = useState<string | null>(null);
  // v5.18: nieuwe offerte samenstellen met meerdere stenen aan Filips prijzen
  const [building, setBuilding] = useState(false);
  const [buildLines, setBuildLines] = useState<{ product_id: string; qty_m2: number; unit_price: number }[]>([]);
  const [buildProducts, setBuildProducts] = useState<import('../lib/api').ApiProduct[]>([]);
  const [editForm, setEditForm] = useState<{ customer_name: string; customer_phone: string; total_excl_vat: string; notes_nl: string }>({ customer_name: '', customer_phone: '', total_excl_vat: '', notes_nl: '' });
  const statuses = ['draft', 'sent', 'accepted', 'rejected', 'expired'];
  const email = customer.email.toLowerCase();

  const load = useCallback(async () => {
    try {
      const api = await import('../lib/api');
      const [d, o, i] = await Promise.all([
        adminGetCustomer(customer.id),
        api.adminGetOrders(),
        api.adminGetInvoices(),
      ]);
      setDetail(d);
      setOrders(o.filter(x => x.customer_email.toLowerCase() === email));
      setInvoices(i.filter(x => x.customer_email.toLowerCase() === email));
    } catch (e) { alert((e as Error).message); }
  }, [customer.id, email]);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (!building || buildProducts.length > 0) return;
    // v5.26: builder start LEEG — Filip kiest bewust zijn eerste steen.
    void import('../lib/api').then(api => api.fetchProducts()).then(setBuildProducts);
  }, [building, buildProducts.length]);

  const buildTotal = buildLines.reduce((sum, l) => sum + l.qty_m2 * l.unit_price, 0);
  const submitNewQuote = () => act('new-quote', async () => {
    const { adminCreateQuote } = await import('../lib/api');
    const q = await adminCreateQuote({
      customer_name: customer.name,
      customer_email: customer.email,
      items: buildLines.map(l => ({ product_id: l.product_id, qty_m2: l.qty_m2, unit_price: l.unit_price })),
    });
    setBuilding(false);
    setBuildLines([]);
    alert(`Offerte #${(q as { quote_number?: number }).quote_number ?? ''} aangemaakt voor ${customer.name}.`);
  });

  const act = async (key: string, fn: () => Promise<unknown>) => {
    setBusy(key);
    try { await fn(); await load(); onChanged(); }
    catch (e) { alert((e as Error).message); }
    finally { setBusy(null); }
  };

  const startEditQuote = (q: AdminQuote) => {
    setEditingQuote(q.id);
    setEditForm({
      customer_name: q.customer_name,
      customer_phone: q.customer_phone ?? '',
      total_excl_vat: String(q.total_excl_vat),
      notes_nl: q.notes_nl ?? '',
    });
  };
  const saveEditQuote = (id: string) => act(id + ':edit', async () => {
    const { adminEditQuote } = await import('../lib/api');
    const result = await adminEditQuote(id, {
      customer_name: editForm.customer_name,
      customer_phone: editForm.customer_phone || null,
      total_excl_vat: parseFloat(editForm.total_excl_vat) || 0,
      notes_nl: editForm.notes_nl || null,
    }) as AdminQuote & { quote_number?: number };
    setEditingQuote(null);
    // v5.28: een bewaarde offerte wordt NOOIT aangepast — elke bewerking
    // maakt een nieuwe versie; de vorige blijft voor altijd bewaard.
    if (result && result.id !== id) {
      alert(`Nieuwe offerte #${result.quote_number ?? ''} aangemaakt met uw wijzigingen. De bewaarde offerte blijft ongewijzigd — zo liggen prijzen en afspraken altijd vast.`);
    }
  });

  const quotePdf = async (q: AdminQuote) => {
    const win = window.open('', '_blank');
    if (!win) { alert('Sta pop-ups toe en probeer opnieuw.'); return; }
    const { printQuotePDF, OWNER_COMPANY } = await import('../lib/quotePdf');
    void printQuotePDF({ quote: q as unknown as import('../lib/quotePdf').PdfQuote, company: OWNER_COMPANY, isResellerRouted: false, t: (k: string) => k, targetWindow: win }).catch((e: Error) => alert(e.message));
  };
  const invoicePdf = async (inv: import('../lib/api').AdminInvoice) => {
    const win = window.open('', '_blank');
    const { printInvoicePDF } = await import('../lib/invoicePdf');
    printInvoicePDF(inv, { targetWindow: win, orderNumber: inv.order_number ?? null, company: uitgeverVanFactuur(inv) });
  };

  const termsLabel: Record<string, string> = { vooraf: 'vooraf', '30_dagen': '30 dagen', einde_maand: 'einde maand' };

  // v5.19: "Reageren" stuurt de inhoud MEE in de mail — zonder context heeft
  // een reactie geen nut (beslissing Franky). mailto kan geen bijlagen, dus
  // de offerte-inhoud gaat voorgevuld in de body; de PDF kan Filip zelf
  // bijvoegen (knop PDF ernaast).
  const quoteRef = (q: AdminQuote) => `offerte ${((q as { quote_number?: number }).quote_number ? '#' + (q as { quote_number?: number }).quote_number : q.id.slice(0, 8))}`;
  const mailForQuote = (q: AdminQuote) => {
    const subject = `Uw ${quoteRef(q)} bij Het Gevelsteenbedrijf — ${formatEuro(q.total_incl_vat)}`;
    const notes = (q.notes_nl ?? '').replaceAll(' || ', '\n').replaceAll(' | ', '\n  ');
    const body = `Beste ${q.customer_name},\n\nIn verband met uw ${quoteRef(q)} van ${formatDate(q.created_at)}:\n\n${notes}\n\nTotaal excl. BTW: ${formatEuro(q.total_excl_vat)}\nTotaal incl. BTW: ${formatEuro(q.total_incl_vat)}\nStatus: ${q.status}\n\n`;
    return `mailto:${customer.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body.slice(0, 1800))}`;
  };
  const mailOverview = () => {
    const lines: string[] = [`Beste ${customer.name},`, ''];
    (detail?.quotes ?? []).forEach(q => lines.push(`- ${quoteRef(q)} (${formatDate(q.created_at)}): ${formatEuro(q.total_incl_vat)} — status ${q.status}`));
    orders.forEach(o => lines.push(`- bestelling #${o.order_number}: ${formatEuro(o.total_incl_vat)} — ${o.status}, betaling ${o.payment_status}`));
    invoices.forEach(i => lines.push(`- ${(i as { doc_type?: string }).doc_type === 'creditnota' ? 'creditnota' : 'factuur'} ${new Date(i.created_at * 1000).getFullYear()}-${String(i.invoice_number).padStart(4, '0')}: ${formatEuro(i.total_incl_vat)} — ${i.status}`));
    lines.push('', '');
    return `mailto:${customer.email}?subject=${encodeURIComponent('Uw dossier bij Het Gevelsteenbedrijf')}&body=${encodeURIComponent(lines.join('\n').slice(0, 1800))}`;
  };

  return (
    <>
      {!inline && <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />}
      <aside className={inline
        ? 'bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden lg:sticky lg:top-24 lg:max-h-[85vh] lg:overflow-y-auto'
        : 'fixed top-0 right-0 bottom-0 w-full sm:w-[560px] bg-white z-50 shadow-2xl overflow-y-auto'}>
        <div className="sticky top-0 bg-[#1a1a1a] text-white px-5 py-4 flex items-start justify-between gap-3 z-10">
          <div className="min-w-0">
            <h2 className="text-lg font-bold truncate" style={{ fontFamily: 'Playfair Display' }}>{customer.name}</h2>
            <p className="text-xs text-gray-300 truncate">{customer.email}</p>
            <div className="flex gap-2 mt-2 text-[11px]">
              <span className="px-2 py-0.5 bg-white/10 rounded">{detail?.quotes.length ?? customer.quotes_count} offertes</span>
              <span className="px-2 py-0.5 bg-white/10 rounded">{orders.length} bestellingen</span>
              <span className="px-2 py-0.5 bg-white/10 rounded">{invoices.length} facturen</span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <a
              href={mailOverview()}
              className="px-2.5 py-1.5 bg-[#c47a3a] hover:bg-[#a8643a] rounded-lg text-xs font-bold"
              title="Reageer per e-mail — het volledige dossieroverzicht (offertes, bestellingen, facturen) staat voorgevuld in de mail"
            >
              Reageren
            </a>
            <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded-lg" title="Sluiten"><X size={18} /></button>
          </div>
        </div>

        <div className="p-4 sm:p-5 space-y-5">
          {/* v5.20: alle klantgegevens bovenaan in het dossier */}
          {detail && (
            <div className="border border-gray-100 rounded-xl p-3 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              <p className="col-span-2 text-xs font-bold text-[#8a7e74] uppercase tracking-wide mb-1">Klantgegevens</p>
              <p><span className="text-xs text-[#8a7e74]">Telefoon:</span> {detail.phone ?? '—'}</p>
              <p><span className="text-xs text-[#8a7e74]">Bedrijf:</span> {detail.company_name ?? '—'}</p>
              <p className="col-span-2"><span className="text-xs text-[#8a7e74]">Adres:</span> {[detail.address, detail.postal_code, detail.city].filter(Boolean).join(', ') || '—'}</p>
              <p><span className="text-xs text-[#8a7e74]">BTW:</span> {detail.btw_number ?? '—'}</p>
              <p><span className="text-xs text-[#8a7e74]">Vaste handelaar:</span> {detail.reseller_company_name ?? 'Filip-direct'}</p>
              <p className="col-span-2"><span className="text-xs text-[#8a7e74]">Klant sinds:</span> {formatDate(detail.created_at)} · totaal besteed: {formatEuro(detail.total_spent ?? customer.total_spent)}</p>
            </div>
          )}

          {/* Te doen — wat nog aandacht vraagt */}
          {detail && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900">
              <p className="font-bold uppercase tracking-wide mb-1">Nog te doen</p>
              <ul className="space-y-0.5">
                {detail.quotes.filter(q => q.status === 'draft' || q.status === 'sent').length > 0 && (
                  <li>• {detail.quotes.filter(q => q.status === 'draft' || q.status === 'sent').length} openstaande offerte(s) — opvolgen</li>
                )}
                {orders.filter(o => o.payment_terms === 'vooraf' && o.payment_status !== 'betaald').length > 0 && (
                  <li>• Bestelling wacht op betaling vóór levering</li>
                )}
                {orders.filter(o => o.status !== 'geannuleerd' && (o.invoice_count ?? 0) === 0).length > 0 && (
                  <li>• Bestelling(en) nog niet gefactureerd</li>
                )}
                {invoices.filter(i => i.status === 'open').length > 0 && (
                  <li>• {invoices.filter(i => i.status === 'open').length} openstaande factuur/facturen</li>
                )}
                {detail.quotes.every(q => !['draft', 'sent'].includes(q.status)) && orders.length === 0 && invoices.length === 0 && (
                  <li>• Niets — dossier is up-to-date</li>
                )}
              </ul>
            </div>
          )}

          {/* Offertes */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-bold text-[#8a7e74] uppercase tracking-wide">Offertes</h3>
              <button
                onClick={() => setBuilding(b => !b)}
                className="px-2.5 py-1 bg-[#c47a3a] hover:bg-[#a8643a] text-white rounded-lg text-xs font-bold"
                title="Stel een nieuwe offerte samen met één of meerdere gevelstenen, aan uw catalogusprijzen"
              >
                {building ? 'Sluit' : '+ Nieuwe offerte'}
              </button>
            </div>
            {building && (
              <div className="border-2 border-[#c47a3a]/40 rounded-xl p-3 mb-3 space-y-2 bg-[#faf7f3]/60">
                {buildLines.map((l, i) => {
                  const prod = buildProducts.find(p => p.id === l.product_id);
                  return (
                    <div key={i} className="flex items-center gap-1.5 flex-wrap">
                      {/* V5.31: Filip ziet de steen zelf, niet alleen de naam */}
                      {prod && (
                        <img
                          src={`/products/${prod.slug}.jpg`}
                          alt={prod.name}
                          title={prod.name}
                          className="h-9 w-14 object-cover rounded border border-gray-200 flex-shrink-0"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      )}
                      <select value={l.product_id} onChange={e => {
                        const np = buildProducts.find(p => p.id === e.target.value);
                        setBuildLines(ls => ls.map((x, xi) => xi === i ? { ...x, product_id: e.target.value, unit_price: np?.locked_price ?? x.unit_price } : x));
                      }} className="flex-1 min-w-[150px] px-2 py-1.5 border border-gray-200 rounded text-xs">
                        {buildProducts.map(p => <option key={p.id} value={p.id}>{p.name} — €{p.locked_price}/m²</option>)}
                      </select>
                      <input type="number" min="1" step="1" value={l.qty_m2}
                        onChange={e => setBuildLines(ls => ls.map((x, xi) => xi === i ? { ...x, qty_m2: parseFloat(e.target.value) || 0 } : x))}
                        className="w-16 px-2 py-1.5 border border-gray-200 rounded text-xs" title="Oppervlakte in m²" />
                      <span className="text-[10px] text-[#8a7e74]">m² ×</span>
                      <input type="number" step="0.01" value={l.unit_price}
                        onChange={e => setBuildLines(ls => ls.map((x, xi) => xi === i ? { ...x, unit_price: parseFloat(e.target.value) || 0 } : x))}
                        className="w-20 px-2 py-1.5 border-2 border-[#c47a3a]/50 rounded text-xs font-bold" title="Prijs per m² — vrij aanpasbaar; de prijsvloer bewaakt uw marge" />
                      <span className="text-[10px] text-[#8a7e74]">€/m²</span>
                      {prod && <span className="text-xs font-bold text-[#c47a3a] ml-auto">{formatEuro(l.qty_m2 * l.unit_price)}</span>}
                      {buildLines.length > 1 && (
                        <button onClick={() => setBuildLines(ls => ls.filter((_, xi) => xi !== i))} className="text-red-600 text-xs font-bold px-1">✕</button>
                      )}
                    </div>
                  );
                })}
                <div className="flex items-center justify-between pt-1">
                  <button
                    onClick={() => setBuildLines(ls => ls.length >= 5 ? ls : [...ls, { product_id: buildProducts[0]?.id ?? '', qty_m2: 50, unit_price: buildProducts[0]?.locked_price ?? 0 }])}
                    disabled={buildLines.length >= 5}
                    className="text-xs font-bold text-[#c47a3a] hover:underline disabled:opacity-40 disabled:no-underline"
                    title="Maximaal 5 gevelstenen per offerte"
                  >
                    {buildLines.length >= 5 ? 'Max. 5 stenen' : `+ Nog een gevelsteen (${buildLines.length}/5)`}
                  </button>
                  <div className="flex items-center gap-2">
                    <span className="text-xs">Totaal excl.: <b>{formatEuro(buildTotal)}</b> · incl.: <b>{formatEuro(buildTotal * 1.21)}</b></span>
                    <button
                      onClick={() => void submitNewQuote()}
                      disabled={busy === 'new-quote' || buildLines.length === 0}
                      className="px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded text-xs font-bold"
                    >
                      Offerte aanmaken
                    </button>
                  </div>
                </div>
              </div>
            )}
            {!detail ? <p className="text-sm text-[#8a7e74]"><Loader2 size={13} className="inline animate-spin" /> Laden…</p>
            : detail.quotes.length === 0 ? <p className="text-sm text-[#8a7e74]">Geen offertes.</p>
            : detail.quotes.map(q => (
              <div key={q.id} className="border border-gray-100 rounded-xl p-3 mb-2">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0">
                    <StatusBadge status={q.status} />
                    <span className="text-xs text-[#8a7e74]">{formatDate(q.created_at)}</span>
                    <span className="text-sm font-bold">{formatEuro(q.total_incl_vat)}</span>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <select
                      value={q.status}
                      disabled={busy === q.id + ':status'}
                      onChange={e => void act(q.id + ':status', () => adminUpdateQuoteStatus(q.id, e.target.value))}
                      className="px-1.5 py-1 border border-gray-200 rounded text-xs"
                    >
                      {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <button onClick={() => void quotePdf(q)} className="px-2 py-1 bg-[#faf7f3] hover:bg-gray-100 rounded text-xs font-bold text-[#c47a3a]">PDF</button>
                    <a href={mailForQuote(q)} className="px-2 py-1 bg-[#faf7f3] hover:bg-gray-100 rounded text-xs font-bold text-[#c47a3a]" title="Mail de klant over déze offerte — de volledige offerte-inhoud staat voorgevuld in het bericht">Mail</a>
                    <button
                      onClick={() => editingQuote === q.id ? setEditingQuote(null) : startEditQuote(q)}
                      className="px-2 py-1 bg-[#c47a3a] hover:bg-[#a8643a] text-white rounded text-xs font-bold"
                      title="Bewaarde offertes liggen vast: uw wijzigingen worden een NIEUWE offerte met nieuw nummer; deze versie blijft ongewijzigd bewaard."
                    >
                      {editingQuote === q.id ? 'Sluit' : 'Hermaken'}
                    </button>
                    <button
                      onClick={() => void act(q.id + ':order', async () => {
                        if (!confirm(`Klant akkoord? Offerte omzetten naar bestelling (${formatEuro(q.total_incl_vat)}).`)) return;
                        const { adminConvertQuoteToOrder } = await import('../lib/api');
                        await adminConvertQuoteToOrder(q.id);
                      })}
                      className="px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-bold"
                      title="Klant gaat akkoord → bestelling (daarna factuur maken)"
                    >
                      → Bestel
                    </button>
                  </div>
                </div>
                {editingQuote === q.id && (
                  <div className="mt-3 space-y-2 border-t border-gray-100 pt-3">
                    <div className="grid grid-cols-2 gap-2">
                      <input value={editForm.customer_name} onChange={e => setEditForm(f => ({ ...f, customer_name: e.target.value }))}
                        placeholder="Naam" className="px-2 py-1.5 border border-gray-200 rounded text-xs" />
                      <input value={editForm.customer_phone} onChange={e => setEditForm(f => ({ ...f, customer_phone: e.target.value }))}
                        placeholder="Telefoon" className="px-2 py-1.5 border border-gray-200 rounded text-xs" />
                    </div>
                    <div className="relative">
                      <input type="number" step="0.01" value={editForm.total_excl_vat} onChange={e => setEditForm(f => ({ ...f, total_excl_vat: e.target.value }))}
                        className="w-full px-2 py-1.5 border-2 border-[#c47a3a] rounded text-xs font-bold pr-16" />
                      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-[#8a7e74]">excl. BTW</span>
                    </div>
                    <textarea rows={4} value={editForm.notes_nl} onChange={e => setEditForm(f => ({ ...f, notes_nl: e.target.value }))}
                      placeholder="Offertedetails / notities" className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs font-mono" />
                    <button
                      onClick={() => void saveEditQuote(q.id)}
                      disabled={busy === q.id + ':edit'}
                      className="px-3 py-1.5 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded text-xs font-bold"
                    >
                      {busy === q.id + ':edit' ? 'Opslaan…' : 'Wijzigingen opslaan'}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </section>

          {/* Bestellingen */}
          <section>
            <h3 className="text-xs font-bold text-[#8a7e74] uppercase tracking-wide mb-2">Bestellingen</h3>
            {orders.length === 0 ? <p className="text-sm text-[#8a7e74]">Geen bestellingen.</p>
            : orders.map(o => (
              <div key={o.id} className="border border-gray-100 rounded-xl p-3 mb-2 flex items-center justify-between gap-2 flex-wrap text-sm">
                <span className="font-mono text-xs">#{o.order_number}</span>
                <span className="text-xs">{o.status} · {termsLabel[o.payment_terms]}</span>
                <button
                  onClick={() => void act(o.id, async () => {
                    const { adminUpdateOrder } = await import('../lib/api');
                    await adminUpdateOrder(o.id, { payment_status: o.payment_status === 'betaald' ? 'open' : 'betaald' });
                  })}
                  className={`px-2 py-0.5 rounded text-xs font-bold ${o.payment_status === 'betaald' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-800'}`}
                >
                  {o.payment_status === 'betaald' ? '✓ Betaald' : 'Open'}
                </button>
                {(o.invoice_count ?? 0) === 0 && o.status !== 'geannuleerd' && (
                  <button
                    onClick={() => void act(o.id + ':fac', async () => {
                      const { adminCreateInvoice } = await import('../lib/api');
                      await adminCreateInvoice(o.id);
                    })}
                    className="px-2 py-1 bg-[#c47a3a] hover:bg-[#a8643a] text-white rounded text-xs font-bold"
                  >
                    Maak factuur
                  </button>
                )}
                <span className="font-bold">{formatEuro(o.total_incl_vat)}</span>
              </div>
            ))}
          </section>

          {/* Facturen */}
          <section>
            <h3 className="text-xs font-bold text-[#8a7e74] uppercase tracking-wide mb-2">Facturen</h3>
            {invoices.length === 0 ? <p className="text-sm text-[#8a7e74]">Geen facturen.</p>
            : invoices.map(inv => (
              <div key={inv.id} className="border border-gray-100 rounded-xl p-3 mb-2 flex items-center justify-between gap-2 text-sm">
                <span className="font-mono text-xs">{(inv as { doc_type?: string }).doc_type === 'creditnota' ? 'CN' : 'FAC'}-{new Date(inv.created_at * 1000).getFullYear()}-{String(inv.invoice_number).padStart(4, '0')}</span>
                <span className={`px-2 py-0.5 rounded text-xs font-bold ${inv.status === 'betaald' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-800'}`}>{inv.status}</span>
                <span className="font-bold">{formatEuro(inv.total_incl_vat)}</span>
                {(inv as { doc_type?: string }).doc_type !== 'creditnota' && (
                  <button
                    onClick={() => void act(inv.id + ':cn', async () => {
                      if (!confirm('Creditnota maken voor deze factuur? De factuur zelf blijft bestaan (wettelijk verplicht); de creditnota vergoedt het volledige bedrag.')) return;
                      const { adminCreateCreditNote } = await import('../lib/api');
                      await adminCreateCreditNote(inv.id);
                    })}
                    className="px-2 py-1 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded text-xs font-bold"
                    title="Correctie = creditnota; facturen worden nooit gewijzigd of verwijderd"
                  >
                    Creditnota
                  </button>
                )}
                <button onClick={() => void invoicePdf(inv)} className="px-2 py-1 bg-[#c47a3a] hover:bg-[#a8643a] text-white rounded text-xs font-bold">PDF</button>
              </div>
            ))}
          </section>
        </div>
      </aside>
    </>
  );
}

// V6.17 — een factuur draagt altijd de gegevens van haar UITGEVER. Bij een
// handelaarsbestelling is dat de handelaar (eigen naam, adres, BTW-nummer en
// logo), niet de fabrikant. De API levert die velden mee met de factuur.
function uitgeverVanFactuur(inv: import('../lib/api').AdminInvoice): import('../lib/quotePdf').PdfCompany | undefined {
  const u = inv as unknown as {
    issuer_name?: string | null; issuer_btw?: string | null; issuer_address?: string | null;
    issuer_postal_code?: string | null; issuer_city?: string | null; issuer_email?: string | null;
    issuer_phone?: string | null; issuer_logo_url?: string | null;
  };
  if (!u.issuer_name) return undefined; // fabrikant → standaardbranding
  return {
    name: u.issuer_name,
    address: u.issuer_address ?? '',
    postalCode: u.issuer_postal_code ?? '',
    city: u.issuer_city ?? '',
    country: 'België',
    btw: u.issuer_btw ?? '',
    email: u.issuer_email ?? '',
    phone: u.issuer_phone ?? '',
    logo_url: u.issuer_logo_url ?? undefined,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// QUOTE NOTES PARSER — parses pipe-delimited notes into structured data
// ═══════════════════════════════════════════════════════════════════════════════

function parseQuoteNotes(notes: string | null): Record<string, string> {
  if (!notes) return {};
  const parsed: Record<string, string> = {};
  for (const part of notes.split(' | ')) {
    const colonIdx = part.indexOf(':');
    if (colonIdx > 0) {
      const key = part.substring(0, colonIdx).trim();
      const value = part.substring(colonIdx + 1).trim();
      if (key && value) parsed[key] = value;
    }
  }
  return parsed;
}

function findProductFromNotes(notes: Record<string, string>): typeof PRODUCTS[number] | undefined {
  const productName = notes['Product'];
  if (!productName) return undefined;
  return PRODUCTS.find(p => p.name === productName);
}

// ═══════════════════════════════════════════════════════════════════════════════
// TEST OFFERTES TAB — for owners to create test quotes
// ═══════════════════════════════════════════════════════════════════════════════

function TestOffertesTab({ quotes, onRefresh, user }: { quotes: AdminQuote[]; onRefresh: () => void; user: import('../store/AuthContext').User | null }) {
  const { t } = useLanguage();
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const updateStatus = async (id: string, status: string) => {
    setUpdatingId(id);
    try {
      await adminUpdateQuoteStatus(id, status);
      onRefresh();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setUpdatingId(null);
    }
  };

  const deleteQuote = async (q: AdminQuote) => {
    if (q.status !== 'draft') {
      alert(t('admin.testQuotes.deleteOnlyDraft'));
      return;
    }
    if (!confirm(`${t('admin.testQuotes.deleteConfirm')}\n\n${q.customer_name} — ${q.customer_email}`)) return;
    setDeletingId(q.id);
    try {
      await adminDeleteQuote(q.id);
      onRefresh();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setDeletingId(null);
    }
  };

  const statuses = ['draft', 'sent', 'accepted', 'rejected', 'expired'];
  // Map status code → translated label for the dropdown options
  const statusLabel = (s: string) => t(`admin.status.${s}`);

  // Company info on the PDF.
  //
  // Two sources:
  //   - Owner profile (from useAuth) — used when the quote is Filip-direct (no reseller).
  //   - Reseller fields (joined into AdminQuote on the API side) — used when q.reseller_id is set.
  //
  // The PDF always carries a "Powered by Het Gevelsteenbedrijf" small tag at the
  // very top so the brand chain is visible to the end customer.
  const escapeHtml = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] || c));
  const ownerCompany = {
    name:       (user?.company_name || 'Het Gevelsteenbedrijf').trim(),
    address:    (user?.address || 'Bredabaan 123').trim(),
    postalCode: (user?.postal_code || '2970').trim(),
    city:       (user?.city || 'Schilde').trim(),
    country:    'België',
    btw:        (user?.btw_number || 'BE0XXX.XXX.XXX').trim(),
    email:      (user?.email || 'info@gevelsteenbedrijf.be').trim(),
    phone:      (user?.phone || '+32 XXX XX XX XX').trim(),
    logo_url:   '' as string,
  };

  // Helper to pick the right company per quote — owner if no reseller, reseller otherwise.
  const companyForQuote = (q: AdminQuote) => {
    if (q.reseller_id && q.reseller_company_name) {
      return {
        name:       q.reseller_company_name.trim(),
        address:    (q.reseller_address || '').trim(),
        postalCode: (q.reseller_postal_code || '').trim(),
        city:       (q.reseller_city || '').trim(),
        country:    'België',
        btw:        (q.reseller_btw_number || '').trim(),
        email:      (q.reseller_contact_email || '').trim(),
        phone:      (q.reseller_contact_phone || '').trim(),
        logo_url:   (q.reseller_logo_url || '').trim(),
      };
    }
    return ownerCompany;
  };

  const printQuotePDF = (q: AdminQuote) => {
    const w = window.open('', '_blank');
    if (!w) return;
    // Owner generates PDFs in their UI language. Customer-facing copy could
    // later route by customer.preferred_language; for v1 use the active UI lang.

    const notes = parseQuoteNotes(q.notes_nl);
    const product = findProductFromNotes(notes);
    const vatAmount = q.total_incl_vat - q.total_excl_vat;
    const quoteDate = formatDate(q.created_at);
    const expiryDate = q.expires_at ? formatDate(q.expires_at) : '';

    // Build detail rows from parsed notes
    const detailRows: { label: string; value: string }[] = [];
    if (notes['Product']) detailRows.push({ label: 'Product', value: notes['Product'] });
    if (product?.supplierCode) detailRows.push({ label: 'Artikelnummer', value: product.supplierCode });
    if (notes['Kleur'] || product?.color) detailRows.push({ label: 'Kleur / Textuur', value: notes['Kleur'] || `${product?.color} | ${product?.texture}` });
    if (product?.dimensions) detailRows.push({ label: 'Afmetingen', value: product.dimensions });
    if (notes['Oppervlakte']) detailRows.push({ label: 'Oppervlakte', value: notes['Oppervlakte'].includes('m²') ? notes['Oppervlakte'] : notes['Oppervlakte'] + ' m²' });
    if (notes['Voegbreedte']) detailRows.push({ label: 'Voegbreedte', value: notes['Voegbreedte'] });
    if (notes['Voegkleur']) detailRows.push({ label: 'Voegkleur', value: notes['Voegkleur'] });
    if (notes['Snijverlies']) detailRows.push({ label: 'Snijverlies', value: notes['Snijverlies'] });
    if (notes['Stenen']) detailRows.push({ label: 'Aantal strips', value: notes['Stenen'] });
    if (notes['Hoeken']) detailRows.push({ label: 'Hoekstukken', value: notes['Hoeken'] });
    if (notes['Lijm']) detailRows.push({ label: 'Lijm', value: notes['Lijm'] });
    if (notes['Voegsel']) detailRows.push({ label: 'Voegsel', value: notes['Voegsel'] });

    // Price breakdown rows
    const priceRows: { label: string; value: string }[] = [];
    if (notes['Baksteenstrips']) priceRows.push({ label: 'Baksteenstrips', value: notes['Baksteenstrips'] });
    if (notes['Hoekstenen']) priceRows.push({ label: 'Hoekstenen', value: notes['Hoekstenen'] });
    if (notes['Lijm']) {
      const lijmPrice = notes['Lijm'].match(/€[\d.,]+/)?.[0];
      if (lijmPrice) priceRows.push({ label: 'Lijm', value: lijmPrice });
    }
    if (notes['Voegsel']) {
      const voegPrice = notes['Voegsel'].match(/€[\d.,]+/)?.[0];
      if (voegPrice) priceRows.push({ label: 'Voegsel', value: voegPrice });
    }

    // Product image
    const productImage = product?.images?.[0] || '';
    const imageUrl = productImage ? `${window.location.origin}${productImage}` : '';

    // QuotePage notes (simpler format): Type, Bedrijf, BTW, Postcode, Bericht
    const isSimpleQuote = !!notes['Type'] && !notes['Stenen'];
    const simpleRows: { label: string; value: string }[] = [];
    if (isSimpleQuote) {
      if (notes['Type']) simpleRows.push({ label: 'Type klant', value: notes['Type'] });
      if (notes['Bedrijf']) simpleRows.push({ label: 'Bedrijf', value: notes['Bedrijf'] });
      if (notes['BTW']) simpleRows.push({ label: 'BTW-nummer', value: notes['BTW'] });
      if (notes['Postcode']) simpleRows.push({ label: 'Postcode', value: notes['Postcode'] });
      if (notes['Product']) simpleRows.push({ label: 'Product', value: notes['Product'] });
      if (notes['Oppervlakte']) simpleRows.push({ label: 'Oppervlakte', value: notes['Oppervlakte'].includes('m²') ? notes['Oppervlakte'] : notes['Oppervlakte'] + ' m²' });
      if (notes['Bericht']) simpleRows.push({ label: 'Bericht', value: notes['Bericht'] });
    }

    const detailsToShow = isSimpleQuote ? simpleRows : detailRows;

    w.document.write(`<!DOCTYPE html><html><head><title>Offerte ${q.customer_name}</title>
<style>
  @media print { @page { margin: 12mm 12mm; } }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Segoe UI', system-ui, -apple-system, sans-serif; color: #1a1a1a; max-width: 800px; margin: 0 auto; padding: 20px 24px; font-size: 11px; line-height: 1.4; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #c47a3a; padding-bottom: 12px; margin-bottom: 16px; }
  .logo { font-size: 20px; font-weight: 800; color: #c47a3a; letter-spacing: -0.5px; }
  .logo-sub { font-size: 9px; color: #8a7e74; margin-top: 1px; font-weight: 400; }
  .quote-ref { text-align: right; font-size: 10px; color: #6b6b6b; }
  .quote-ref strong { display: block; font-size: 16px; color: #1a1a1a; margin-bottom: 2px; }
  .client-info { background: #faf7f3; border-radius: 6px; padding: 10px 14px; margin-bottom: 14px; display: flex; gap: 24px; }
  .client-info div { flex: 1; }
  .client-info .label { font-size: 8px; text-transform: uppercase; letter-spacing: 1px; color: #8a7e74; font-weight: 600; margin-bottom: 2px; }
  .client-info .value { font-size: 11px; font-weight: 500; }
  .section-title { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: #c47a3a; margin: 14px 0 6px; }
  .product-block { display: flex; gap: 16px; margin-bottom: 12px; align-items: flex-start; }
  .product-img { width: 100px; height: 75px; border-radius: 6px; object-fit: cover; border: 1px solid #e5e5e5; flex-shrink: 0; }
  .product-meta { flex: 1; }
  .product-name { font-size: 14px; font-weight: 700; margin-bottom: 2px; }
  .product-code { font-size: 10px; color: #8a7e74; }
  table { width: 100%; border-collapse: collapse; margin: 4px 0 12px; }
  table.details td { padding: 4px 8px; border-bottom: 1px solid #f0ece8; font-size: 10px; }
  table.details td:first-child { color: #6b6b6b; width: 40%; font-weight: 500; }
  table.details td:last-child { font-weight: 500; }
  table.details tr:last-child td { border-bottom: none; }
  table.pricing th { text-align: left; padding: 5px 8px; font-size: 9px; text-transform: uppercase; letter-spacing: 0.8px; color: #8a7e74; border-bottom: 2px solid #e5e5e5; font-weight: 600; }
  table.pricing td { padding: 6px 8px; border-bottom: 1px solid #f0ece8; font-size: 11px; }
  table.pricing .subtotal td { font-weight: 500; }
  table.pricing .vat td { color: #6b6b6b; font-size: 10px; }
  table.pricing .total td { border-top: 2px solid #c47a3a; border-bottom: none; font-size: 13px; font-weight: 700; color: #c47a3a; padding-top: 8px; }
  table.pricing td:last-child, table.pricing th:last-child { text-align: right; }
  .conditions { margin-top: 10px; padding-top: 8px; border-top: 1px solid #e5e5e5; }
  .conditions h4 { font-size: 9px; text-transform: uppercase; letter-spacing: 1px; color: #8a7e74; font-weight: 600; margin-bottom: 4px; }
  .conditions ul { list-style: none; padding: 0; }
  .conditions li { font-size: 9px; color: #999; padding: 1px 0; padding-left: 12px; position: relative; }
  .conditions li::before { content: '\\2022'; position: absolute; left: 0; color: #c47a3a; }
  .footer { margin-top: 8px; text-align: center; font-size: 9px; color: #aaa; padding-top: 6px; border-top: 1px solid #f0ece8; }
</style></head><body>`);

    // Header — for reseller-routed quotes: small "Het Gevelsteenbedrijf" tag on top,
    // then the reseller's logo + name + contact as primary identity.
    // For Filip-direct quotes: only the owner brand.
    const company = companyForQuote(q);
    const isResellerRouted = !!q.reseller_id;

    w.document.write(`<div class="header">`);
    if (isResellerRouted) {
      w.document.write(`
  <div>
    <div style="font-size:9px;color:#8a7e74;letter-spacing:1px;text-transform:uppercase;margin-bottom:4px">${escapeHtml(t('pdf.poweredBy'))}</div>
    ${company.logo_url ? `<img src="${escapeHtml(company.logo_url)}" alt="${escapeHtml(company.name)}" style="max-height:36px;max-width:180px;margin-bottom:6px;display:block" onerror="this.style.display='none'" />` : ''}
    <div class="logo">${escapeHtml(company.name)}</div>
    <div class="logo-sub" style="margin-top:4px;color:#6b6b6b">
      ${escapeHtml([company.address, `${company.postalCode} ${company.city}`, company.country].filter(Boolean).join(' · '))}<br/>
      ${company.btw ? 'BTW ' + escapeHtml(company.btw) : ''} ${company.email ? '&middot; ' + escapeHtml(company.email) : ''} ${company.phone ? '&middot; ' + escapeHtml(company.phone) : ''}
    </div>
  </div>`);
    } else {
      w.document.write(`
  <div>
    <div class="logo">${escapeHtml(company.name)}</div>
    <div class="logo-sub">Premium baksteenstrips &amp; gevelbekleding</div>
    <div class="logo-sub" style="margin-top:4px;color:#6b6b6b">
      ${escapeHtml(company.address)} &middot; ${escapeHtml(company.postalCode)} ${escapeHtml(company.city)}, ${escapeHtml(company.country)}<br/>
      BTW ${escapeHtml(company.btw)} &middot; ${escapeHtml(company.email)} &middot; ${escapeHtml(company.phone)}
    </div>
  </div>`);
    }
    w.document.write(`
  <div class="quote-ref">
    <strong>${escapeHtml(t('pdf.quote'))}</strong>
    ${escapeHtml(t('pdf.ref'))}: ${q.id.substring(0, 8).toUpperCase()}<br/>
    ${escapeHtml(t('pdf.date'))}: ${quoteDate}${expiryDate ? `<br/>${escapeHtml(t('pdf.validUntil'))}: ${expiryDate}` : ''}
  </div>
</div>`);

    // Client info — all customer-controlled fields MUST be escaped (XSS protection
    // since customer can register with arbitrary text in name/email/btw).
    w.document.write(`
<div class="client-info">
  <div>
    <div class="label">${escapeHtml(t('pdf.client'))}</div>
    <div class="value">${escapeHtml(q.customer_name)}</div>
    <div style="font-size:12px;color:#6b6b6b;margin-top:2px">${escapeHtml(q.customer_email)}</div>
    ${q.customer_phone ? `<div style="font-size:12px;color:#6b6b6b">${escapeHtml(q.customer_phone)}</div>` : ''}
  </div>
  <div>
    <div class="label">${escapeHtml(t('pdf.status'))}</div>
    <div class="value">${escapeHtml(t(`admin.status.${q.status}`))}</div>
    ${q.is_b2b ? `<div style="font-size:12px;color:#6b6b6b;margin-top:2px">B2B${q.customer_btw_number ? ` — ${escapeHtml(q.customer_btw_number)}` : ''}</div>` : '<div style="font-size:12px;color:#6b6b6b;margin-top:2px">Particulier (B2C)</div>'}
  </div>
</div>`);

    // Product block with image
    if (product) {
      w.document.write(`<div class="section-title">${escapeHtml(t('pdf.product'))}</div>`);
      w.document.write(`<div class="product-block">`);
      if (imageUrl) {
        w.document.write(`<img class="product-img" src="${imageUrl}" alt="${product.name}" onerror="this.style.display='none'" />`);
      }
      w.document.write(`<div class="product-meta">
  <div class="product-name">${product.name}</div>
  <div class="product-code">${product.supplierCode} &bull; ${product.category}</div>
  <div style="font-size:12px;color:#6b6b6b;margin-top:6px">${product.description}</div>
</div></div>`);
    }

    // Configuration details table
    if (detailsToShow.length > 0) {
      w.document.write(`<div class="section-title">${escapeHtml(isSimpleQuote ? t('pdf.requestDetails') : t('pdf.configuration'))}</div>`);
      w.document.write(`<table class="details">`);
      for (const row of detailsToShow) {
        // row.value originates from parsed q.notes_nl (customer-controlled) — must escape
        w.document.write(`<tr><td>${row.label}</td><td>${escapeHtml(row.value)}</td></tr>`);
      }
      w.document.write(`</table>`);
    }

    // Price breakdown
    w.document.write(`<div class="section-title">${escapeHtml(t('pdf.priceOverview'))}</div>`);
    w.document.write(`<table class="pricing">
<thead><tr><th>${escapeHtml(t('pdf.description'))}</th><th>${escapeHtml(t('pdf.amount'))}</th></tr></thead><tbody>`);
    if (priceRows.length > 0 && !isSimpleQuote) {
      for (const row of priceRows) {
        // row.value originates from parsed q.notes_nl (customer-controlled) — must escape
        w.document.write(`<tr><td>${row.label}</td><td>${escapeHtml(row.value)}</td></tr>`);
      }
    }
    // BTW display:
    //   - B2B (customer has BTW number) → split: subtotaal excl. + BTW + totaal incl.
    //   - B2C (no BTW) → single total (incl. BTW), with "BTW inbegrepen" note
    const isB2B = !!(q.is_b2b || (q.customer_btw_number && q.customer_btw_number.trim().length > 0));
    if (isB2B) {
      w.document.write(`<tr class="subtotal"><td>${escapeHtml(t('pdf.subtotalExcl'))}</td><td>${formatEuro(q.total_excl_vat)}</td></tr>`);
      w.document.write(`<tr class="vat"><td>${escapeHtml(t('pdf.vat21'))}</td><td>${formatEuro(vatAmount)}</td></tr>`);
      w.document.write(`<tr class="total"><td>${escapeHtml(t('pdf.totalIncl'))}</td><td>${formatEuro(q.total_incl_vat)}</td></tr>`);
    } else {
      w.document.write(`<tr class="total"><td>${escapeHtml(t('pdf.totalPrice'))}</td><td>${formatEuro(q.total_incl_vat)}</td></tr>`);
      w.document.write(`<tr class="vat"><td colspan="2" style="text-align:right;font-style:italic">${escapeHtml(t('pdf.vatIncluded'))}</td></tr>`);
    }
    w.document.write(`</tbody></table>`);

    // Standard conditions
    w.document.write(`
<div class="conditions">
  <h4>${escapeHtml(t('pdf.conditions'))}</h4>
  <ul>
    <li>${escapeHtml(t('pdf.cond1'))}</li>
    <li>${escapeHtml(t('pdf.cond2'))}</li>
    <li>${escapeHtml(t('pdf.cond3'))}</li>
    <li>${escapeHtml(t('pdf.cond4'))}</li>
  </ul>
</div>`);

    // Footer — repeated company info for legal completeness
    w.document.write(`
<div class="footer">
  ${escapeHtml(company.name)}${company.address ? ' &mdash; ' + escapeHtml(company.address) : ''}${company.city ? ', ' + escapeHtml(company.postalCode) + ' ' + escapeHtml(company.city) : ''}${company.btw ? ' &mdash; BTW ' + escapeHtml(company.btw) : ''}<br/>
  ${escapeHtml(company.email)}${company.phone ? ' &nbsp;|&nbsp; ' + escapeHtml(company.phone) : ''}
  ${isResellerRouted ? `<br/><span style="color:#aaa;font-size:8px">${escapeHtml(t('pdf.installerNote'))}</span>` : ''}
</div>`);

    w.document.write(`</body></html>`);
    w.document.close();
    w.print();
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-[#1a1a1a] flex items-center gap-2">
            <FileText size={18} className="text-[#c47a3a]" /> {t('admin.testQuotes.title')}
            <span className="px-2 py-0.5 bg-[#c47a3a]/10 text-[#c47a3a] text-xs font-semibold rounded-full">{quotes.length}</span>
          </h2>
          <p className="text-xs text-[#8a7e74] mt-1">
            {t('admin.testQuotes.desc')}
          </p>
        </div>
        <div className="px-4 sm:px-6 pb-6 pt-2">
          {/* Quick-create test quote button */}
          <div className="mb-4 p-4 bg-[#faf7f3] rounded-lg border border-dashed border-[#c47a3a]/30">
            <p className="text-sm text-[#4a4a4a] mb-2">{t('admin.testQuotes.goToPrefix')}<a href="/offerte" className="text-[#c47a3a] font-medium hover:underline">{t('admin.testQuotes.goToQuotePage')}</a>{t('admin.testQuotes.goToSuffix')}</p>
            <p className="text-xs text-[#8a7e74]">{t('admin.testQuotes.autoAppear')}</p>
          </div>

          {quotes.length === 0 ? (
            <p className="text-sm text-[#8a7e74] py-4">{t('admin.testQuotes.noQuotes')}</p>
          ) : (
            <>
              {/* Mobile cards */}
              <div className="sm:hidden space-y-3">
                {quotes.map(q => (
                  <div key={q.id} className="border border-gray-100 rounded-lg p-4">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div>
                        <p className="font-medium text-[#1a1a1a] text-base">{q.customer_name}</p>
                        <p className="text-sm text-[#8a7e74]">{q.customer_email}</p>
                      </div>
                      <StatusBadge status={q.status} />
                    </div>
                    <div className="grid grid-cols-2 gap-y-1.5 text-sm mb-3">
                      <span className="text-[#8a7e74]">{t('admin.testQuotes.items')}</span>
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs font-bold w-fit">{q.items_count}</span>
                      <span className="text-[#8a7e74]">{t('admin.testQuotes.exclVat')}</span>
                      <span className="text-[#4a4a4a]">{formatEuro(q.total_excl_vat)}</span>
                      <span className="text-[#8a7e74]">{t('admin.testQuotes.inclVat')}</span>
                      <span className="font-medium text-[#1a1a1a]">{formatEuro(q.total_incl_vat)}</span>
                      <span className="text-[#8a7e74]">{t('admin.testQuotes.date')}</span>
                      <span className="text-[#8a7e74] text-xs">{formatDate(q.created_at)}</span>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[#8a7e74] uppercase tracking-wide mb-1">{t('admin.testQuotes.changeStatus')}</label>
                      <select
                        className="w-full px-3 py-3 border border-gray-200 rounded-lg text-sm focus:border-[#c47a3a] focus:outline-none disabled:opacity-50 min-h-[44px]"
                        value={q.status}
                        disabled={updatingId === q.id}
                        onChange={e => updateStatus(q.id, e.target.value)}
                      >
                        {statuses.map(s => (
                          <option key={s} value={s}>{statusLabel(s)}</option>
                        ))}
                      </select>
                    </div>
                    <div className="mt-3 flex gap-2">
                      <button
                        className="flex-1 px-3 py-3 text-sm font-medium bg-[#c47a3a] text-white rounded-lg hover:bg-[#a8643a] transition-colors min-h-[44px]"
                        onClick={() => printQuotePDF(q)}
                      >
                        PDF
                      </button>
                      {q.status === 'draft' && (
                        <button
                          className="px-3 py-3 text-sm font-medium bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 transition-colors min-h-[44px] disabled:opacity-50 flex items-center justify-center gap-1.5"
                          onClick={() => deleteQuote(q)}
                          disabled={deletingId === q.id}
                          aria-label={t('admin.testQuotes.delete')}
                          title={t('admin.testQuotes.delete')}
                        >
                          <Trash2 size={16} />
                          <span>{t('admin.testQuotes.delete')}</span>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      {[t('admin.testQuotes.client'), t('admin.testQuotes.email'), t('admin.testQuotes.status'), t('admin.testQuotes.items'), t('admin.testQuotes.exclVat'), t('admin.testQuotes.inclVat'), t('admin.testQuotes.date'), t('admin.testQuotes.action'), ''].map(h => (
                        <th key={h} className="text-left py-2 pr-4 text-xs font-semibold text-[#8a7e74] uppercase tracking-wide whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {quotes.map(q => (
                      <tr key={q.id} className="border-b border-gray-50 hover:bg-[#faf7f3] transition-colors">
                        <td className="py-2.5 pr-4 font-medium text-[#1a1a1a]">{q.customer_name}</td>
                        <td className="py-2.5 pr-4 text-[#8a7e74]">{q.customer_email}</td>
                        <td className="py-2.5 pr-4"><StatusBadge status={q.status} /></td>
                        <td className="py-2.5 pr-4">
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs font-bold">{q.items_count}</span>
                        </td>
                        <td className="py-2.5 pr-4 text-[#4a4a4a]">{formatEuro(q.total_excl_vat)}</td>
                        <td className="py-2.5 pr-4 font-medium text-[#1a1a1a]">{formatEuro(q.total_incl_vat)}</td>
                        <td className="py-2.5 pr-4 text-[#8a7e74] text-xs whitespace-nowrap">{formatDate(q.created_at)}</td>
                        <td className="py-2.5">
                          <select
                            className="px-2 py-2 border border-gray-200 rounded-lg text-xs focus:border-[#c47a3a] focus:outline-none disabled:opacity-50 min-h-[44px]"
                            value={q.status}
                            disabled={updatingId === q.id}
                            onChange={e => updateStatus(q.id, e.target.value)}
                          >
                            {statuses.map(s => (
                              <option key={s} value={s}>{statusLabel(s)}</option>
                            ))}
                          </select>
                        </td>
                        <td className="py-2.5">
                          <div className="flex items-center gap-1.5">
                            <button
                              className="px-2 py-1.5 text-xs font-medium bg-[#c47a3a] text-white rounded hover:bg-[#a8643a] transition-colors"
                              onClick={() => printQuotePDF(q)}
                            >
                              PDF
                            </button>
                            {q.status === 'draft' && (
                              <button
                                className="p-1.5 text-red-700 bg-red-50 border border-red-200 rounded hover:bg-red-100 transition-colors disabled:opacity-50"
                                onClick={() => deleteQuote(q)}
                                disabled={deletingId === q.id}
                                aria-label={t('admin.testQuotes.delete')}
                                title={t('admin.testQuotes.delete')}
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Audit log info bar */}
      <div className="flex items-start gap-2 p-4 bg-amber-50 rounded-xl border border-amber-100">
        <Info size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-800">
          {t('admin.testQuotes.archiveNote')}
        </p>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MEDE-EIGENAAR SECTION — verwijderd in v3.2.31 op verzoek van Filip.
// Filip + Bicek zijn beide owner met dezelfde rechten en zien dezelfde data;
// een apart blok dat dat herhaalt vulde plaats in zonder waarde toe te voegen.
// ═══════════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════════
// PRIJZEN TAB — mobile: card layout
// ═══════════════════════════════════════════════════════════════════════════════

// ════════════════════════════════════════════════════════════════════════════
// v5 blok 8 — BESTELLINGEN & FACTUREN (in Klanten & Offertes-tab)
// ════════════════════════════════════════════════════════════════════════════

function OrdersInvoicesPanel() {
  const [ordersList, setOrdersList] = useState<import('../lib/api').AdminOrder[]>([]);
  const [invoices, setInvoices] = useState<import('../lib/api').AdminInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const api = await import('../lib/api');
      const [o, i] = await Promise.all([api.adminGetOrders(), api.adminGetInvoices()]);
      setOrdersList(o);
      setInvoices(i);
    } catch { /* stil — paneel toont leeg */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const patchOrder = async (id: string, data: Record<string, unknown>) => {
    setBusy(id);
    try {
      const { adminUpdateOrder } = await import('../lib/api');
      await adminUpdateOrder(id, data);
      await load();
    } catch (e) { alert((e as Error).message); } finally { setBusy(null); }
  };

  const makeInvoice = async (orderId: string) => {
    setBusy(orderId);
    try {
      const { adminCreateInvoice } = await import('../lib/api');
      const inv = await adminCreateInvoice(orderId);
      await load();
      alert(`Factuur ${new Date(inv.created_at * 1000).getFullYear()}-${String(inv.invoice_number).padStart(4, '0')} aangemaakt. Download via de Facturen-lijst hieronder.`);
    } catch (e) { alert((e as Error).message); } finally { setBusy(null); }
  };

  const downloadInvoice = async (inv: import('../lib/api').AdminInvoice) => {
    const win = window.open('', '_blank');
    const { printInvoicePDF } = await import('../lib/invoicePdf');
    printInvoicePDF(inv, { targetWindow: win, orderNumber: inv.order_number ?? null, company: uitgeverVanFactuur(inv) });
  };

  const termsLabel: Record<string, string> = { vooraf: 'Vooraf (particulier)', '30_dagen': '30 dagen', einde_maand: 'Einde maand' };

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="px-4 sm:px-6 py-4 border-b border-gray-100">
        <h2 className="text-lg font-bold text-[#1a1a1a] flex items-center gap-2">
          <FileText size={18} className="text-[#c47a3a]" /> Bestellingen & Facturen
          <span className="px-2 py-0.5 bg-[#c47a3a]/10 text-[#c47a3a] text-xs font-semibold rounded-full">{ordersList.length} · {invoices.length}</span>
        </h2>
        <p className="text-xs text-[#8a7e74] mt-0.5">Zet offertes om via de "→ Bestelling"-knop hierboven. Particulier betaalt vóór levering; handelaars op termijn.</p>
      </div>
      <div className="px-4 sm:px-6 py-4 space-y-5">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-[#8a7e74] py-2"><Loader2 className="animate-spin" size={14} /> Laden…</div>
        ) : (
          <>
            {ordersList.length === 0 ? (
              <p className="text-sm text-[#8a7e74]">Nog geen bestellingen.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-gray-100">
                    {['#', 'Klant', 'Bedrag incl.', 'Voorwaarde', 'Betaling', 'Status', 'Factuur'].map((h, i) => (
                      <th key={i} className="text-left py-2 pr-4 text-xs font-semibold text-[#8a7e74] uppercase tracking-wide">{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {ordersList.map(o => (
                      <tr key={o.id} className="border-b border-gray-50">
                        <td className="py-2 pr-4 font-mono text-xs">#{o.order_number}{o.quote_number ? <span className="text-[#8a7e74]"> (off. {o.quote_number})</span> : ''}</td>
                        <td className="py-2 pr-4">{o.customer_name}<div className="text-xs text-[#8a7e74]">{o.customer_email}</div></td>
                        <td className="py-2 pr-4 font-semibold">{formatEuro(o.total_incl_vat)}</td>
                        <td className="py-2 pr-4">
                          <select value={o.payment_terms} disabled={busy === o.id} onChange={e => void patchOrder(o.id, { payment_terms: e.target.value })}
                            className="px-2 py-1 border border-gray-200 rounded text-xs">
                            {Object.entries(termsLabel).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                          </select>
                        </td>
                        <td className="py-2 pr-4">
                          <button
                            onClick={() => void patchOrder(o.id, { payment_status: o.payment_status === 'betaald' ? 'open' : 'betaald' })}
                            disabled={busy === o.id}
                            className={`px-2 py-1 rounded text-xs font-bold ${o.payment_status === 'betaald' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-800'}`}
                            title="Klik om betaalstatus te wisselen"
                          >
                            {o.payment_status === 'betaald' ? '✓ Betaald' : 'Open'}
                          </button>
                        </td>
                        <td className="py-2 pr-4">
                          <select value={o.status} disabled={busy === o.id} onChange={e => void patchOrder(o.id, { status: e.target.value })}
                            className="px-2 py-1 border border-gray-200 rounded text-xs">
                            {['nieuw', 'bevestigd', 'geleverd', 'geannuleerd'].map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </td>
                        <td className="py-2">
                          {(o.invoice_count ?? 0) > 0 ? (
                            <span className="text-xs text-green-700 font-semibold">✓</span>
                          ) : (
                            <button onClick={() => void makeInvoice(o.id)} disabled={busy === o.id}
                              className="px-2 py-1 bg-[#c47a3a] hover:bg-[#a8643a] text-white rounded text-xs font-bold disabled:opacity-50">
                              Maak factuur
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {invoices.length > 0 && (
              <div>
                <h3 className="text-xs font-bold text-[#8a7e74] uppercase tracking-wide mb-2">Facturen ({invoices.length})</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-100">
                      {['Nummer', 'Klant', 'Bedrag incl.', 'Vervaldatum', 'Status', ''].map((h, i) => (
                        <th key={i} className="text-left py-2 pr-4 text-xs font-semibold text-[#8a7e74] uppercase tracking-wide">{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {invoices.map(inv => (
                        <tr key={inv.id} className="border-b border-gray-50">
                          <td className="py-2 pr-4 font-mono text-xs">FAC-{new Date(inv.created_at * 1000).getFullYear()}-{String(inv.invoice_number).padStart(4, '0')}</td>
                          <td className="py-2 pr-4">{inv.customer_name}</td>
                          <td className="py-2 pr-4 font-semibold">{formatEuro(inv.total_incl_vat)}</td>
                          <td className="py-2 pr-4 text-xs">{formatDate(inv.due_date)}</td>
                          <td className="py-2 pr-4">
                            <select value={inv.status} onChange={async e => {
                              const { adminSetInvoiceStatus } = await import('../lib/api');
                              await adminSetInvoiceStatus(inv.id, e.target.value as 'open' | 'betaald' | 'vervallen');
                              await load();
                            }} className="px-2 py-1 border border-gray-200 rounded text-xs">
                              {['open', 'betaald', 'vervallen'].map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                          </td>
                          <td className="py-2">
                            <div className="flex gap-1.5">
                              <button onClick={() => void downloadInvoice(inv)}
                                className="px-2 py-1 bg-[#c47a3a] hover:bg-[#a8643a] text-white rounded text-xs font-bold">PDF</button>
                              {/* V6 punt 25: creditnota ook vanuit dit overzicht */}
                              {(inv as { doc_type?: string }).doc_type !== 'creditnota' && (
                                <button onClick={async () => {
                                  if (!confirm(`Creditnota maken voor factuur ${inv.invoice_number} van ${inv.customer_name}? De factuur blijft wettelijk bewaard; de creditnota vergoedt het volledige bedrag.`)) return;
                                  const { adminCreateCreditNote } = await import('../lib/api');
                                  await adminCreateCreditNote(inv.id);
                                  await load();
                                }} className="px-2 py-1 bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 rounded text-xs font-bold">Creditnota</button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// [v5 blok 2] PrijzenTab verwijderd — ResellerPricingPanel leeft nu in het
// Resellers-vak (zie ResellersTab), zoals Filip vroeg: vier hoofdtabs.

// ════════════════════════════════════════════════════════════════════════════
// RESELLERS TAB — owner manages doorverkoopbedrijven
// ════════════════════════════════════════════════════════════════════════════

/** V5.31: uitnodigingen + binnengekomen aanvragen. Filip vraagt aan de
 *  telefoon een e-mailadres, maakt hier de link, stuurt die door (kopieer of
 *  mail), en keurt de ingevulde aanvraag daarna goed — pas dan ontstaat het
 *  echte reseller-account. */
function ResellerInvitePanel({ onChanged }: { onChanged?: () => void }) {
  const [email, setEmail] = useState('');
  const [type, setType] = useState('kleinhandel');
  const [busy, setBusy] = useState(false);
  const [lastLink, setLastLink] = useState<string | null>(null);
  const [apps, setApps] = useState<import('../lib/api').ResellerApplication[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [approving, setApproving] = useState<string | null>(null);
  const [apprPct, setApprPct] = useState('20');
  const [apprPw, setApprPw] = useState('');

  const loadApps = async () => {
    try {
      const api = await import('../lib/api');
      setApps(await api.adminGetResellerApplications());
    } catch (e) { setError((e as Error).message); }
  };
  useEffect(() => { void loadApps(); }, []);

  const makeInvite = async () => {
    if (!email.trim()) return;
    setBusy(true); setError(null);
    try {
      const api = await import('../lib/api');
      const inv = await api.adminCreateResellerInvite(email.trim().toLowerCase(), type);
      const link = `${window.location.origin}${inv.path}`;
      setLastLink(link);
      try { await navigator.clipboard.writeText(link); } catch { /* stil */ }
      setEmail('');
      void loadApps();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const approve = async (id: string, appType: string | null) => {
    setBusy(true); setError(null);
    try {
      const api = await import('../lib/api');
      await api.adminApproveResellerApplication(id, {
        default_discount_pct: Number(apprPct) || 0,
        type: (appType ?? 'kleinhandel'),
        ...(apprPw.trim().length >= 8 ? { initial_password: apprPw.trim() } : {}),
      });
      setApproving(null); setApprPw('');
      void loadApps(); onChanged?.();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const STATUS_NL: Record<string, { label: string; cls: string }> = {
    open:      { label: 'Link verstuurd — nog niet ingevuld', cls: 'bg-gray-100 text-gray-600' },
    submitted: { label: 'Ingediend — wacht op jouw goedkeuring', cls: 'bg-amber-100 text-amber-800' },
    approved:  { label: 'Goedgekeurd', cls: 'bg-green-100 text-green-700' },
    rejected:  { label: 'Afgewezen', cls: 'bg-red-100 text-red-700' },
  };

  return (
    <div className="mb-4 rounded-xl border border-[#e6ded4] bg-[#faf7f3] p-4">
      <p className="text-sm font-bold text-[#1a1a1a] mb-1">Nieuwe handelaar uitnodigen</p>
      <p className="text-xs text-[#8a7e74] mb-3">
        Vraag een e-mailadres, maak de link en stuur die door. De handelaar vult zelf al zijn
        bedrijfsgegevens in (met BTW-controle). Jij keurt goed en bepaalt de korting — pas dan bestaat het account.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="email" value={email} onChange={e => setEmail(e.target.value)}
          placeholder="e-mailadres van de handelaar"
          className="flex-1 min-w-[220px] px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white"
        />
        <select value={type} onChange={e => setType(e.target.value)} className="px-2 py-2 border border-gray-200 rounded-lg text-sm bg-white">
          <option value="groothandel">Groothandel</option>
          <option value="kleinhandel">Kleinhandel</option>
          <option value="bouwonderneming">Bouwonderneming</option>
          <option value="installateur">Installateur</option>
        </select>
        <button onClick={() => void makeInvite()} disabled={busy || !email.trim()}
          className="px-3 py-2 bg-[#c47a3a] text-white rounded-lg text-sm font-semibold disabled:opacity-50">
          Maak uitnodigingslink
        </button>
      </div>
      {lastLink && (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs bg-white border border-green-200 rounded-lg px-3 py-2">
          <span className="text-green-700 font-semibold">Link gekopieerd:</span>
          <code className="text-[#4a4a4a] break-all">{lastLink}</code>
          <a className="text-[#c47a3a] font-semibold underline"
            href={`mailto:?subject=${encodeURIComponent('Uitnodiging handelaarsaccount — Gevelsteenbedrijf')}&body=${encodeURIComponent('Beste,\n\nVia deze link kan je jouw bedrijfsgegevens invullen voor een handelaarsaccount:\n' + lastLink + '\n\nNa goedkeuring krijg je toegang tot jouw werkplek.\n\nMet vriendelijke groeten,\nFilip')}`}>
            Mail versturen
          </a>
        </div>
      )}
      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}

      {apps && apps.length > 0 && (
        <div className="mt-4 space-y-2">
          <p className="text-xs font-bold text-[#8a7e74] uppercase tracking-wide">Uitnodigingen & aanvragen</p>
          {apps.map(a => (
            <div key={a.id} className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-[#1a1a1a]">{a.company_name ?? a.email}</span>
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_NL[a.status]?.cls ?? ''}`}>{STATUS_NL[a.status]?.label ?? a.status}</span>
                {a.btw_number && (
                  <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${a.btw_valid ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                    BTW {a.btw_number} {a.btw_valid ? '✓' : '✗ controlecijfer klopt niet'}
                  </span>
                )}
              </div>
              {a.status === 'submitted' && (
                <div className="mt-1 text-xs text-[#4a4a4a]">
                  {a.contact_name} · {a.contact_phone} · {a.postal_code} {a.city}
                  {a.website ? <> · {a.website}</> : null}
                  {a.notes ? <div className="text-[#8a7e74] mt-0.5">{a.notes}</div> : null}
                </div>
              )}
              {a.status === 'open' && (
                <div className="mt-1 text-xs text-[#8a7e74]">
                  {a.email} — <button className="text-[#c47a3a] underline" onClick={() => { void navigator.clipboard.writeText(`${window.location.origin}/reseller-aanvraag?t=${a.token}`); }}>kopieer de link opnieuw</button>
                </div>
              )}
              {a.status === 'submitted' && (
                approving === a.id ? (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <label className="text-xs text-[#8a7e74]">Korting</label>
                    <input type="number" min={0} max={60} value={apprPct} onChange={e => setApprPct(e.target.value)} className="w-16 px-2 py-1 border border-gray-200 rounded text-sm" />%
                    <input type="text" value={apprPw} onChange={e => setApprPw(e.target.value)} placeholder="startpaswoord (min. 8)" className="px-2 py-1 border border-gray-200 rounded text-sm" />
                    <button onClick={() => void approve(a.id, a.type)} disabled={busy} className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-semibold disabled:opacity-50">Definitief goedkeuren</button>
                    <button onClick={() => setApproving(null)} className="px-2 py-1.5 text-xs text-[#8a7e74]">Annuleer</button>
                  </div>
                ) : (
                  <div className="mt-2 flex gap-2">
                    <button onClick={() => setApproving(a.id)} className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-semibold">Goedkeuren…</button>
                    <button onClick={async () => { const api = await import('../lib/api'); await api.adminRejectResellerApplication(a.id); void loadApps(); }} className="px-3 py-1.5 bg-red-50 text-red-700 rounded-lg text-xs font-semibold">Afwijzen</button>
                  </div>
                )
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ResellersTab({ initialItems, pricing, onRefresh }: {
  initialItems?: import('../lib/api').AdminReseller[];
  pricing?: AdminPricingRow[];
  onRefresh?: () => void;
}) {
  const { t } = useLanguage();
  // v3.2.72 — start meteen met de data van de parent (loadFullData) zodat
  // er geen loading-flicker is bij tab-switch. Wanneer je expliciet 'load'
  // aanroept (na save/delete) re-fetchen we voor verse data.
  const [items, setItems] = useState<import('../lib/api').AdminReseller[]>(initialItems ?? []);
  const [loading, setLoading] = useState(!initialItems || initialItems.length === 0);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<import('../lib/api').AdminReseller | null>(null);
  const [creating, setCreating] = useState(false);
  // v5 blok 2: filter op handelstype (groothandel / kleinhandel / bouwonderneming)
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  // V5.31: focusmodus — klik op een rij toont alléén dat bedrijf (privacy
  // wanneer Filip bij een handelaar aan tafel zit) + opent het dossier.
  const [focusId, setFocusId] = useState<string | null>(null);
  const [showInvite, setShowInvite] = useState(false);
  const shownItems = focusId
    ? items.filter(r => r.id === focusId)
    : (typeFilter ? items.filter(r => (r.type ?? 'kleinhandel') === typeFilter) : items);
  // v5.18: duidelijke vakken per handelstype — gesorteerd op type, met een
  // gekleurde tussenkop per groep (groothandel blauw, kleinhandel amber,
  // bouwonderneming groen). Zo ziet Filip in één blik wie wat is.
  const TYPE_ORDER = ['groothandel', 'kleinhandel', 'bouwonderneming', 'installateur'];
  const TYPE_STYLE: Record<string, { label: string; cls: string }> = {
    groothandel:     { label: 'Groothandel',                 cls: 'bg-blue-600 text-white' },
    installateur:    { label: 'Installateur',                cls: 'bg-violet-600 text-white' },
    kleinhandel:     { label: 'Kleinhandel',                 cls: 'bg-amber-600 text-white' },
    bouwonderneming: { label: 'Bouwondernemingen / aannemers', cls: 'bg-emerald-600 text-white' },
  };
  const sortedResellers = [...shownItems].sort((a, b) =>
    TYPE_ORDER.indexOf(a.type ?? 'kleinhandel') - TYPE_ORDER.indexOf(b.type ?? 'kleinhandel')
    || a.company_name.localeCompare(b.company_name));

  // Sync wanneer parent-data ververst (bv. na een loadFullData refresh).
  useEffect(() => {
    if (initialItems && initialItems.length > 0) {
      setItems(initialItems);
      setLoading(false);
    }
  }, [initialItems]);

  const load = async () => {
    setLoading(true);
    try {
      const { adminGetResellers } = await import('../lib/api');
      setItems(await adminGetResellers());
      // Trigger ook parent-refresh zodat resellersCount en stats up-to-date zijn.
      onRefresh?.();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };
  // Geen useEffect met initial load — initialItems van parent volstaat.

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="px-4 sm:px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <h2 className="text-lg font-bold text-[#1a1a1a] flex items-center gap-2">
          <Users size={18} className="text-[#c47a3a]" /> {t('admin.resellers.title')}
          <span className="px-2 py-0.5 bg-[#c47a3a]/10 text-[#c47a3a] text-xs font-semibold rounded-full">{items.length}</span>
        </h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowInvite(v => !v)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-white border border-[#c47a3a] text-[#c47a3a] rounded-lg hover:bg-[#c47a3a]/10 transition-colors"
            title="Stuur een link waarmee een handelaar zichzelf invult; jij keurt daarna goed"
          >
            <Mail size={14} /> Uitnodigen
          </button>
          <button
            onClick={() => setCreating(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#c47a3a] text-white rounded-lg hover:bg-[#a8643a] transition-colors"
          >
            <Plus size={14} /> {t('admin.resellers.add')}
          </button>
        </div>
      </div>

      <div className="px-4 sm:px-6 py-4">
        {showInvite && <ResellerInvitePanel onChanged={() => void load()} />}
        {focusId && (
          <div className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
            <span>Je ziet alleen <b>{items.find(r => r.id === focusId)?.company_name ?? 'dit bedrijf'}</b> — de andere doorverkoopbedrijven zijn verborgen.</span>
            <button onClick={() => setFocusId(null)} className="flex-shrink-0 px-3 py-1 rounded-lg bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700">
              Toon alle bedrijven ({items.length})
            </button>
          </div>
        )}
        {/* v5 blok 2: onderverdeling per handelstype */}
        <div className="mb-3 flex flex-wrap items-center gap-1.5">
          {([['alle', null], ['groothandel', 'groothandel'], ['kleinhandel', 'kleinhandel'], ['bouwonderneming', 'bouwonderneming'], ['installateur', 'installateur']] as const).map(([label, val]) => (
            <button
              key={label}
              onClick={() => setTypeFilter(val)}
              className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize transition-colors ${
                typeFilter === val ? 'bg-[#c47a3a] text-white' : 'bg-gray-100 text-[#4a4a4a] hover:bg-gray-200'
              }`}
            >
              {label}{val ? ` (${items.filter(r => (r.type ?? 'kleinhandel') === val).length})` : ` (${items.length})`}
            </button>
          ))}
        </div>
        {error && <div className="mb-3 p-2 bg-red-50 text-red-700 text-sm rounded">{error}</div>}
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-[#8a7e74] py-4">
            <Loader2 className="animate-spin" size={14} /> {t('admin.loading')}
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-[#8a7e74] py-4">{t('admin.resellers.empty')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  {['', t('admin.resellers.company'), 'Type', t('admin.resellers.city'), t('admin.resellers.discount'), t('admin.resellers.postcodes'), t('admin.resellers.customers'), t('admin.resellers.quotes'), t('admin.resellers.status'), 'Bijgewerkt', ''].map((h, i) => (
                    <th key={i} className="text-left py-2 pr-4 text-xs font-semibold text-[#8a7e74] uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedResellers.map((r, idx) => (
                  <React.Fragment key={r.id}>
                  {(idx === 0 || (sortedResellers[idx - 1].type ?? 'kleinhandel') !== (r.type ?? 'kleinhandel')) && (
                    <tr>
                      <td colSpan={11} className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest ${TYPE_STYLE[r.type ?? 'kleinhandel'].cls}`}>
                        {TYPE_STYLE[r.type ?? 'kleinhandel'].label} ({items.filter(x => (x.type ?? 'kleinhandel') === (r.type ?? 'kleinhandel')).length})
                      </td>
                    </tr>
                  )}
                  <tr
                    className="border-b border-gray-50 hover:bg-[#faf7f3] transition-colors cursor-pointer"
                    onClick={() => { setFocusId(r.id); setEditing(r); }}
                    title="Klik om het dossier van dit bedrijf te openen (de andere bedrijven worden verborgen)"
                  >
                    <td className="py-2.5 pr-2 w-12">
                      {r.logo_url ? (
                        <img src={r.logo_url} alt={r.company_name} className="h-8 w-auto max-w-[60px]" />
                      ) : (
                        <div className="h-8 w-8 bg-gray-100 rounded text-[10px] flex items-center justify-center text-gray-500">—</div>
                      )}
                    </td>
                    <td className="py-2.5 pr-4 font-medium text-[#1a1a1a]">
                      <div className="flex items-center gap-2">
                        {r.company_name}
                        {r.is_demo === 1 && (
                          <span
                            className="px-1.5 py-0.5 bg-purple-100 text-purple-800 border border-purple-300 rounded text-[9px] font-bold uppercase tracking-wide"
                            title="Demo voorbeeld — wordt nooit gekozen voor echte offertes en kan niet verwijderd worden"
                          >
                            DEMO
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-[#8a7e74]">{r.user_email}</div>
                    </td>
                    {/* v5 blok 2: handelstype-badge */}
                    <td className="py-2.5 pr-4">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border ${
                        r.type === 'groothandel' ? 'bg-blue-50 text-blue-700 border-blue-300' :
                        r.type === 'bouwonderneming' ? 'bg-emerald-50 text-emerald-700 border-emerald-300' :
                        r.type === 'installateur' ? 'bg-violet-50 text-violet-700 border-violet-300' :
                        'bg-amber-50 text-amber-800 border-amber-300'
                      }`}>{r.type ?? 'kleinhandel'}</span>
                      {r.type !== 'installateur' && !!r.is_installateur && (
                        <span className="ml-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-violet-50 text-violet-700 border border-violet-300">plaatst ook</span>
                      )}
                    </td>
                    <td className="py-2.5 pr-4 text-[#8a7e74]">{r.city ?? '—'}</td>
                    <td className="py-2.5 pr-4">
                      <span className="px-2 py-0.5 bg-amber-50 text-amber-800 rounded text-xs font-medium">{r.default_discount_pct}%</span>
                    </td>
                    <td className="py-2.5 pr-4 text-xs text-[#8a7e74] max-w-[200px] truncate" title={r.postcodes ?? ''}>{r.postcodes ?? '—'}</td>
                    <td className="py-2.5 pr-4 text-[#4a4a4a]">{r.customer_count ?? 0}</td>
                    <td className="py-2.5 pr-4 text-[#4a4a4a]">{r.quote_count ?? 0}</td>
                    <td className="py-2.5 pr-4">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${r.is_active ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {r.is_active ? t('admin.resellers.active') : t('admin.resellers.inactive')}
                      </span>
                    </td>
                    {/* Laatst bijgewerkt — toont relatief 'X dagen geleden' wanneer
                         recent, anders datum. Helpt Filip om te zien welke
                         resellers lang niet meer zijn aangepast. (v3.2.55) */}
                    <td className="py-2.5 pr-4 text-xs text-[#8a7e74] whitespace-nowrap" title={new Date(r.updated_at * 1000).toLocaleString('nl-BE')}>
                      {(() => {
                        const days = Math.floor((Date.now() / 1000 - r.updated_at) / 86400);
                        if (days < 1) return 'vandaag';
                        if (days < 7) return `${days}d geleden`;
                        if (days < 30) return `${Math.floor(days / 7)}w geleden`;
                        return new Date(r.updated_at * 1000).toLocaleDateString('nl-BE', { day: '2-digit', month: 'short', year: 'numeric' });
                      })()}
                    </td>
                    <td className="py-2.5">
                      <button onClick={(e) => { e.stopPropagation(); setFocusId(r.id); setEditing(r); }} className="p-1.5 text-[#c47a3a] hover:bg-[#c47a3a]/10 rounded transition-colors" title={t('admin.resellers.edit')}>
                        <Edit3 size={14} />
                      </button>
                    </td>
                  </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* v5 blok 2: de vroegere "Kortingen Resellers"-tab leeft nu hier —
           prijsafspraken per reseller, direct onder de reseller-lijst. */}
      {pricing && pricing.length > 0 && (
        <div className="px-4 sm:px-6 pb-4">
          <ResellerPricingPanel pricing={pricing} onRefresh={onRefresh} focusResellerId={focusId} />
        </div>
      )}

      {(editing || creating) && (
        <ResellerEditModal
          reseller={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); void load(); }}
        />
      )}
    </div>
  );
}

/** v5 blok 2: compacte lijst van offertes/klanten gekoppeld aan één reseller,
 *  getoond in het reseller-detail zodat Filip alles op één plek ziet. */
function ResellerQuotesList({ resellerId }: { resellerId: string }) {
  const [quotes, setQuotes] = useState<import('../lib/api').AdminQuote[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    adminGetQuotes()
      .then(all => { if (!cancelled) setQuotes(all.filter(q => q.reseller_id === resellerId)); })
      .catch(() => { if (!cancelled) setQuotes([]); });
    return () => { cancelled = true; };
  }, [resellerId]);
  if (quotes === null) return <p className="text-xs text-[#8a7e74]">Offertes laden…</p>;
  return (
    <div className="border border-gray-100 rounded-lg p-3 bg-[#faf7f3]/50">
      <p className="text-xs font-bold text-[#8a7e74] uppercase tracking-wide mb-2">
        Klanten & offertes van deze reseller ({quotes.length})
      </p>
      {quotes.length === 0 ? (
        <p className="text-xs text-[#8a7e74]">Nog geen offertes aan deze reseller gekoppeld.</p>
      ) : (
        <ul className="space-y-1 max-h-40 overflow-y-auto">
          {quotes.map(q => (
            <li key={q.id} className="flex items-center justify-between text-xs">
              <span className="truncate text-[#1a1a1a]">{q.customer_name} <span className="text-[#8a7e74]">({q.customer_email})</span></span>
              <span className="flex items-center gap-2 flex-shrink-0 ml-2">
                <StatusBadge status={q.status} />
                <span className="font-semibold">{formatEuro(q.total_incl_vat)}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** V5.31: subaccounts (medewerkers) van een reseller-bedrijf. Alleen Filip
 *  maakt ze aan of zet ze uit, zodat hij het overzicht houdt over wie kan
 *  inloggen. Een subaccount logt in met paswoord en werkt in dezelfde
 *  werkplek als de hoofdaccount, maar is duidelijk herkenbaar. */
// V6 punt 21 — vestigingen van een groothandel (adres per vestiging; offertes
// gaan automatisch naar de dichtstbijzijnde; subaccounts hangen aan een vestiging).
function VestigingenPanel({ resellerId }: { resellerId: string }) {
  const [rows, setRows] = useState<import('../lib/api').ResellerVestiging[] | null>(null);
  const [naam, setNaam] = useState('');
  const [pc, setPc] = useState('');
  const [stad, setStad] = useState('');
  const [mail, setMail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      const api = await import('../lib/api');
      setRows(await api.adminGetVestigingen(resellerId));
    } catch (e) { setError((e as Error).message); }
  };
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [resellerId]);

  const add = async () => {
    if (naam.trim().length < 2) { setError('Geef de vestiging een naam.'); return; }
    setBusy(true); setError(null);
    try {
      const api = await import('../lib/api');
      await api.adminCreateVestiging(resellerId, {
        naam: naam.trim(), postal_code: pc.trim() || null,
        city: stad.trim() || null, contact_email: mail.trim() || null,
      });
      setNaam(''); setPc(''); setStad(''); setMail('');
      void load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const toggle = async (v: import('../lib/api').ResellerVestiging) => {
    const api = await import('../lib/api');
    await api.adminUpdateVestiging(v.id, { is_active: v.is_active ? 0 : 1 });
    void load();
  };

  return (
    <div className="border border-gray-100 rounded-lg p-3 bg-teal-50/40 mb-3">
      <p className="text-xs font-bold text-teal-800 uppercase tracking-wide mb-2">
        Vestigingen — offertes gaan automatisch naar de dichtstbijzijnde
      </p>
      {rows === null ? (
        <p className="text-xs text-[#8a7e74]">Laden…</p>
      ) : rows.length === 0 ? (
        <p className="text-xs text-[#8a7e74] mb-2">Geen aparte vestigingen — alles loopt via het hoofdkantoor.</p>
      ) : (
        <ul className="space-y-1 mb-2">
          {rows.map(v => (
            <li key={v.id} className="flex items-center justify-between text-xs bg-white border border-gray-100 rounded px-2 py-1.5">
              <span>
                <span className="px-1.5 py-0.5 rounded bg-teal-100 text-teal-800 border border-teal-300 text-[10px] font-bold uppercase mr-1.5">Vestiging</span>
                <b>{v.naam}</b>
                <span className="text-[#8a7e74]"> {v.postal_code ?? ''} {v.city ?? ''}{v.contact_email ? ` · ${v.contact_email}` : ''}
                  {' '}· {v.member_count ?? 0} medew. · {v.quote_count ?? 0} offertes</span>
              </span>
              <button onClick={() => void toggle(v)}
                className={`px-2 py-1 rounded text-[11px] font-semibold ${v.is_active ? 'bg-green-50 text-green-700 hover:bg-red-50 hover:text-red-700' : 'bg-gray-100 text-gray-500 hover:bg-green-50 hover:text-green-700'}`}>
                {v.is_active ? 'Actief' : 'Inactief'}
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap gap-2">
        <input value={naam} onChange={e => setNaam(e.target.value)} placeholder="naam vestiging" className="flex-1 min-w-[140px] px-2 py-1.5 border border-gray-200 rounded text-xs bg-white" />
        <input value={pc} onChange={e => setPc(e.target.value)} placeholder="postcode" className="w-20 px-2 py-1.5 border border-gray-200 rounded text-xs bg-white" />
        <input value={stad} onChange={e => setStad(e.target.value)} placeholder="gemeente" className="w-28 px-2 py-1.5 border border-gray-200 rounded text-xs bg-white" />
        <input value={mail} onChange={e => setMail(e.target.value)} type="email" placeholder="e-mail vestiging" className="flex-1 min-w-[150px] px-2 py-1.5 border border-gray-200 rounded text-xs bg-white" />
        <button onClick={() => void add()} disabled={busy} className="px-3 py-1.5 bg-teal-700 text-white rounded text-xs font-semibold disabled:opacity-50">+ Vestiging</button>
      </div>
      {error && <p className="mt-1.5 text-xs text-red-600">{error}</p>}
    </div>
  );
}

function ResellerMembersPanel({ resellerId }: { resellerId: string }) {
  const [members, setMembers] = useState<import('../lib/api').ResellerMember[] | null>(null);
  const [vestigingen, setVestigingen] = useState<import('../lib/api').ResellerVestiging[]>([]);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [vest, setVest] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      const api = await import('../lib/api');
      setMembers(await api.adminGetResellerMembers(resellerId));
      setVestigingen((await api.adminGetVestigingen(resellerId)).filter(v => v.is_active));
    } catch (e) { setError((e as Error).message); }
  };
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [resellerId]);

  const add = async () => {
    if (!name.trim() || !email.trim() || pw.trim().length < 8) {
      setError('Naam, e-mail en een paswoord van minstens 8 tekens zijn verplicht.');
      return;
    }
    setBusy(true); setError(null);
    try {
      const api = await import('../lib/api');
      await api.adminCreateResellerMember(resellerId, { name: name.trim(), email: email.trim().toLowerCase(), password: pw.trim(), vestiging_id: vest || null });
      setName(''); setEmail(''); setPw(''); setVest('');
      void load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const toggle = async (m: import('../lib/api').ResellerMember) => {
    const api = await import('../lib/api');
    await api.adminUpdateResellerMember(m.id, { is_active: m.is_active ? 0 : 1 });
    void load();
  };

  return (
    <div className="border border-gray-100 rounded-lg p-3 bg-[#faf7f3]/50">
      <p className="text-xs font-bold text-[#8a7e74] uppercase tracking-wide mb-2">
        Subaccounts (medewerkers) — beheerd door jou
      </p>
      {members === null ? (
        <p className="text-xs text-[#8a7e74]">Laden…</p>
      ) : members.length === 0 ? (
        <p className="text-xs text-[#8a7e74] mb-2">Nog geen medewerkers. De hoofdaccount hierboven blijft de baas van dit bedrijf.</p>
      ) : (
        <ul className="space-y-1 mb-2">
          {members.map(m => (
            <li key={m.id} className="flex items-center justify-between text-xs bg-white border border-gray-100 rounded px-2 py-1.5">
              <span>
                <b>{m.name}</b> <span className="text-[#8a7e74]">({m.email})</span>
                {/* V6 punt 22: kleur per niveau — hoofdkantoor blauw, vestiging teal */}
                <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase border ${m.vestiging_id ? 'bg-teal-100 text-teal-800 border-teal-300' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
                  {m.vestiging_naam ?? 'Hoofdkantoor'}
                </span>
                {vestigingen.length > 0 && (
                  <select value={m.vestiging_id ?? ''} onChange={e => { void (async () => {
                      const api = await import('../lib/api');
                      await api.adminUpdateResellerMember(m.id, { vestiging_id: e.target.value || null });
                      void load();
                    })(); }}
                    className="ml-1.5 px-1 py-0.5 border border-gray-200 rounded text-[10px] bg-white">
                    <option value="">Hoofdkantoor</option>
                    {vestigingen.map(v => <option key={v.id} value={v.id}>{v.naam}</option>)}
                  </select>
                )}
              </span>
              <button onClick={() => void toggle(m)}
                className={`px-2 py-1 rounded text-[11px] font-semibold ${m.is_active ? 'bg-green-50 text-green-700 hover:bg-red-50 hover:text-red-700' : 'bg-gray-100 text-gray-500 hover:bg-green-50 hover:text-green-700'}`}>
                {m.is_active ? 'Actief — klik om te blokkeren' : 'Geblokkeerd — klik om te activeren'}
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap gap-2">
        <input value={name} onChange={e => setName(e.target.value)} placeholder="naam medewerker" className="flex-1 min-w-[140px] px-2 py-1.5 border border-gray-200 rounded text-xs bg-white" />
        <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="e-mail" className="flex-1 min-w-[160px] px-2 py-1.5 border border-gray-200 rounded text-xs bg-white" />
        <input value={pw} onChange={e => setPw(e.target.value)} type="text" placeholder="paswoord (min. 8)" className="w-36 px-2 py-1.5 border border-gray-200 rounded text-xs bg-white" autoComplete="off" />
        {vestigingen.length > 0 && (
          <select value={vest} onChange={e => setVest(e.target.value)} className="px-2 py-1.5 border border-gray-200 rounded text-xs bg-white">
            <option value="">Hoofdkantoor</option>
            {vestigingen.map(v => <option key={v.id} value={v.id}>{v.naam}</option>)}
          </select>
        )}
        <button onClick={() => void add()} disabled={busy} className="px-3 py-1.5 bg-[#c47a3a] text-white rounded text-xs font-semibold disabled:opacity-50">+ Medewerker</button>
      </div>
      {error && <p className="mt-1.5 text-xs text-red-600">{error}</p>}
    </div>
  );
}

function ResellerEditModal({ reseller, onClose, onSaved }: {
  reseller: import('../lib/api').AdminReseller | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useLanguage();
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const isNew = !reseller;

  // Parse stored JSON arrays into local state arrays. Empty arrays = "none".
  const initialContacts: import('../lib/api').ResellerContact[] = (() => {
    if (!reseller?.contacts_json) return [];
    try { return JSON.parse(reseller.contacts_json); } catch { return []; }
  })();
  const initialInstallers: import('../lib/api').ResellerInstaller[] = (() => {
    if (!reseller?.installers_json) return [];
    try { return JSON.parse(reseller.installers_json); } catch { return []; }
  })();

  const [form, setForm] = useState({
    email:                reseller?.user_email ?? '',
    name:                 reseller?.user_name ?? '',
    company_name:         reseller?.company_name ?? '',
    slug:                 reseller?.slug ?? '',
    btw_number:           reseller?.btw_number ?? '',
    contact_email:        reseller?.contact_email ?? '',
    contact_phone:        reseller?.contact_phone ?? '',
    address:              reseller?.address ?? '',
    postal_code:          reseller?.postal_code ?? '',
    city:                 reseller?.city ?? '',
    default_discount_pct: reseller?.default_discount_pct ?? 30,
    type:                 reseller?.type ?? 'kleinhandel',
    is_installateur:      !!reseller?.is_installateur,
    initial_password:     '',
    postcodes:            reseller?.postcodes ?? '',
    notes:                reseller?.notes ?? '',
    logo_url:             reseller?.logo_url ?? '',
    website:              reseller?.website ?? '',
    color_hex:            reseller?.color_hex ?? '#888888',
  });
  const [contacts, setContacts] = useState<import('../lib/api').ResellerContact[]>(initialContacts);
  const [installers, setInstallers] = useState<import('../lib/api').ResellerInstaller[]>(initialInstallers);

  // v3.2.55 — leverancier-koppelingen. Lege array = "alle leveranciers"
  // (legacy gedrag, niet breken voor demo data). Volle array = strikt:
  // enkel deze leveranciers tellen.
  const [suppliers, setSuppliersState] = useState<AdminSupplier[]>([]);
  const [supplierIds, setSupplierIds] = useState<string[]>(reseller?.assigned_supplier_ids ?? []);
  useEffect(() => {
    void (async () => {
      try {
        const list = await adminGetSuppliers();
        setSuppliersState(list.filter(s => s.is_active === 1));
      } catch { /* niet kritisch — checkbox-blok blijft dan leeg */ }
    })();
  }, []);
  const toggleSupplier = (sid: string) =>
    setSupplierIds(prev => prev.includes(sid) ? prev.filter(x => x !== sid) : [...prev, sid]);

  const addContact = () => setContacts(c => [...c, { name: '', role: '', email: '', phone: '' }]);
  const updateContact = (idx: number, patch: Partial<import('../lib/api').ResellerContact>) =>
    setContacts(c => c.map((x, i) => i === idx ? { ...x, ...patch } : x));
  const removeContact = (idx: number) => setContacts(c => c.filter((_, i) => i !== idx));

  const addInstaller = () => setInstallers(i => [...i, {
    company_name: '', contact_name: '', email: '', phone: '',
    btw: '', address: '', postal_code: '', city: '', notes: '',
  }]);
  const updateInstaller = (idx: number, patch: Partial<import('../lib/api').ResellerInstaller>) =>
    setInstallers(arr => arr.map((x, i) => i === idx ? { ...x, ...patch } : x));
  const removeInstaller = (idx: number) => setInstallers(arr => arr.filter((_, i) => i !== idx));

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      const api = await import('../lib/api');
      // Strip empty rows so we don't store {name:''} junk.
      const cleanContacts = contacts.filter(c => c.name.trim().length > 0);
      const cleanInstallers = installers.filter(i => i.company_name.trim().length > 0);
      const websiteOrNull = form.website.trim() || null;

      // v5 blok 7: paswoord meesturen als het ingevuld is (min. 8 tekens).
      const pw = (form.initial_password ?? '').trim();
      if (pw && pw.length < 8) { setErr('Paswoord: minstens 8 tekens (of laat leeg).'); setSaving(false); return; }
      if (isNew) {
        const created = await api.adminCreateReseller({
          email: form.email,
          name: form.name,
          company_name: form.company_name,
          slug: form.slug,
          btw_number: form.btw_number || null,
          contact_email: form.contact_email || null,
          contact_phone: form.contact_phone || null,
          address: form.address || null,
          postal_code: form.postal_code || null,
          city: form.city || null,
          default_discount_pct: Number(form.default_discount_pct) || 0,
          type: form.type as 'groothandel' | 'kleinhandel' | 'bouwonderneming' | 'installateur',
          is_installateur: form.type === 'installateur' || !!form.is_installateur,
          postcodes: form.postcodes || null,
          notes: form.notes || null,
          website: websiteOrNull,
          contacts: cleanContacts,
          installers: cleanInstallers,
          supplier_ids: supplierIds,
          color_hex: form.color_hex,
        });
        if (pw) await api.adminSetUserPassword(created.user_id, pw, 'both');
      } else {
        if (pw) await api.adminSetUserPassword(reseller!.user_id, pw, 'both');
        await api.adminUpdateReseller(reseller!.id, {
          email: form.email !== reseller!.user_email ? form.email : undefined,
          name: form.name !== reseller!.user_name ? form.name : undefined,
          company_name: form.company_name,
          slug: form.slug,
          btw_number: form.btw_number || null,
          contact_email: form.contact_email || null,
          contact_phone: form.contact_phone || null,
          address: form.address || null,
          postal_code: form.postal_code || null,
          city: form.city || null,
          default_discount_pct: Number(form.default_discount_pct) || 0,
          type: form.type as 'groothandel' | 'kleinhandel' | 'bouwonderneming' | 'installateur',
          is_installateur: form.type === 'installateur' || !!form.is_installateur,
          postcodes: form.postcodes || null,
          notes: form.notes || null,
          website: websiteOrNull,
          contacts: cleanContacts,
          installers: cleanInstallers,
          supplier_ids: supplierIds,
          color_hex: form.color_hex,
        });
      }
      onSaved();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!reseller) return;
    if (!confirm(t('admin.resellers.confirmDeactivate'))) return;
    setSaving(true);
    try {
      const { adminDeleteReseller } = await import('../lib/api');
      await adminDeleteReseller(reseller.id);
      onSaved();
    } catch (e) {
      setErr((e as Error).message);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl my-8" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-lg font-bold text-[#1a1a1a]">{isNew ? t('admin.resellers.new') : t('admin.resellers.edit')}</h3>
          <button onClick={onClose} className="p-1 text-[#8a7e74] hover:text-[#1a1a1a]"><X size={18} /></button>
        </div>
        <div className="px-6 py-5 grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto">
          <ModalField label={t('admin.resellers.companyName')} value={form.company_name} onChange={v => setForm(f => ({ ...f, company_name: v }))} required />
          <ModalField label={t('admin.resellers.slug')} value={form.slug} onChange={v => setForm(f => ({ ...f, slug: v.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))} hint="lowercase, dashes" required />
          <ModalField label={t('admin.resellers.contactPerson')} value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} required />
          <ModalField label={t('admin.resellers.loginEmail')} value={form.email} type="email" onChange={v => setForm(f => ({ ...f, email: v }))} required disabled={!isNew} />
          <ModalField label={t('admin.resellers.btwNumber')} value={form.btw_number} onChange={v => setForm(f => ({ ...f, btw_number: v }))} placeholder="BE0123.456.789" />
          <ModalField label={t('admin.resellers.discount')} value={String(form.default_discount_pct)} type="number" onChange={v => setForm(f => ({ ...f, default_discount_pct: Number(v) }))} suffix="%" />
          {/* v5 blok 2: handelstype — bepaalt de indeling in het Resellers-vak */}
          <div>
            <label className="block text-xs font-bold text-[#8a7e74] uppercase tracking-wide mb-1">Type handelaar</label>
            <select
              value={form.type}
              onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a] capitalize"
            >
              <option value="groothandel">Groothandel</option>
              <option value="kleinhandel">Kleinhandel</option>
              <option value="bouwonderneming">Bouwonderneming</option>
              <option value="installateur">Installateur</option>
            </select>
            {/* V6 punt 12: een handelaar kan óók installateur zijn */}
            {form.type !== 'installateur' && (
              <label className="flex items-center gap-2 mt-1.5 cursor-pointer text-xs text-[#1a1a1a]">
                <input
                  type="checkbox"
                  checked={form.is_installateur}
                  onChange={e => setForm(f => ({ ...f, is_installateur: e.target.checked }))}
                  className="w-4 h-4 accent-violet-600"
                />
                Plaatst óók (verschijnt mee in de installateurslijst voor klanten)
              </label>
            )}
          </div>
          {/* v5 blok 7: (initieel) paswoord — Filip maakt het account aan en
               bezorgt login + paswoord; de reseller wijzigt het daarna zelf.
               Leeg laten = niets wijzigen. Zet login op 'paswoord én mailcode'. */}
          <div>
            <label className="block text-xs font-bold text-[#8a7e74] uppercase tracking-wide mb-1">{isNew ? 'Initieel paswoord' : 'Nieuw paswoord (reset)'}</label>
            <input
              type="text"
              value={form.initial_password ?? ''}
              onChange={e => setForm(f => ({ ...f, initial_password: e.target.value }))}
              placeholder="min. 8 tekens — leeg = geen wijziging"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:border-[#c47a3a]"
              autoComplete="off"
            />
          </div>
          {/* v5 blok 2: klanten & offertes die aan deze reseller hangen */}
          {!isNew && (
            <div className="sm:col-span-2">
              <ResellerQuotesList resellerId={reseller!.id} />
            </div>
          )}
          {/* V5.31: subaccounts (medewerkers) — door Filip beheerd */}
          {!isNew && (
            <div className="sm:col-span-2">
              <VestigingenPanel resellerId={reseller!.id} />
              <ResellerMembersPanel resellerId={reseller!.id} />
            </div>
          )}
          <ModalField label={t('admin.resellers.contactEmail')} value={form.contact_email} type="email" onChange={v => setForm(f => ({ ...f, contact_email: v }))} />
          <ModalField label={t('admin.resellers.contactPhone')} value={form.contact_phone} onChange={v => setForm(f => ({ ...f, contact_phone: v }))} />
          <ModalField label={t('admin.resellers.address')} value={form.address} onChange={v => setForm(f => ({ ...f, address: v }))} fullWidth />
          <ModalField label={t('admin.resellers.postalCode')} value={form.postal_code} onChange={v => setForm(f => ({ ...f, postal_code: v }))} />
          <ModalField label={t('admin.resellers.city')} value={form.city} onChange={v => setForm(f => ({ ...f, city: v }))} />
          <div className="sm:col-span-2">
            <label className="block text-xs font-semibold text-[#8a7e74] uppercase tracking-wide mb-1">
              {t('admin.resellers.servicePostcodes')}
            </label>
            <textarea
              value={form.postcodes}
              onChange={e => setForm(f => ({ ...f, postcodes: e.target.value }))}
              rows={3}
              placeholder="2000,2018,2020,..."
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a] font-mono"
            />
            <p className="text-[10px] text-[#8a7e74] mt-1">{t('admin.resellers.postcodesHint')}</p>
          </div>
          <ModalField label={t('admin.resellers.logoUrl')} value={form.logo_url} onChange={v => setForm(f => ({ ...f, logo_url: v }))} placeholder="/reseller-logos/xxx.svg" fullWidth hint={t('admin.resellers.logoHint')} />
          <ModalField label={t('admin.resellers.website') || 'Website'} value={form.website} onChange={v => setForm(f => ({ ...f, website: v }))} placeholder="https://antwerpse-bricks.be" type="url" fullWidth hint={t('admin.resellers.websiteHint') || 'Publieke website van deze reseller (optioneel)'} />

          {/* ─── Extra contactpersonen — meerdere mensen van dezelfde reseller ─── */}
          <div className="sm:col-span-2 bg-blue-50/50 border border-blue-200 rounded-lg p-3 mt-2">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-bold text-blue-900 uppercase tracking-wide flex items-center gap-1.5">
                👥 {t('admin.resellers.contactsTitle') || 'Extra contactpersonen'}
              </label>
              <button
                type="button"
                onClick={addContact}
                className="flex items-center gap-1 px-2.5 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-semibold"
              >
                <Plus size={12} /> {t('admin.resellers.contactAdd') || 'Toevoegen'}
              </button>
            </div>
            <p className="text-[10px] text-blue-800 mb-3 leading-relaxed">
              {t('admin.resellers.contactsHint') || 'Voor wanneer er meerdere mensen bij deze reseller met offertes/orders bezig zijn (bv. verkoop, logistiek).'}
            </p>
            {contacts.length === 0 ? (
              <p className="text-[11px] text-blue-700 italic">{t('admin.resellers.contactsEmpty') || 'Nog geen extra contactpersonen.'}</p>
            ) : (
              <div className="space-y-2">
                {contacts.map((c, idx) => (
                  <div key={idx} className="bg-white border border-blue-200 rounded p-2 grid grid-cols-1 sm:grid-cols-2 gap-2 relative">
                    <input
                      type="text"
                      value={c.name}
                      onChange={e => updateContact(idx, { name: e.target.value })}
                      placeholder={t('admin.resellers.contactName') || 'Naam *'}
                      className="px-2 py-1.5 border border-gray-200 rounded text-xs focus:outline-none focus:border-blue-500"
                    />
                    <input
                      type="text"
                      value={c.role ?? ''}
                      onChange={e => updateContact(idx, { role: e.target.value })}
                      placeholder={t('admin.resellers.contactRole') || 'Rol (bv. Verkoop, Logistiek)'}
                      className="px-2 py-1.5 border border-gray-200 rounded text-xs focus:outline-none focus:border-blue-500"
                    />
                    <input
                      type="email"
                      value={c.email ?? ''}
                      onChange={e => updateContact(idx, { email: e.target.value })}
                      placeholder="email@bedrijf.be"
                      className="px-2 py-1.5 border border-gray-200 rounded text-xs focus:outline-none focus:border-blue-500"
                    />
                    <input
                      type="tel"
                      value={c.phone ?? ''}
                      onChange={e => updateContact(idx, { phone: e.target.value })}
                      placeholder="+32 …"
                      className="px-2 py-1.5 border border-gray-200 rounded text-xs focus:outline-none focus:border-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => removeContact(idx)}
                      className="absolute top-1 right-1 p-1 text-red-500 hover:bg-red-50 rounded"
                      title={t('admin.resellers.remove') || 'Verwijder'}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ─── Installateurs — bedrijven die voor deze reseller plaatsen ─── */}
          <div className="sm:col-span-2 bg-emerald-50/50 border border-emerald-200 rounded-lg p-3 mt-2">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-xs font-bold text-emerald-900 uppercase tracking-wide flex items-center gap-1.5">
                🔨 {t('admin.resellers.installersTitle') || 'Installateurs onder deze reseller'}
              </label>
              <button
                type="button"
                onClick={addInstaller}
                className="flex items-center gap-1 px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-semibold"
              >
                <Plus size={12} /> {t('admin.resellers.installerAdd') || 'Toevoegen'}
              </button>
            </div>
            <p className="text-[10px] text-emerald-800 mb-3 leading-relaxed">
              {t('admin.resellers.installersHint') || 'Plaatsingsbedrijven die voor deze reseller werken. Eindklant ziet enkel de reseller — hier hou je intern bij wie de feitelijke plaatsing doet.'}
            </p>
            {installers.length === 0 ? (
              <p className="text-[11px] text-emerald-700 italic">{t('admin.resellers.installersEmpty') || 'Nog geen installateurs gekoppeld.'}</p>
            ) : (
              <div className="space-y-3">
                {installers.map((inst, idx) => (
                  <div key={idx} className="bg-white border border-emerald-200 rounded p-3 relative">
                    <button
                      type="button"
                      onClick={() => removeInstaller(idx)}
                      className="absolute top-1 right-1 p-1 text-red-500 hover:bg-red-50 rounded"
                      title={t('admin.resellers.remove') || 'Verwijder'}
                    >
                      <Trash2 size={12} />
                    </button>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <input
                        type="text"
                        value={inst.company_name}
                        onChange={e => updateInstaller(idx, { company_name: e.target.value })}
                        placeholder={t('admin.resellers.installerCompany') || 'Bedrijfsnaam *'}
                        className="px-2 py-1.5 border border-gray-200 rounded text-xs focus:outline-none focus:border-emerald-500 sm:col-span-2 font-medium"
                      />
                      <input
                        type="text"
                        value={inst.contact_name ?? ''}
                        onChange={e => updateInstaller(idx, { contact_name: e.target.value })}
                        placeholder={t('admin.resellers.installerContact') || 'Contactpersoon'}
                        className="px-2 py-1.5 border border-gray-200 rounded text-xs focus:outline-none focus:border-emerald-500"
                      />
                      <input
                        type="text"
                        value={inst.btw ?? ''}
                        onChange={e => updateInstaller(idx, { btw: e.target.value })}
                        placeholder="BTW (BE0…)"
                        className="px-2 py-1.5 border border-gray-200 rounded text-xs focus:outline-none focus:border-emerald-500"
                      />
                      <input
                        type="email"
                        value={inst.email ?? ''}
                        onChange={e => updateInstaller(idx, { email: e.target.value })}
                        placeholder="email@plaatser.be"
                        className="px-2 py-1.5 border border-gray-200 rounded text-xs focus:outline-none focus:border-emerald-500"
                      />
                      <input
                        type="tel"
                        value={inst.phone ?? ''}
                        onChange={e => updateInstaller(idx, { phone: e.target.value })}
                        placeholder="+32 …"
                        className="px-2 py-1.5 border border-gray-200 rounded text-xs focus:outline-none focus:border-emerald-500"
                      />
                      <input
                        type="text"
                        value={inst.address ?? ''}
                        onChange={e => updateInstaller(idx, { address: e.target.value })}
                        placeholder={t('admin.resellers.address') || 'Adres'}
                        className="px-2 py-1.5 border border-gray-200 rounded text-xs focus:outline-none focus:border-emerald-500 sm:col-span-2"
                      />
                      <input
                        type="text"
                        value={inst.postal_code ?? ''}
                        onChange={e => updateInstaller(idx, { postal_code: e.target.value })}
                        placeholder={t('admin.resellers.postalCode') || 'Postcode'}
                        className="px-2 py-1.5 border border-gray-200 rounded text-xs focus:outline-none focus:border-emerald-500"
                      />
                      <input
                        type="text"
                        value={inst.city ?? ''}
                        onChange={e => updateInstaller(idx, { city: e.target.value })}
                        placeholder={t('admin.resellers.city') || 'Gemeente'}
                        className="px-2 py-1.5 border border-gray-200 rounded text-xs focus:outline-none focus:border-emerald-500"
                      />
                      <textarea
                        value={inst.notes ?? ''}
                        onChange={e => updateInstaller(idx, { notes: e.target.value })}
                        rows={2}
                        placeholder={t('admin.resellers.installerNotes') || 'Notities (specialiteit, regio, contact-uren…)'}
                        className="px-2 py-1.5 border border-gray-200 rounded text-xs focus:outline-none focus:border-emerald-500 sm:col-span-2"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ─── Leveranciers die deze reseller verdeelt (v3.2.55) ───────────
               Filip vinkt aan welke leveranciers deze reseller mag aanbieden.
               Niets aanvinken = alle leveranciers (legacy gedrag, niet breken
               voor demo data). Bij echte klanten verwacht Filip dat dit per
               reseller specifiek wordt geconfigureerd. */}
          <div className="sm:col-span-2 bg-purple-50/60 border border-purple-200 rounded-lg p-3 mt-2">
            <label className="block text-xs font-bold text-purple-900 uppercase tracking-wide mb-1 flex items-center gap-1.5">
              📦 Leveranciers die deze reseller verdeelt
            </label>
            <p className="text-[10px] text-purple-800 mb-2 leading-relaxed">
              Vink aan welke leveranciers deze reseller mag aanbieden. Wanneer je niets aanvinkt zijn standaard <strong>alle</strong> leveranciers actief — productenfilter komt pas online wanneer je expliciet kiest.
            </p>
            {suppliers.length === 0 ? (
              <p className="text-[10px] text-purple-700 italic">Geen actieve leveranciers gevonden.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {suppliers.map(s => {
                  const checked = supplierIds.includes(s.id);
                  return (
                    <label
                      key={s.id}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded border text-xs cursor-pointer transition-colors ${
                        checked
                          ? 'bg-purple-100 border-purple-400 text-purple-900 font-semibold'
                          : 'bg-white border-gray-200 text-[#4a4a4a] hover:bg-purple-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleSupplier(s.id)}
                        className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="flex-1 truncate">{s.name}</span>
                      <span className="text-[9px] text-[#8a7e74] font-mono">{s.country}</span>
                    </label>
                  );
                })}
              </div>
            )}
            {supplierIds.length > 0 && (
              <p className="text-[10px] text-purple-700 mt-2 font-semibold">
                ✓ {supplierIds.length} leverancier{supplierIds.length === 1 ? '' : 's'} aangevinkt — enkel hun producten zichtbaar voor deze reseller.
              </p>
            )}
          </div>

          {/* Afspraken — prominent, eigen vak met amber accent */}
          <div className="sm:col-span-2 bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
            <label className="block text-xs font-bold text-amber-900 uppercase tracking-wide mb-2 flex items-center gap-1.5">
              📋 {t('admin.resellers.agreementsTitle')}
            </label>
            <p className="text-[10px] text-amber-800 mb-2 leading-relaxed">
              {t('admin.resellers.agreementsHint')}
            </p>
            <textarea
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              rows={6}
              placeholder={t('admin.resellers.agreementsPlaceholder')}
              className="w-full px-3 py-2 border border-amber-200 rounded-lg text-sm bg-white focus:outline-none focus:border-amber-500 font-mono"
            />
          </div>
        </div>
        {err && <div className="px-6 py-2 bg-red-50 text-red-700 text-sm border-t border-red-100">{err}</div>}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-3">
          {!isNew && reseller?.is_demo === 1 ? (
            // Demo-reseller is onverwijderbaar — Deactiveer-knop disabled.
            <span
              className="text-purple-700 text-xs font-medium bg-purple-50 border border-purple-200 px-3 py-2 rounded-lg flex items-center gap-1.5"
              title="Dit is een vast voorbeeld dat altijd zichtbaar moet blijven"
            >
              🛡️ Demo · niet verwijderbaar
            </span>
          ) : !isNew ? (
            <button onClick={remove} disabled={saving} className="text-red-700 text-sm font-medium hover:bg-red-50 px-3 py-2 rounded-lg disabled:opacity-50">
              {t('admin.resellers.deactivate')}
            </button>
          ) : null}
          <div className="flex gap-2 ml-auto">
            <button onClick={onClose} disabled={saving} className="px-4 py-2 text-sm font-medium text-[#4a4a4a] bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50">
              {t('admin.profile.cancel')}
            </button>
            <button onClick={save} disabled={saving} className="px-4 py-2 text-sm font-semibold bg-[#c47a3a] text-white rounded-lg hover:bg-[#a8643a] disabled:opacity-50 flex items-center gap-2">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? t('admin.profile.saving') : t('admin.profile.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ModalField({ label, value, onChange, type = 'text', placeholder, suffix, hint, fullWidth, required, disabled }: {
  label: string; value: string; onChange: (v: string) => void; type?: string;
  placeholder?: string; suffix?: string; hint?: string; fullWidth?: boolean; required?: boolean; disabled?: boolean;
}) {
  return (
    <div className={fullWidth ? 'sm:col-span-2' : ''}>
      <label className="block text-xs font-semibold text-[#8a7e74] uppercase tracking-wide mb-1">
        {label}{required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <div className="relative">
        <input
          type={type}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a] disabled:bg-gray-50 disabled:text-gray-500"
        />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#8a7e74]">{suffix}</span>}
      </div>
      {hint && <p className="text-[10px] text-[#8a7e74] mt-1">{hint}</p>}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// BACKLOG TAB — activity log + deleted quotes (recoverable) + CSV export
// ════════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════════
// BERICHTEN TAB (v3.2.95) — contact-formulier inbox
// Toont alle contact_messages uit de DB. Filip kan status veranderen
// (new → read → replied). Lazy fetch bij tab-mount.
// ═══════════════════════════════════════════════════════════════════════════════

/** V5.32: berichten van de Restpartijen-pagina. Interesse van kopers
 *  (met de partij erbij — één klik maakt er een concept-offerte van) en
 *  voorstellen van bedrijven die zelf een partij aanbieden. De databank
 *  weigert wissen of herschrijven; alleen de status mag veranderen. */
/** V6 punten 18-20: opvolging & forecast — termijn-vakken met de som in
 *  euro's van lopende offertes, plus per offerte de status die Filip kan
 *  overrulen. De opvolgmail vertrekt automatisch (cron) zodra een termijn
 *  verstrijkt; de knop "Stuur opvolgmails nu" draait die veeg meteen. */
function OpvolgingPanel() {
  const [data, setData] = useState<import('../lib/api').OpvolgingData | null>(null);
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [filter, setFilter] = useState<string | null>(null);

  const load = async () => {
    try {
      const api = await import('../lib/api');
      setData(await api.adminGetOpvolging());
    } catch { /* stil */ }
  };
  useEffect(() => { void load(); }, []);
  if (!data) return null;

  const VAK: Array<[key: '3m' | '6m' | '1j' | 'meer', label: string, cls: string]> = [
    ['3m', 'Binnen 3 maanden', 'border-green-300 bg-green-50'],
    ['6m', 'Binnen 6 maanden', 'border-blue-300 bg-blue-50'],
    ['1j', 'Binnen het jaar', 'border-amber-300 bg-amber-50'],
    ['meer', 'Later', 'border-gray-300 bg-gray-50'],
  ];
  const rows = filter ? data.rows.filter(r => r.project_termijn === filter) : data.rows;
  const STATUS_NL: Record<string, string> = {
    open: 'Open', uitgesteld: 'Uitgesteld', bezig: 'Mee bezig',
    niet_meer: 'Niet meer geïnteresseerd', concurrent: 'Gekocht bij concurrent',
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="px-4 sm:px-6 py-4 border-b border-gray-100 flex items-center gap-2 flex-wrap">
        <TrendingUp size={18} className="text-[#c47a3a]" />
        <h2 className="text-lg font-bold text-[#1a1a1a]">Opvolging & forecast</h2>
        <span className="text-xs text-[#8a7e74]">lopende offertes per projecttermijn</span>
        <button
          onClick={async () => {
            setBusy(true); setInfo(null);
            try {
              const api = await import('../lib/api');
              const n = await api.adminRunOpvolgSweep();
              setInfo(n > 0 ? `${n} opvolgmail(s) verstuurd.` : 'Geen termijnen verstreken — niets te versturen.');
              void load();
            } catch (e) { setInfo((e as Error).message); }
            finally { setBusy(false); }
          }}
          disabled={busy}
          className="ml-auto px-3 py-1.5 bg-white border border-[#c47a3a] text-[#c47a3a] rounded-lg text-xs font-bold disabled:opacity-50"
          title="De opvolgmails vertrekken elke nacht vanzelf; hiermee draai je de controle meteen"
        >
          {busy ? 'Bezig…' : 'Stuur opvolgmails nu'}
        </button>
      </div>
      <div className="px-4 sm:px-6 py-4">
        {info && <p className="mb-3 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">{info}</p>}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          {VAK.map(([k, label, cls]) => (
            <button key={k} onClick={() => setFilter(f => f === k ? null : k)}
              className={`text-left rounded-xl border-2 p-3 transition-all ${cls} ${filter === k ? 'ring-2 ring-[#c47a3a]/50' : ''}`}>
              <p className="text-[11px] font-bold uppercase tracking-wide text-[#4a4a4a]">{label}</p>
              <p className="text-xl font-bold text-[#1a1a1a] mt-1">€{Math.round(data.vakken[k].total).toLocaleString('nl-BE')}</p>
              <p className="text-[11px] text-[#8a7e74]">{data.vakken[k].count} lopende offerte(s)</p>
            </button>
          ))}
        </div>
        {rows.length === 0 ? (
          <p className="text-sm text-[#8a7e74]">Nog geen offertes met een projecttermijn — die kiest de klant voortaan bij elke aanvraag.</p>
        ) : (
          <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
            {rows.map(r => (
              <div key={r.id} className={`flex items-center gap-2 flex-wrap rounded-lg border px-3 py-2 text-sm ${
                r.opvolg_status === 'niet_meer' || r.opvolg_status === 'concurrent' ? 'border-red-200 bg-red-50/50 opacity-75' : 'border-gray-100 bg-white'
              }`}>
                <span className="font-mono text-xs text-[#8a7e74]">#{r.quote_number}</span>
                <b className="text-[#1a1a1a]">{r.customer_name}</b>
                <span className="font-bold text-[#c47a3a]">€{Math.round(r.total_incl_vat).toLocaleString('nl-BE')}</span>
                {r.opvolg_mail_sent_at && <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px] font-bold" title="Opvolgmail is verstuurd">✉ opgevolgd</span>}
                <span className="ml-auto flex items-center gap-1.5">
                  <select value={r.project_termijn} onChange={async e => {
                    const api = await import('../lib/api');
                    await api.adminSetOpvolging(r.id, { project_termijn: e.target.value });
                    void load();
                  }} className="px-1.5 py-1 border border-gray-200 rounded text-xs">
                    {[['3m', '3 mnd'], ['6m', '6 mnd'], ['1j', '1 jaar'], ['meer', 'later'], ['info', 'ter info']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  <select value={r.opvolg_status} onChange={async e => {
                    const api = await import('../lib/api');
                    await api.adminSetOpvolging(r.id, { opvolg_status: e.target.value });
                    void load();
                  }} className="px-1.5 py-1 border border-gray-200 rounded text-xs">
                    {Object.entries(STATUS_NL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function RestpartijBerichtenPanel() {
  const [items, setItems] = useState<import('../lib/api').RestpartijBericht[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const load = async () => {
    try {
      const api = await import('../lib/api');
      setItems(await api.adminGetRestpartijBerichten());
    } catch (e) { setError((e as Error).message); }
  };
  useEffect(() => { void load(); }, []);

  const maakOfferte = async (m: import('../lib/api').RestpartijBericht) => {
    if (!m.product_id) return;
    const qtyStr = window.prompt(`Hoeveel m² voor de offerte van ${m.product_name}?`, '50');
    if (!qtyStr) return;
    const qty = parseFloat(qtyStr.replace(',', '.'));
    if (!Number.isFinite(qty) || qty <= 0) { setError('Ongeldige oppervlakte.'); return; }
    setBusy(m.id); setError(null); setInfo(null);
    try {
      const api = await import('../lib/api');
      await api.adminCreateQuote({
        customer_name: m.name,
        customer_email: m.email,
        customer_phone: m.phone ?? undefined,
        notes_nl: `RESTPARTIJ: ${m.product_name} — ${qty} m² × €${m.remnant_price}/m² || Vraag van de klant: ${m.message} || Werkuren van de plaatsingen niet inbegrepen.`,
        items: [{ product_id: m.product_id, qty_m2: qty, ...(m.remnant_price != null ? { unit_price: m.remnant_price } : {}) }],
      });
      await api.adminSetRestpartijBerichtStatus(m.id, 'behandeld');
      setInfo(`Concept-offerte aangemaakt voor ${m.name} — zie het klantdossier hierboven.`);
      void load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(null); }
  };

  const toggle = async (m: import('../lib/api').RestpartijBericht) => {
    const api = await import('../lib/api');
    await api.adminSetRestpartijBerichtStatus(m.id, m.status === 'nieuw' ? 'behandeld' : 'nieuw');
    void load();
  };

  if (items === null) return null;
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="px-4 sm:px-6 py-4 border-b border-gray-100 flex items-center gap-2">
        <Package size={18} className="text-[#c47a3a]" />
        <h2 className="text-lg font-bold text-[#1a1a1a]">Restpartij-berichten</h2>
        <span className="px-2 py-0.5 bg-[#c47a3a]/10 text-[#c47a3a] text-xs font-semibold rounded-full">{items.length}</span>
        <span className="ml-auto text-[10px] text-[#8a7e74] uppercase tracking-wide">Onwisbaar bewaard</span>
      </div>
      <div className="px-4 sm:px-6 py-4 space-y-2">
        {info && <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">{info}</p>}
        {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
        {items.length === 0 && <p className="text-sm text-[#8a7e74]">Nog geen berichten van de Restpartijen-pagina.</p>}
        {items.map(m => (
          <div key={m.id} className={`rounded-lg border px-3 py-2.5 text-sm ${m.status === 'nieuw' ? 'border-amber-300 bg-amber-50/50' : 'border-gray-100 bg-white'}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide border ${
                m.kind === 'interesse' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'
              }`}>{m.kind === 'interesse' ? 'Interesse koper' : 'Voorstel partij'}</span>
              <b className="text-[#1a1a1a]">{m.company ? `${m.company} — ` : ''}{m.name}</b>
              <span className="text-xs text-[#8a7e74]">{m.email}{m.phone ? ` · ${m.phone}` : ''}</span>
              {m.product_name && (
                <span className="px-2 py-0.5 bg-[#c47a3a]/10 text-[#8a4423] rounded text-[11px] font-semibold">
                  {m.product_name}{m.remnant_price != null ? ` — €${m.remnant_price}/m²` : ''}
                </span>
              )}
              <span className="ml-auto text-[11px] text-[#8a7e74]">{new Date(m.created_at * 1000).toLocaleString('nl-BE')}</span>
            </div>
            <p className="mt-1 text-[#4a4a4a] whitespace-pre-wrap">{m.message}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {m.kind === 'interesse' && m.product_id && (
                <button onClick={() => void maakOfferte(m)} disabled={busy === m.id}
                  className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-bold disabled:opacity-50">
                  {busy === m.id ? 'Bezig…' : '→ Maak offerte van deze restpartij'}
                </button>
              )}
              <a href={`mailto:${m.email}?subject=${encodeURIComponent('Uw bericht over restpartijen — Gevelsteenbedrijf')}`}
                className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-semibold text-[#4a4a4a]">
                Reageren per mail
              </a>
              <button onClick={() => void toggle(m)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${m.status === 'nieuw' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-500'}`}>
                {m.status === 'nieuw' ? 'Markeer als behandeld' : 'Behandeld ✓ — zet terug op nieuw'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function BerichtenTab() {
  const [items, setItems] = useState<import('../lib/api').ContactMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [updating, setUpdating] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { adminGetContactMessages } = await import('../lib/api');
      const data = await adminGetContactMessages();
      setItems(data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id: string, status: string) => {
    setUpdating(id);
    try {
      const { adminUpdateContactMessageStatus } = await import('../lib/api');
      await adminUpdateContactMessageStatus(id, status);
      setItems(prev => prev.map(m => m.id === id ? { ...m, status } : m));
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setUpdating(null);
    }
  };

  const statusBadge = (status: string) => {
    const cls = status === 'new'
      ? 'bg-blue-100 text-blue-700 border-blue-300'
      : status === 'replied'
        ? 'bg-emerald-100 text-emerald-700 border-emerald-300'
        : 'bg-gray-100 text-gray-700 border-gray-300';
    const label = status === 'new' ? 'NIEUW' : status === 'replied' ? 'BEANTWOORD' : 'GELEZEN';
    return <span className={`px-2 py-0.5 text-[10px] font-bold border rounded ${cls}`}>{label}</span>;
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="px-4 sm:px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <h2 className="text-lg font-bold text-[#1a1a1a] flex items-center gap-2">
          <Mail size={18} className="text-[#c47a3a]" /> Contact-berichten
          <span className="px-2 py-0.5 bg-[#c47a3a]/10 text-[#c47a3a] text-xs font-semibold rounded-full">{items.length}</span>
        </h2>
        <button onClick={load} className="flex items-center gap-1 px-3 py-1.5 bg-[#c47a3a]/10 hover:bg-[#c47a3a]/20 text-[#c47a3a] rounded-lg text-xs font-medium">
          <RefreshCw size={12} /> Verversen
        </button>
      </div>
      <div className="px-4 sm:px-6 py-4">
        {loading && (
          <div className="flex items-center gap-2 text-sm text-[#8a7e74]">
            <Loader2 size={14} className="animate-spin" /> Berichten laden...
          </div>
        )}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
            <AlertCircle size={14} /> {error}
          </div>
        )}
        {!loading && !error && items.length === 0 && (
          <p className="text-sm text-[#8a7e74] py-4">Nog geen berichten ontvangen via het contact-formulier.</p>
        )}
        {!loading && !error && items.length > 0 && (
          <div className="space-y-2">
            {items.map(m => {
              const isExp = expanded === m.id;
              return (
                <div key={m.id} className={`border rounded-lg overflow-hidden ${m.status === 'new' ? 'border-blue-200 bg-blue-50/30' : 'border-gray-100'}`}>
                  <button
                    onClick={() => {
                      setExpanded(prev => prev === m.id ? null : m.id);
                      if (m.status === 'new') setStatus(m.id, 'read');
                    }}
                    className="w-full flex items-center gap-3 p-3 text-left hover:bg-[#faf7f3] transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {statusBadge(m.status)}
                        <span className="font-semibold text-[#1a1a1a] text-sm">{m.name}</span>
                        <span className="text-xs text-[#8a7e74]">{m.email}</span>
                        {m.postal_code && <span className="text-[10px] text-[#8a7e74]">📍 {m.postal_code}</span>}
                        {m.reseller_name && <span className="px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-[10px] font-medium">→ {m.reseller_name}</span>}
                      </div>
                      <div className="text-xs text-[#8a7e74] mt-0.5 truncate">
                        {m.subject && <strong className="text-[#4a4a4a] mr-2">{m.subject}</strong>}
                        {m.message.slice(0, 80)}{m.message.length > 80 ? '…' : ''}
                      </div>
                      <div className="text-[10px] text-[#8a7e74] mt-0.5">
                        {new Date(m.created_at * 1000).toLocaleString('nl-BE')}
                      </div>
                    </div>
                    {isExp ? <ChevronUp size={16} className="text-[#8a7e74]" /> : <ChevronDown size={16} className="text-[#8a7e74]" />}
                  </button>
                  {isExp && (
                    <div className="border-t border-gray-100 bg-white p-4 space-y-3">
                      {m.phone && <div className="text-xs"><span className="text-[#8a7e74]">Telefoon:</span> <span className="text-[#1a1a1a] font-medium">{m.phone}</span></div>}
                      <div className="text-xs"><span className="text-[#8a7e74]">Taal:</span> <span className="text-[#1a1a1a] font-medium uppercase">{m.language}</span></div>
                      <div>
                        <div className="text-[10px] text-[#8a7e74] font-bold uppercase tracking-wide mb-1">Bericht</div>
                        <p className="text-sm text-[#1a1a1a] whitespace-pre-wrap leading-relaxed">{m.message}</p>
                      </div>
                      <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
                        <a
                          href={`mailto:${m.email}?subject=${encodeURIComponent('Re: ' + (m.subject ?? 'uw bericht'))}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#c47a3a] hover:bg-[#b06930] text-white rounded text-xs font-semibold"
                        >
                          <Mail size={12} /> Beantwoord per mail
                        </a>
                        <button
                          onClick={() => setStatus(m.id, 'replied')}
                          disabled={updating === m.id || m.status === 'replied'}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded text-xs font-medium disabled:opacity-40"
                        >
                          <CheckCircle size={12} /> Markeer beantwoord
                        </button>
                        <button
                          onClick={() => setStatus(m.id, 'new')}
                          disabled={updating === m.id || m.status === 'new'}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded text-xs font-medium disabled:opacity-40"
                        >
                          Markeer als nieuw
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function BacklogTab() {
  const { t } = useLanguage();
  const [activity, setActivity] = useState<import('../lib/api').ActivityLogEntry[]>([]);
  const [deleted, setDeleted] = useState<import('../lib/api').AdminQuote[]>([]);
  const [berichten, setBerichten] = useState<import('../lib/api').BerichtLogRegel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const api = await import('../lib/api');
      const [act, del, ber] = await Promise.all([
        api.adminGetActivityLog({ limit: 100 }),
        api.adminGetDeletedQuotes(),
        api.adminGetBerichtenlog(100).catch(() => ({ data: [], total: 0 })),
      ]);
      setActivity(act.data);
      setDeleted(del);
      setBerichten(ber.data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void load(); }, []);

  const restore = async (id: string) => {
    if (!confirm(t('admin.backlog.restoreConfirm'))) return;
    try {
      const api = await import('../lib/api');
      await api.adminRestoreQuote(id);
      void load();
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const downloadCsv = async (resource: 'quotes' | 'customers') => {
    try {
      const api = await import('../lib/api');
      const url = api.adminExportUrl(resource);
      const token = localStorage.getItem('gevel_session_token');
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`Export failed: ${res.status}`);
      const blob = await res.blob();
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `${resource}-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(downloadUrl);
    } catch (e) {
      alert((e as Error).message);
    }
  };

  const tijdstip = (ts: number) => new Date(ts * 1000).toLocaleString('nl-BE', {
    timeZone: 'Europe/Brussels', day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

  return (
    <div className="space-y-6">
      {/* V6.17 — berichtenlogboek: wat is er wanneer naar wie vertrokken */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-[#1a1a1a] flex items-center gap-2">
            <FileText size={18} className="text-[#c47a3a]" /> Berichtenlogboek
            <span className="px-2 py-0.5 bg-[#c47a3a]/10 text-[#c47a3a] text-xs font-semibold rounded-full">{berichten.length}</span>
          </h2>
          <p className="text-xs text-[#8a7e74] mt-1">
            Elke verstuurde mail met datum, uur en resultaat — inlogcodes, offertebevestigingen
            en opvolgherinneringen. In testmodus gaat alles naar het testadres.
          </p>
        </div>
        <div className="px-4 sm:px-6 py-4">
          {berichten.length === 0 ? (
            <p className="text-sm text-[#8a7e74]">Nog geen berichten verstuurd.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-[640px]">
                <thead>
                  <tr className="text-left text-[#8a7e74] uppercase tracking-wide">
                    <th className="py-1.5 pr-3">Datum en uur</th>
                    <th className="py-1.5 pr-3">Soort</th>
                    <th className="py-1.5 pr-3">Bestemd voor</th>
                    <th className="py-1.5 pr-3">Werkelijk verstuurd naar</th>
                    <th className="py-1.5 pr-3">Resultaat</th>
                  </tr>
                </thead>
                <tbody>
                  {berichten.map(b => (
                    <tr key={b.id} className="border-t border-gray-100">
                      <td className="py-1.5 pr-3 whitespace-nowrap tabular-nums">{tijdstip(b.created_at)}</td>
                      <td className="py-1.5 pr-3">{b.template}{b.context ? <span className="text-[#8a7e74]"> · {b.context}</span> : null}</td>
                      <td className="py-1.5 pr-3">{b.to_email}</td>
                      <td className="py-1.5 pr-3">
                        {b.delivered_to}
                        {b.mail_mode === 'test' && <span className="ml-1 px-1.5 py-0.5 bg-amber-50 text-amber-800 rounded font-semibold">test</span>}
                      </td>
                      <td className="py-1.5 pr-3">
                        <span className={`px-1.5 py-0.5 rounded font-semibold ${b.status === 'sent' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                          {b.status === 'sent' ? 'verstuurd' : 'mislukt'}
                        </span>
                        {b.error && <span className="ml-1 text-red-600">{b.error.slice(0, 60)}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Backup / export actions */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-[#1a1a1a] flex items-center gap-2">
            <Download size={18} className="text-[#c47a3a]" /> {t('admin.backlog.exportTitle')}
          </h2>
          <p className="text-xs text-[#8a7e74] mt-1">{t('admin.backlog.exportDesc')}</p>
        </div>
        <div className="px-4 sm:px-6 py-4 flex flex-wrap gap-3">
          <button
            onClick={() => downloadCsv('quotes')}
            className="flex items-center gap-2 px-4 py-2 bg-[#c47a3a] text-white text-sm font-semibold rounded-lg hover:bg-[#a8643a] transition-colors"
          >
            <Download size={14} /> {t('admin.backlog.exportQuotes')}
          </button>
          <button
            onClick={() => downloadCsv('customers')}
            className="flex items-center gap-2 px-4 py-2 bg-[#c47a3a] text-white text-sm font-semibold rounded-lg hover:bg-[#a8643a] transition-colors"
          >
            <Download size={14} /> {t('admin.backlog.exportCustomers')}
          </button>
        </div>
      </div>

      {/* Deleted quotes — recoverable */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-[#1a1a1a] flex items-center gap-2">
            <RefreshCw size={18} className="text-[#c47a3a]" /> {t('admin.backlog.deletedTitle')}
            <span className="px-2 py-0.5 bg-[#c47a3a]/10 text-[#c47a3a] text-xs font-semibold rounded-full">{deleted.length}</span>
          </h2>
          <p className="text-xs text-[#8a7e74] mt-1">{t('admin.backlog.deletedDesc')}</p>
        </div>
        <div className="px-4 sm:px-6 py-4">
          {error && <div className="mb-3 p-2 bg-red-50 text-red-700 text-sm rounded">{error}</div>}
          {loading ? (
            <p className="text-sm text-[#8a7e74] py-4">{t('admin.loading')}</p>
          ) : deleted.length === 0 ? (
            <p className="text-sm text-[#8a7e74] py-4">{t('admin.backlog.deletedEmpty')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    {[t('admin.testQuotes.client'), t('admin.testQuotes.email'), t('admin.testQuotes.inclVat'), t('admin.backlog.deletedAt'), ''].map(h => (
                      <th key={h} className="text-left py-2 pr-4 text-xs font-semibold text-[#8a7e74] uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {deleted.map(q => (
                    <tr key={q.id} className="border-b border-gray-50">
                      <td className="py-2.5 pr-4 font-medium text-[#1a1a1a]">{q.customer_name}</td>
                      <td className="py-2.5 pr-4 text-[#8a7e74]">{q.customer_email}</td>
                      <td className="py-2.5 pr-4 font-medium">{formatEuro(q.total_incl_vat)}</td>
                      <td className="py-2.5 pr-4 text-[#8a7e74] text-xs">
                        {(q as unknown as { deleted_at?: number }).deleted_at
                          ? new Date(((q as unknown as { deleted_at: number }).deleted_at) * 1000).toLocaleString('nl-BE')
                          : '—'}
                        <div className="text-[10px] text-gray-400">door {(q as unknown as { deleted_by?: string }).deleted_by ?? '?'}</div>
                      </td>
                      <td className="py-2.5">
                        <button
                          onClick={() => restore(q.id)}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-green-50 text-green-700 border border-green-200 rounded hover:bg-green-100 transition-colors"
                        >
                          <RefreshCw size={12} /> {t('admin.backlog.restore')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Activity log */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-[#1a1a1a] flex items-center gap-2">
            <BarChart2 size={18} className="text-[#c47a3a]" /> {t('admin.backlog.activityTitle')}
            <span className="px-2 py-0.5 bg-[#c47a3a]/10 text-[#c47a3a] text-xs font-semibold rounded-full">{activity.length}</span>
          </h2>
          <p className="text-xs text-[#8a7e74] mt-1">{t('admin.backlog.activityDesc')}</p>
        </div>
        <div className="px-4 sm:px-6 py-4">
          {loading ? (
            <p className="text-sm text-[#8a7e74] py-4">{t('admin.loading')}</p>
          ) : activity.length === 0 ? (
            <p className="text-sm text-[#8a7e74] py-4">{t('admin.backlog.activityEmpty')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    {[t('admin.backlog.when'), t('admin.backlog.who'), t('admin.backlog.what'), t('admin.backlog.details')].map(h => (
                      <th key={h} className="text-left py-2 pr-4 text-xs font-semibold text-[#8a7e74] uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {activity.map(a => (
                    <tr key={a.id} className="border-b border-gray-50 hover:bg-[#faf7f3]">
                      <td className="py-2 pr-4 text-xs text-[#8a7e74] whitespace-nowrap">
                        {new Date(a.created_at).toLocaleString('nl-BE')}
                      </td>
                      <td className="py-2 pr-4 text-xs text-[#1a1a1a]">
                        {a.user_email}
                        {a.user_role && <span className="ml-1 px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[9px] uppercase">{a.user_role}</span>}
                      </td>
                      <td className="py-2 pr-4">
                        <span className="px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-blue-50 text-blue-700">{a.action}</span>
                      </td>
                      <td className="py-2 pr-4 text-xs text-[#4a4a4a] max-w-md truncate" title={a.details ?? ''}>{a.details ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* D1 native backup info */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <h3 className="text-sm font-bold text-blue-900 mb-1 flex items-center gap-2">
          <Info size={14} /> {t('admin.backlog.cloudflareTitle')}
        </h3>
        <p className="text-xs text-blue-800 leading-relaxed">{t('admin.backlog.cloudflareDesc')}</p>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// RESELLER PRICING PANEL — per-reseller default discount + per-product overrides
// ════════════════════════════════════════════════════════════════════════════

function ResellerPricingPanel({ pricing, onRefresh, focusResellerId }: { pricing: AdminPricingRow[]; onRefresh?: () => void; focusResellerId?: string | null }) {
  const { t } = useLanguage();
  const [resellers, setResellers] = useState<import('../lib/api').AdminReseller[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [detail, setDetail] = useState<(import('../lib/api').AdminReseller & { pricing_overrides: import('../lib/api').ResellerPricingOverride[] }) | null>(null);
  const [defaultDiscount, setDefaultDiscount] = useState<number>(0);
  const [overrides, setOverrides] = useState<Record<string, { override_price: number | null; override_discount_pct: number | null; extra_discount_pct?: number | null; is_paused?: number }>>({});
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  // Default open — dit is wat Filip dagelijks gebruikt op de Prijzen tab.
  // Catalog Prijsbeheer (de andere block hieronder) staat default dicht.
  const [expanded, setExpanded] = useState(true);

  // V5.31: klik op een reseller-rij hierboven → dit paneel springt mee.
  useEffect(() => {
    if (focusResellerId) setSelectedId(focusResellerId);
  }, [focusResellerId]);

  // Load reseller list once
  useEffect(() => {
    void (async () => {
      try {
        const api = await import('../lib/api');
        const list = await api.adminGetResellers();
        setResellers(list.filter(r => r.is_active === 1));
        if (list.length > 0 && !selectedId) setSelectedId(list[0].id);
      } catch (e) {
        setError((e as Error).message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load detail (incl. pricing overrides) when selectedId changes
  useEffect(() => {
    if (!selectedId) return;
    void (async () => {
      try {
        const api = await import('../lib/api');
        const d = await api.adminGetReseller(selectedId);
        setDetail(d);
        setDefaultDiscount(d.default_discount_pct ?? 0);
        setSavedAt(d.updated_at);
        const map: Record<string, { override_price: number | null; override_discount_pct: number | null; extra_discount_pct?: number | null; is_paused?: number }> = {};
        for (const o of d.pricing_overrides) {
          map[o.product_id] = {
            override_price: o.override_price,
            override_discount_pct: o.override_discount_pct,
            extra_discount_pct: o.extra_discount_pct ?? null,
            is_paused: o.is_paused ?? 0,
          };
        }
        setOverrides(map);
        setSuccess(false);
        setError(null);
      } catch (e) {
        setError((e as Error).message);
      }
    })();
  }, [selectedId]);

  const setOverride = (productId: string, key: 'override_price' | 'override_discount_pct' | 'extra_discount_pct', value: number | null) => {
    setOverrides(prev => ({
      ...prev,
      [productId]: {
        override_price: key === 'override_price' ? value : (prev[productId]?.override_price ?? null),
        override_discount_pct: key === 'override_discount_pct' ? value : (prev[productId]?.override_discount_pct ?? null),
        extra_discount_pct: key === 'extra_discount_pct' ? value : (prev[productId]?.extra_discount_pct ?? null),
        is_paused: prev[productId]?.is_paused ?? 0,
      },
    }));
  };

  const togglePause = (productId: string) => {
    setOverrides(prev => ({
      ...prev,
      [productId]: {
        override_price: prev[productId]?.override_price ?? null,
        override_discount_pct: prev[productId]?.override_discount_pct ?? null,
        extra_discount_pct: prev[productId]?.extra_discount_pct ?? null,
        is_paused: prev[productId]?.is_paused === 1 ? 0 : 1,
      },
    }));
  };

  const save = async () => {
    if (!selectedId || !detail) return;
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const api = await import('../lib/api');
      // 1) update default discount via PUT /resellers/:id
      if (defaultDiscount !== detail.default_discount_pct) {
        await api.adminUpdateReseller(selectedId, { default_discount_pct: defaultDiscount });
      }
      // 2) replace pricing overrides via PUT /resellers/:id/pricing
      // Bewaar als korting OF pauze gezet is. extra_discount_pct altijd null
      // (kolom verwijderd in v3.2.42, oude data wordt overschreven).
      const overrideList = Object.entries(overrides)
        .filter(([, v]) => v.override_discount_pct != null || v.is_paused === 1)
        .map(([product_id, v]) => ({
          product_id,
          override_price: null,
          override_discount_pct: v.override_discount_pct,
          extra_discount_pct: null,
          is_paused: v.is_paused ?? 0,
        }));
      await api.adminUpdateResellerPricing(selectedId, overrideList);
      setSavedAt(Math.floor(Date.now() / 1000));
      setSuccess(true);
      setTimeout(() => setSuccess(false), 4000);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const selected = resellers.find(r => r.id === selectedId);

  // Effectieve verkoopprijs — Korting per product (indien ingevuld) overschrijft
  // de standaardkorting van de reseller. Geen stapeling meer (v3.2.42).
  const computeEffective = (productId: string, catalogPrice: number | null): { price: number | null; discountPct: number | null } => {
    if (catalogPrice == null) return { price: null, discountPct: null };
    const ov = overrides[productId];
    const pct = Math.max(0, Math.min(50, ov?.override_discount_pct ?? defaultDiscount));
    const price = Math.round(catalogPrice * (1 - pct / 100) * 100) / 100;
    return { price, discountPct: pct };
  };

  return (
    <div className="bg-white rounded-xl border border-amber-200 shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(e => !e)}
        className="w-full px-4 sm:px-6 py-4 border-b border-amber-100 bg-amber-50 hover:bg-amber-100 transition-colors flex items-center justify-between gap-3 text-left"
      >
        <div className="flex items-center gap-3">
          <CheckCircle size={20} className="text-green-600 flex-shrink-0" />
          <div>
            <h2 className="text-lg font-bold text-[#1a1a1a] flex items-center gap-2">
              <Users size={18} className="text-amber-700" /> {t('admin.pricing.resellerSection')}
              {selected && <span className="ml-2 text-sm font-normal text-amber-800">— {selected.company_name}</span>}
              {/* Versie-tag — zo zie je in één oogopslag of de juiste bundle
                   geladen is. Verwijder of verberg gerust later. */}
              {/* versie-tag verwijderd in v3.2.42 — was tijdelijk debug-marker */}
            </h2>
            <p className="text-xs text-amber-800 mt-0.5">{t('admin.pricing.resellerSectionDesc')}</p>
          </div>
        </div>
        <ChevronDown size={18} className={`text-amber-700 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="px-4 sm:px-6 py-4 space-y-5">
          {error && <div className="p-2 bg-red-50 text-red-700 text-sm rounded">{error}</div>}
          {success && <div className="p-2 bg-green-50 text-green-700 text-sm rounded">{t('admin.pricing.saved')}</div>}

          {/* Reseller picker + default discount */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
            <div className="sm:col-span-2">
              <label className="block text-xs font-bold text-[#8a7e74] uppercase tracking-wide mb-1">{t('admin.pricing.selectReseller')}</label>
              <select
                value={selectedId}
                onChange={e => setSelectedId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a]"
              >
                {resellers.map(r => (
                  <option key={r.id} value={r.id}>{r.company_name} ({r.default_discount_pct}%)</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-[#8a7e74] uppercase tracking-wide mb-1">{t('admin.pricing.defaultDiscount')}</label>
              <div className="relative">
                <input
                  type="number" min="0" max="100" step="0.5"
                  value={defaultDiscount}
                  onChange={e => setDefaultDiscount(Number(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a] pr-7"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#8a7e74]">%</span>
              </div>
            </div>
          </div>

          {savedAt && (
            <p className="text-[11px] text-[#8a7e74] italic">
              {t('admin.pricing.lastSaved')}: {formatDate(savedAt)} ({new Date(savedAt * 1000).toLocaleTimeString('nl-BE')})
            </p>
          )}

          {/* ─── Sticky save-bar (boven de tabel) ─────────────────────────────
               Filip miste vroeger de save-knop omdat die onderaan stond na 27
               rijen. Nu sticky aan de top met een duidelijke "Opslaan" CTA. */}
          <div className="sticky top-2 z-10 bg-white border-2 border-amber-400 rounded-lg shadow-md p-3 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
            <p className="text-xs text-[#4a4a4a] leading-snug">
              <span className="text-amber-800 font-bold">💾 {t('admin.pricing.saveChanges')}</span> — {t('admin.pricing.saveBarDesc')}{' '}
              <strong className="text-[#1a1a1a]">{selected?.company_name ?? '—'}</strong>.
            </p>
            <button
              onClick={save}
              disabled={saving}
              className="px-6 py-2.5 bg-[#c47a3a] text-white text-sm font-bold rounded-lg hover:bg-[#a8643a] disabled:opacity-50 flex items-center justify-center gap-2 min-h-[44px] flex-shrink-0 shadow-sm"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? t('admin.pricing.savingDots') : `${t('admin.pricing.saveFor')} ${selected?.company_name?.split(' ')[0] ?? t('admin.pricing.thisReseller')}`}
            </button>
          </div>

          {/* Per-product overrides */}
          <div className="border-t border-gray-100 pt-4">
            <p className="text-[11px] text-[#8a7e74] mb-3">
              {t('admin.pricing.tableDesc')}
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left py-2 px-3 font-semibold text-[#8a7e74] uppercase tracking-wide">{t('admin.pricing.colProduct')}</th>
                    <th className="text-left py-2 px-3 font-semibold text-[#8a7e74] uppercase tracking-wide" title={t('admin.pricing.colInkoopTitle')}>{t('admin.pricing.colInkoop')}</th>
                    <th className="text-left py-2 px-3 font-semibold text-[#8a7e74] uppercase tracking-wide" title={t('admin.pricing.colMargeTitle')}>{t('admin.pricing.colMarge')}</th>
                    <th className="text-left py-2 px-3 font-semibold text-[#8a7e74] uppercase tracking-wide" title={t('admin.pricing.colSellTitle')}>{t('admin.pricing.colSell')}</th>
                    <th className="text-left py-2 px-3 font-semibold text-[#8a7e74] uppercase tracking-wide" title={t('admin.pricing.colStandardTitle')}>{t('admin.pricing.colStandard')}</th>
                    <th className="text-left py-2 px-3 font-semibold text-amber-800 uppercase tracking-wide bg-amber-100/50" title={t('admin.pricing.colDiscountTitle')}>{t('admin.pricing.colDiscount')}</th>
                    <th className="text-left py-2 px-3 font-semibold text-[#8a7e74] uppercase tracking-wide">{t('admin.pricing.colEffective')}</th>
                    <th className="text-center py-2 px-2 font-semibold text-[#8a7e74] uppercase tracking-wide w-20">{t('admin.pricing.colStatus')}</th>
                  </tr>
                </thead>
                <tbody>
                  {pricing.map(p => {
                    const ov = overrides[p.id] ?? { override_price: null, override_discount_pct: null, is_paused: 0 };
                    const isPaused = ov.is_paused === 1;
                    const eff = computeEffective(p.id, p.locked_price);
                    // Catalog cijfers — same source als Producten tab.
                    // Marge = (sell - cost) / sell × 100 (zelfde formule overal).
                    // Geen inkoop bekend? Dan tonen we '—' — nooit een
                    // terug-gerekende schatting (oude /1.25 gokte verkeerd).
                    const bruto = p.bruto_price;
                    const sell = p.locked_price;
                    const margePct = (bruto != null && sell != null && sell > 0)
                      ? Math.round(((sell - bruto) / sell) * 1000) / 10
                      : null;
                    return (
                      <tr key={p.id} className={`border-b border-gray-50 transition-colors ${
                        isPaused ? 'bg-gray-50 opacity-60' : 'hover:bg-[#faf7f3]/50'
                      }`}>
                        <td className="py-2 px-3">
                          <p className={`font-medium ${isPaused ? 'text-gray-500 line-through' : 'text-[#1a1a1a]'}`}>{p.name}</p>
                          <p className="text-[10px] text-[#8a7e74] capitalize">{p.category}</p>
                        </td>
                        {/* Catalog — altijd zichtbaar, ook als paused (info blijft Filip's) */}
                        <td className="py-2 px-3 text-[#4a4a4a] bg-blue-50/20">{bruto != null ? `${formatEuro(bruto)}/m²` : '—'}</td>
                        <td className="py-2 px-3 bg-blue-50/20">
                          {margePct != null ? (
                            <span className={`font-semibold ${margePct > 0 ? 'text-green-700' : 'text-red-600'}`}>{margePct.toFixed(1)}%</span>
                          ) : <span className="text-[#8a7e74]">—</span>}
                        </td>
                        <td className="py-2 px-3 font-semibold text-blue-800 bg-blue-50/20">{sell != null ? `${formatEuro(sell)}/m²` : '—'}</td>
                        {/* Standaard % — read-only, toont de reseller default
                             zodat Filip in één oogopslag ziet wat de basis is. */}
                        <td className="py-2 px-3 bg-gray-50/50">
                          <span className="text-sm text-[#8a7e74] font-medium">{defaultDiscount}%</span>
                        </td>
                        {/* Korting per product % — editable, overschrijft de
                             standaard voor dit product. Step 1, leeg = standaard. */}
                        <td className="py-2 px-3 bg-amber-100/40">
                          <div className="relative max-w-[100px]">
                            <input
                              type="number" min="0" max="100" step="1"
                              value={ov.override_discount_pct ?? ''}
                              placeholder="—"
                              onChange={e => setOverride(p.id, 'override_discount_pct', e.target.value === '' ? null : Number(e.target.value))}
                              disabled={isPaused}
                              className="w-full px-2 py-1.5 border border-amber-300 rounded text-sm font-semibold focus:outline-none focus:border-[#c47a3a] focus:ring-1 focus:ring-[#c47a3a] disabled:bg-gray-50 disabled:text-gray-400 pr-6 bg-white"
                            />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-[#8a7e74] font-semibold">%</span>
                          </div>
                        </td>
                        {/* + Extra % kolom verwijderd in v3.2.42 — Filip wil één
                             korting per product, niet twee. Korting per product
                             overschrijft de standaard (zoals voorheen). */}
                        <td className="py-2 px-3 bg-amber-50/20">
                          {isPaused ? (
                            <span className="text-xs text-gray-400 italic">{t('admin.pricing.pausedItalic')}</span>
                          ) : (
                            <>
                              <span className="font-bold text-amber-700">{eff.price != null ? `${formatEuro(eff.price)}/m²` : '—'}</span>
                              {eff.discountPct != null && eff.discountPct > 0 && (
                                <span className="ml-1 text-[10px] text-green-700">−{eff.discountPct}%</span>
                              )}
                            </>
                          )}
                        </td>
                        {/* Pause toggle — Filip kan dit product voor deze
                             reseller buiten zijn catalog zetten zonder de
                             override-rij volledig te moeten weggooien. */}
                        <td className="py-2 px-2 bg-amber-50/20 text-center">
                          <button
                            type="button"
                            onClick={() => togglePause(p.id)}
                            className={`inline-flex items-center justify-center w-full px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wide transition-colors ${
                              isPaused
                                ? 'bg-red-100 text-red-700 hover:bg-red-200 border border-red-300'
                                : 'bg-green-100 text-green-700 hover:bg-green-200 border border-green-300'
                            }`}
                            title={isPaused ? t('admin.pricing.pauseUnpause') : t('admin.pricing.pauseClick')}
                          >
                            {isPaused ? `⏸ ${t('admin.pricing.statusPause')}` : `✓ ${t('admin.pricing.statusActive')}`}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* onRefresh hint — voor toekomstige inline catalog edit. */}
            {onRefresh === undefined && null}
          </div>

          {/* Save bar onderaan — herhaling van de sticky bar bovenaan, voor
               wanneer Filip helemaal beneden zit na een lange productenlijst. */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-3 border-t border-gray-100 bg-amber-50/30 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 mt-3">
            <p className="text-[11px] text-[#8a7e74] sm:max-w-md leading-snug">
              <strong className="text-[#1a1a1a]">{t('admin.pricing.saveAllAbove')}</strong> — {t('admin.pricing.saveBarDesc')}
              <strong className="text-amber-800"> {selected?.company_name ?? '—'}</strong>.
            </p>
            <button
              onClick={save}
              disabled={saving}
              className="px-5 py-2.5 bg-[#c47a3a] text-white text-sm font-semibold rounded-lg hover:bg-[#a8643a] disabled:opacity-50 flex items-center justify-center gap-2 min-h-[40px] flex-shrink-0"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? t('admin.pricing.savingDots') : `${t('admin.pricing.saveFor')} ${selected?.company_name?.split(' ')[0] ?? t('admin.pricing.thisReseller')}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
